import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Users, Package, ShoppingCart, Truck,
  ClipboardList, DollarSign, FileText, Building, Wallet,
  CreditCard, Settings, BarChart3, HelpCircle, Menu, X,
  LogOut, Plus, Edit, Trash2, User, Check, Download, Upload, Search,
  PhilippinePesoIcon, Calculator, CheckCircle, Eye,
  Factory, Sigma, AlertCircle, ScanFace, Filter, Group, TrendingUp,
  RefreshCw, BoxesIcon, Utensils, Ham, ShieldCheck, ShieldAlert, ArrowLeft,
  ChevronDown, FlaskConical, PackageSearch, XCircle, Circle,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Contact,
  Calendar, CalendarDays, Lock, EyeOff, PackageMinus, Scale, LayoutList,
  Banknote, UserRoundCheck, ChartArea, ChartBar, ChartBarBig, ChartCandlestick,
  ChartColumnBig, ChartColumnStacked, ChartLine, ChartSpline, ChartScatter,
  ChartNoAxesColumn, ChartNoAxesColumnIncreasing, ChartNetwork, CalendarCheck
} from 'lucide-react';
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable';
import { PasswordChangeModal, PasswordChangePage, usePasswordCheck } from './PasswordChangeComponents';
import { PermissionProvider } from './context/PermissionContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PermissionButton } from './components/ProtectedRoute';
import { usePermissions } from './context/PermissionContext';

const API_URL = process.env.REACT_APP_API_URL;

// Global fetch wrapper with auto-logout on 401
const fetchWithAuth = async (url, options = {}) => {
  const response = await fetch(url, options);

  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
    throw new Error('Session expired. Please log in again.');
  }

  return response;
};


const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const d = getPHTDate(dateString);

  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  const hour = String(d.getHours()).padStart(2, '0');
  const minute = String(d.getMinutes()).padStart(2, '0');

  return `${month} ${day}, ${year}, ${hour}:${minute}`;
};

const formatPHDatetime = (dateString) => {
  if (!dateString) return "";
  const d = getPHTDate(dateString);
  if (isNaN(d.getTime())) return "Invalid Date";

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  const hour = String(d.getHours()).padStart(2, '0');
  const minute = String(d.getMinutes()).padStart(2, '0');

  return `${month} ${day}, ${year}, ${hour}:${minute}`;
};

// Format currency to comma separated with 2 decimals
const formatCurrency = (amount, locale = 'en-US') => {
  const value = parseFloat(amount) || 0;

  return value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Philippine Time (PHT - Asia/Manila) Date Helpers
const getPHTDate = (dateParam) => {
  // If no dateParam, get current time in Manila and "fake" it as local
  if (!dateParam) {
    const d = new Date();
    const rawPHT = d.toLocaleString("en-US", { timeZone: "Asia/Manila" });
    return new Date(rawPHT);
  }

  // If it's a string, we want to ensure it's converted to Manila time
  if (typeof dateParam === 'string') {
    // Replace ' ' with 'T' for ISO compatibility if needed
    let sanitized = dateParam.replace(' ', 'T');

    // Create a Date object. 
    // If it has an offset (Z, +, GMT, UTC), it will be absolute.
    // If it doesn't, it will be local to the browser.
    const d = new Date(sanitized);

    // If parsing failed (NaN), return an invalid date
    if (isNaN(d.getTime())) return d;

    // Convert the absolute time to a "fake" local Date object that reflects Manila time
    const rawPHT = d.toLocaleString("en-US", { timeZone: "Asia/Manila" });
    return new Date(rawPHT);
  }

  // Fallback for Date objects or other types
  const d = new Date(dateParam);
  if (isNaN(d.getTime())) return d;
  const rawPHT = d.toLocaleString("en-US", { timeZone: "Asia/Manila" });
  return new Date(rawPHT);
};

const getPHTDateString = (dateParam) => {
  const pht = getPHTDate(dateParam);
  const yyyy = pht.getFullYear();
  const mm = String(pht.getMonth() + 1).padStart(2, '0');
  const dd = String(pht.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getPHTDateTimeString = (dateParam) => {
  const pht = getPHTDate(dateParam);
  const yyyy = pht.getFullYear();
  const mm = String(pht.getMonth() + 1).padStart(2, '0');
  const dd = String(pht.getDate()).padStart(2, '0');
  const hh = String(pht.getHours()).padStart(2, '0');
  const mins = String(pht.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mins}`;
};

const getPHTFirstDayOfMonth = () => {
  const pht = getPHTDate();
  const yyyy = pht.getFullYear();
  const mm = String(pht.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}-01`;
};

const getPHTLastDayOfMonth = () => {
  const pht = getPHTDate();
  const yyyy = pht.getFullYear();
  const mm = pht.getMonth() + 1; // 1-12
  // get last day of this month by getting '0'th day of next month
  const lastDayPht = new Date(yyyy, mm, 0);
  // since we used Date constructor, it's local time, but we just need the day number which is correct
  const dd = String(lastDayPht.getDate()).padStart(2, '0');
  const mmStr = String(mm).padStart(2, '0');
  return `${yyyy}-${mmStr}-${dd}`;
};

// Branch Posting Helpers
const isPeriodRestricted = (branchId, dateString, postedPeriods) => {
  if (!branchId || !dateString || !postedPeriods) return false;
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return postedPeriods.some(p =>
    p.branch_id === parseInt(branchId) &&
    p.month === month &&
    p.year === year
  );
};

const getMinDate = (branchId, postedPeriods) => {
  if (!branchId || !postedPeriods || postedPeriods.length === 0) return "";

  // 1. Filter postings for this specific branch
  const branchPostings = postedPeriods.filter(
    (p) => parseInt(p.branch_id) === parseInt(branchId)
  );

  if (branchPostings.length === 0) return "";

  // 2. Find the latest (max) year and month
  // We sort descending to get the most recent period at index 0
  const latest = branchPostings.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  })[0];

  // 3. Calculate the start of the next month
  let minYear = latest.year;
  let minMonth = latest.month + 1;

  if (minMonth > 12) {
    minMonth = 1;
    minYear += 1;
  }

  // 4. Format as YYYY-MM-DD for the 'min' attribute
  const formattedMonth = String(minMonth).padStart(2, '0');
  return `${minYear}-${formattedMonth}-01`;
};

const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' };

const styles = {
  loginContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f3f4f6', // This is the base floor
    position: 'relative',
    overflow: 'hidden',
    // REMOVE all background and opacity lines from here
  },
  container: {
    display: 'flex',
    height: '100vh',
    backgroundColor: '#f3f4f6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    overflow: 'hidden'
  },
  sidebar: {
    backgroundColor: '#312e81',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s',
    position: 'relative',
    zIndex: 100
  },
  sidebarMobile: {
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    zIndex: 1000
  },
  sidebarOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999
  },
  sidebarOpen: {
    width: '256px'
  },
  sidebarClosed: {
    width: '80px'
  },
  sidebarHeader: {
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  sidebarTitle: {
    fontSize: '20px',
    fontWeight: 'bold'
  },
  navButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    textAlign: 'left'
  },
  navButtonActive: {
    backgroundColor: '#4338ca',
    borderLeft: '4px solid white'
  },
  mainContent: {
    flex: 1,
    overflow: 'auto',
    padding: '32px',
    width: '100%'
  },
  mainContentMobile: {
    padding: '16px'
  },
  loginBox: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    padding: '32px',
    width: '100%',
    maxWidth: '400px'
  },
  loginIcon: {
    backgroundColor: '#4f46e5',
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  button: {
    backgroundColor: '#4f46e5',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  buttonReport: {
    padding: '10px 24px',
    alignSelf: 'flex-end',
    marginBottom: '16px',
    justifyContent: 'center'
  },
  buttonSecondary: {
    backgroundColor: '#d1d5db',
    color: '#374151'
  },
  buttonDanger: {
    backgroundColor: '#ef4444'
  },
  buttonSuccess: {
    backgroundColor: '#10b981'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    padding: '24px',
    marginBottom: '24px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  th: {
    backgroundColor: '#f9fafb',
    padding: '12px 24px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    borderBottom: '1px solid #e5e7eb'
  },
  td: {
    padding: '16px 24px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb'
  },
  badge: {
    display: 'inline-flex',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '9999px'
  },
  badgeGreen: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  badgeRed: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  badgeYellow: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  badgeBlue: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  },
  grid2Mobile: {
    gridTemplateColumns: '1fr'
  },
  grid4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px'
  },
  grid4Mobile: {
    gridTemplateColumns: '1fr'
  },
  formGroup: {
    marginBottom: '16px'
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    padding: '24px'
  },
  statCardContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  statIcon: {
    padding: '16px',
    borderRadius: '8px'
  },
  iconButton: {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  },
  badgeBlue: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    border: '1px solid #3b82f6'
  },
  buttonSuccess: {
    backgroundColor: '#10b981'
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }
};

const toggleStyles = {
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  toggleSwitch: {
    position: 'relative',
    display: 'inline-block',
    width: '48px',
    height: '24px'
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0
  },
  toggleSlider: (checked) => ({
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: checked ? '#10b981' : '#d1d5db',
    transition: '0.3s',
    borderRadius: '24px'
  }),
  toggleSliderBefore: (checked) => ({
    position: 'absolute',
    content: '""',
    height: '18px',
    width: '18px',
    left: checked ? '27px' : '3px',
    bottom: '3px',
    backgroundColor: 'white',
    transition: '0.3s',
    borderRadius: '50%'
  })
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    width: '100%',
    maxWidth: '720px',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  body: {
    padding: '20px'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6b7280'
  }
};


const Modal = ({ isOpen, onClose, title, children, maxWidth = '720px' }) => {
  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay}>
      <div style={{ ...modalStyles.modal, maxWidth }}>
        <div style={modalStyles.header}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
            {title}
          </h2>
          <button onClick={onClose} style={modalStyles.closeBtn}>×</button>
        </div>

        <div style={modalStyles.body}>
          {children}
        </div>
      </div>
    </div>
  );
};


const BranchPostings = ({ token }) => {
  const [postings, setPostings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    branch_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    notes: ''
  });

  useEffect(() => {
    fetchPostings();
    fetchBranches();
  }, []);

  const fetchPostings = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/branch_postings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPostings(data);
    } catch (err) {
      console.error('Error fetching postings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setBranches(data);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.branch_id || !form.month || !form.year) {
      alert('Branch, Month, and Year are required');
      return;
    }

    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/branch_postings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();
      if (res.ok) {
        alert('Period posted successfully');
        setShowForm(false);
        fetchPostings();
        setForm({ ...form, notes: '' });
      } else {
        const mainError = data.error || 'Failed to post period';

        const details = data.details && data.details.length > 0
          ? '\n\nDetails:\n' + data.details.join('\n')
          : '';

        alert(mainError + details);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this posting? This will re-open the period for transactions.')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/branch_postings/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Posting removed successfully');
        fetchPostings();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to remove posting');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Branch Posting</h1>
        <button onClick={() => setShowForm(true)} style={styles.button}>
          <Lock style={{ width: '18px', height: '18px' }} />
          Post New Period
        </button>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Post New Period">
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch *</label>
            <select
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
              style={styles.input}
              required
            >
              <option value="">Select Branch</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Month *</label>
              <select
                value={form.month}
                onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) })}
                style={styles.input}
                required
              >
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Year *</label>
              <select
                value={form.year}
                onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                style={styles.input}
                required
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              style={{ ...styles.input, minHeight: '80px' }}
              placeholder="Reason for posting or any other notes"
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Validating...' : 'Confirm Posting'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancel
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
            <AlertCircle style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px', verticalAlign: 'text-bottom' }} />
            Posting will check if all transactions for this period are closed. Once posted, no changes can be made to this branch's data for this period.
          </p>
        </form>
      </Modal>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Period</th>
              <th style={styles.th}>Posted At</th>
              <th style={styles.th}>Posted By</th>
              <th style={styles.th}>Notes</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {postings.map(p => (
              <tr key={p.id}>
                <td style={{ ...styles.td, fontWeight: '600' }}>{p.branch_name}</td>
                <td style={styles.td}>{months.find(m => m.value === p.month).label} {p.year}</td>
                <td style={styles.td}>{formatDateTime(p.posted_at)}</td>
                <td style={styles.td}>{p.posted_by_name}</td>
                <td style={styles.td}>{p.notes || '-'}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button onClick={() => handleDelete(p.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Remove Posting">
                    <Trash2 style={{ width: '16px', height: '16px' }} />
                  </button>
                </td>
              </tr>
            ))}
            {postings.length === 0 && (
              <tr>
                <td colSpan="6" style={{ ...styles.td, textAlign: 'center', color: '#6b7280' }}>No posted periods found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};



const AppContent = ({ token, setToken }) => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [activeModule, setActiveModule] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const { mustChangePassword, setMustChangePassword, loading: loadingPassword } = usePasswordCheck(token);
  const { canView, loading: loadingPermissions } = usePermissions();

  const [postedPeriods, setPostedPeriods] = useState([]);
  const [loadingPostedPeriods, setLoadingPostedPeriods] = useState(false);

  const fetchPostedPeriods = async () => {
    if (!token) return;
    setLoadingPostedPeriods(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/branch_postings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPostedPeriods(data);
    } catch (err) {
      console.error('Error fetching posted periods:', err);
    } finally {
      setLoadingPostedPeriods(false);
    }
  };

  useEffect(() => {
    fetchPostedPeriods();
  }, [token, activeModule]); // Re-fetch when module changes to stay up-to-date

  const [openCategories, setOpenCategories] = React.useState({});

  useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement.type === 'number') {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetchWithAuth(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await res.json();

      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);

        if (data.must_change_password) {
          // Modal will show automatically
        }
      } else {
        alert('Login failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Login error: ' + err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
    window.location.reload();
  };

  // Show mandatory password change modal
  if (mustChangePassword && !loadingPassword) {
    return (
      <PasswordChangeModal
        isOpen={true}
        onClose={(success) => {
          if (success) {
            setMustChangePassword(false);
            // Continue to main app
          }
        }}
        token={token}
        isMandatory={true}
      />
    );
  }

  if (!token) {
    return (
      <div style={styles.loginContainer}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "url('/Yakiniku-transparent.png')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '85%',
          opacity: '0.15',
          zIndex: 1
        }} />

        <div style={{ ...styles.loginBox, zIndex: 2, position: 'relative', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.3)', opacity: '0.75' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={styles.loginIcon}>
              <LayoutDashboard style={{ width: '32px', height: '32px', color: 'white' }} />
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0' }}>Yakiniku199 ERP System</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Sign in to your account</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Username</label>
            <input
              type="text"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
              style={styles.input}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
              style={styles.input}
            />
          </div>
          <button onClick={handleLogin} style={{ ...styles.button, width: '100%', justifyContent: 'center' }}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const modules = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'suppliers', name: 'Suppliers', icon: Ham, permission: 'suppliers' },
    { id: 'contacts', name: 'Contacts', icon: Contact, permission: 'contacts' },
    { id: 'branches', name: 'Branches', icon: Building, permission: 'branches' },
    { id: 'purchase_orders', name: 'Purchase Orders', icon: ShoppingCart, permission: 'purchase_orders' },
    { id: 'delivery_receipts', name: 'Delivery Receipts', icon: Truck, permission: 'delivery_receipts' },
    { id: 'receiving_entries', name: 'Receiving Entries', icon: ClipboardList, permission: 'receiving_entries' },
    {
      id: 'inventory-management', name: 'Inventory Management', icon: PackageSearch, isCategory: true,
      children: [
        { id: 'items', name: 'Items', icon: Package, permission: 'items' },
        { id: 'inventory', name: 'Branch Inventory', icon: BoxesIcon, permission: 'inventory' },
        { id: 'inventory_batches', name: 'Inventory Batches', icon: Group, permission: 'inventory_batches' },
      ]
    },
    { id: 'adjustment_entries', name: 'Adjustment Entries', icon: Settings, permission: 'adjustment_entries' },
    {
      id: 'production', name: 'Production', icon: FlaskConical, isCategory: true,
      children: [
        { id: 'production_entries', name: 'Production Entries', icon: Factory, permission: 'production_entries' },
        { id: 'production_formulas', name: 'Production Formulas', icon: Sigma, permission: 'production_formulas' },
      ]
    },
    {
      id: 'consumption', name: 'Consumption', icon: Utensils, isCategory: true,
      children: [
        { id: 'pull_out', name: 'Pull outs', icon: PackageMinus, permission: 'pull_out' },
        { id: 'daily_ending_inventory', name: 'Daily Ending Inventory', icon: Scale, permission: 'daily_ending_inventory' }
      ]
    },
    {
      id: 'daily_sales', name: 'Daily Sales', icon: PhilippinePesoIcon, isCategory: true,
      children: [
        { id: 'item_sales_summary', name: 'Item Sales Summary', icon: LayoutList, permission: 'item_sales_summary' },
        { id: 'shift_report', name: 'Shift Report', icon: UserRoundCheck, permission: 'shift_reports' },
        { id: 'deposit_drop', name: 'Deposit Drop', icon: Banknote, permission: 'deposit_drops' }
      ]
    },
    { id: 'branch_postings', name: 'Branch Posting', icon: CalendarCheck, permission: 'branches' },
    { id: 'chart_of_accounts', name: 'Chart of Accounts', icon: FileText, permission: 'chart_of_accounts' },
    { id: 'cheque_monitoring', name: 'Cheques', icon: CreditCard, permission: 'cheque_monitoring' },
    { id: 'journal_vouchers', name: 'Journal Vouchers', icon: FileText, permission: 'journal_vouchers' },
    { id: 'disbursement_vouchers', name: 'Disbursement', icon: Wallet, permission: 'disbursement_vouchers' },
    { id: 'branch_expenses', name: 'Branch Expenses', icon: DollarSign, permission: 'branch_expenses' },
    {
      id: 'reports', name: 'Reports', icon: BarChart3, isCategory: true,
      children: [
        { id: 'inventory_batches_report', name: 'Inventory Batches Report', icon: ChartArea, permission: 'inventory_batches_report' },
        { id: 'inventory_summary_report', name: 'Inventory Summary Report', icon: ChartBar, permission: 'inventory_summary_report' },
        { id: 'inventory_summary_prep_ending_report', name: 'Inventory Summary with Prep Ending Report', icon: ChartBarBig, permission: 'inventory_summary_prep_ending_report' },
        { id: 'receiving_detail_by_date', name: 'Receiving Detail by Date Report', icon: ChartCandlestick, permission: 'receiving_detail_report' },
        { id: 'receiving_detail_by_supplier', name: 'Receiving Detail by Supplier Report', icon: ChartColumnBig, permission: 'receiving_detail_report' },
        { id: 'receiving_detail_by_item', name: 'Receiving Detail by Item Report', icon: ChartColumnStacked, permission: 'receiving_detail_report' },
        { id: 'production_summary_report', name: 'Production Summary Report', icon: ChartNetwork, permission: 'production_summary_report' },
        { id: 'production_consumption_report', name: 'Production Consumption Report', icon: ChartLine, permission: 'production_consumption_report' },
        { id: 'delivery_receipt_report', name: 'Delivery Receipt Report', icon: ChartSpline, permission: 'delivery_receipt_report' },
        { id: 'adjustment_report', name: 'Adjustment Report', icon: ChartScatter, permission: 'adjustment_report' },
        { id: 'pull_out_detailed_report', name: 'Pull Out Detailed Report', icon: ChartNoAxesColumn, permission: 'pull_out_detailed_report' },
        { id: 'pull_out_summary_report', name: 'Pull Out Summary Report', icon: ChartNoAxesColumnIncreasing, permission: 'pull_out_summary_report' },
        // { id: 'income_statement_report', name: 'Income Statement Report', icon: BarChart3, permission: 'income_statement_report' },
        // { id: 'balance_sheet_report', name: 'Balance Sheet Report', icon: BarChart3, permission: 'balance_sheet_report' },
        // { id: 'purchase_orders_report', name: 'Purchase Orders Report', icon: BarChart3, permission: 'purchase_orders_report' },
        // { id: 'daily_sales_report', name: 'Daily Sales Report', icon: BarChart3, permission: 'daily_sales_report' },
        // { id: 'branch_expenses_report', name: 'Branch Expenses Report', icon: BarChart3, permission: 'branch_expenses_report' }
      ]
    },
    {
      id: 'account-management', name: 'Account Management', icon: ShieldCheck, isCategory: true,
      children: [
        { id: 'users', name: 'Users', icon: Users, permission: 'users' },
        { id: 'roles', name: 'Roles', icon: ScanFace, permission: 'roles' },
        { id: 'change-password', name: 'Change Password', icon: Lock },
      ]
    },
    { id: 'helpdesk', name: 'Helpdesk', icon: HelpCircle, permission: 'helpdesk' },
  ];

  // Filter modules based on permission
  const visibleModules = modules.reduce((acc, module) => {
    // 1. If it's a simple module
    if (!module.isCategory) {
      // Dashboard is always visible ideally, or check permission
      if (module.id === 'dashboard' || canView(module.permission)) {
        acc.push(module);
      }
      return acc;
    }

    // 2. If it's a category, filter children
    if (module.isCategory) {
      const visibleChildren = module.children.filter(child => {
        // Change password is always visible? or just users with login
        if (child.id === 'change-password') return true;
        return canView(child.permission || module.permission); // Fallback to module permission if not specific
      });

      if (visibleChildren.length > 0) {
        acc.push({ ...module, children: visibleChildren });
      }
      return acc;
    }
    return acc;
  }, []);

  return (
    <div style={styles.container}>
      <div style={{ ...styles.sidebar, ...(sidebarOpen ? styles.sidebarOpen : styles.sidebarClosed) }}>
        <div style={styles.sidebarHeader}>
          {sidebarOpen && <h1 style={styles.sidebarTitle}>ERP System</h1>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ ...styles.iconButton, backgroundColor: 'rgba(255,255,255,0.1)' }}
          >
            {sidebarOpen ? <X style={{ width: '20px', height: '20px' }} /> : <Menu style={{ width: '20px', height: '20px' }} />}
          </button>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto' }}>
          {loadingPermissions ? (
            <div style={{ padding: '16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
              Loading permissions...
            </div>
          ) : (
            visibleModules.map(module => {            // If it's a category (like Account Management), handle nesting
              if (module.isCategory) {
                const isOpen = openCategories[module.id];
                const Icon = module.icon;

                return (
                  <div key={module.id}>
                    {/* Category Toggle Button */}
                    <button
                      onClick={() => toggleCategory(module.id)}
                      style={{
                        ...styles.navButton,
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#4338ca')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                        {sidebarOpen && <span style={{ fontSize: '14px' }}>{module.name}</span>}
                      </div>
                      {sidebarOpen && (
                        <ChevronDown
                          style={{
                            width: '16px',
                            height: '16px',
                            transition: 'transform 0.2s',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      )}
                    </button>

                    {/* Render Children if Category is Open */}
                    {isOpen && module.children && (
                      <div style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
                        {module.children.map(child => {
                          const ChildIcon = child.icon;
                          const isChildActive = activeModule === child.id;
                          return (
                            <button
                              key={child.id}
                              onClick={() => setActiveModule(child.id)}
                              style={{
                                ...styles.navButton,
                                paddingLeft: sidebarOpen ? '32px' : '16px', // Indent children
                                ...(isChildActive ? styles.navButtonActive : {})
                              }}
                              onMouseEnter={(e) => !isChildActive && (e.currentTarget.style.backgroundColor = '#4338ca')}
                              onMouseLeave={(e) => !isChildActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <ChildIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                              {sidebarOpen && <span style={{ fontSize: '13px' }}>{child.name}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Standard Modules (like Inventory or Daily Sales)
              const Icon = module.icon;
              const isActive = activeModule === module.id;
              return (
                <button
                  key={module.id}
                  onClick={() => setActiveModule(module.id)}
                  style={{
                    ...styles.navButton,
                    ...(isActive ? styles.navButtonActive : {})
                  }}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = '#4338ca')}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <Icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                  {sidebarOpen && <span style={{ fontSize: '14px' }}>{module.name}</span>}
                </button>
              );
            })
          )}
        </nav>

        <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
            {sidebarOpen && (
              <div>
                <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>{user?.username}</p>
                <p style={{ fontSize: '12px', color: '#c7d2fe', margin: 0 }}>{user?.role}</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              style={{ ...styles.iconButton, backgroundColor: 'rgba(255,255,255,0.1)' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
            >
              <LogOut style={{ width: '20px', height: '20px' }} />
            </button>
          </div>
        </div>
      </div >

      <div style={styles.mainContent}>
        <ModuleContent module={activeModule} token={token} user={user} onReturn={setActiveModule} postedPeriods={postedPeriods} />
      </div>
    </div >
  );
};

const ModuleContent = ({ module, token, user, onReturn, postedPeriods }) => {
  const hasPermission = (requiredModule) => {
    if (!user || !user.permissions) {
      return false;
    }

    return user.permissions.some(p =>
      p.module === requiredModule &&
      (p.can_read === 1 || p.can_read === true)
    );
  };

  switch (module) {
    case 'dashboard':
      return <Dashboard token={token} />
    case 'suppliers':
      return hasPermission('suppliers') ? <Suppliers token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'contacts':
      return hasPermission('contacts') ? <Contacts token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'branches':
      return hasPermission('branches') ? <BranchManagement token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'branch_postings':
      return hasPermission('branches') ? <BranchPostings token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'purchase_orders':
      return hasPermission('purchase_orders') ? <PurchaseOrders token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'items':
      return hasPermission('items') ? <Items token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'inventory':
      return hasPermission('inventory') ? <Inventory token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'inventory_batches':
      return hasPermission('inventory_batches') ? <InventoryBatches token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'delivery_receipts':
      return hasPermission('delivery_receipts') ? <DeliveryReceipts token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'receiving_entries':
      return hasPermission('receiving_entries') ? <ReceivingEntries token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'item_sales_summary':
      return hasPermission('item_sales_summary') ? <ItemSalesSummary token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'shift_report':
      return hasPermission('shift_reports') ? <ShiftReports token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'journal_vouchers':
      return hasPermission('journal_vouchers') ? <JournalVouchers token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'deposit_drop':
      return hasPermission('deposit_drops') ? <DepositDrops token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'branch_expenses':
      return hasPermission('branch_expenses') ? <BranchExpenses token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'chart_of_accounts':
      return hasPermission('chart_of_accounts') ? <ChartOfAccounts token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'cheque_monitoring':
      return hasPermission('cheque_monitoring') ? <ChequeMonitoring token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'pull_out':
      return hasPermission('pull_out') ? <Pullout token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'daily_ending_inventory':
      return hasPermission('daily_ending_inventory') ? <DailyEndingInventory token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'adjustment_entries':
      return hasPermission('adjustment_entries') ? <AdjustmentEntries token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'production_entries':
      return hasPermission('production_entries') ? <ProductionEntry token={token} postedPeriods={postedPeriods} /> : <AccessDenied onReturn={onReturn} />;
    case 'production_formulas':
      return hasPermission('production_formulas') ? <ProductionFormulas token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'users':
      return hasPermission('users') ? <UserManagement token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'roles':
      return hasPermission('roles') ? <RoleManagement token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'change-password':
      return <PasswordChangePage token={token} />;
    case 'inventory_batches_report':
      return hasPermission('inventory_batches_report') ? <InventoryBatchesReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'inventory_summary_report':
      return hasPermission('inventory_summary_report') ? <InventorySummaryReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'inventory_summary_prep_ending_report':
      return hasPermission('inventory_summary_prep_ending_report') ? <InventorySummaryPrepEndingReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'receiving_detail_by_date':
      return hasPermission('receiving_detail_report') ? <ReceivingDetailByDate token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'receiving_detail_by_supplier':
      return hasPermission('receiving_detail_report') ? <ReceivingDetailBySupplier token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'receiving_detail_by_item':
      return hasPermission('receiving_detail_report') ? <ReceivingDetailByItem token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'production_summary_report':
      return hasPermission('production_summary_report') ? <ProductionSummaryReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'production_consumption_report':
      return hasPermission('production_consumption_report') ? <ProductionConsumptionReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'delivery_receipt_report':
      return hasPermission('delivery_receipt_report') ? <DeliveryReceiptReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'adjustment_report':
      return hasPermission('adjustment_report') ? <AdjustmentReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'pull_out_detailed_report':
      return hasPermission('pull_out_detailed_report') ? <PullOutDetailedReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    case 'pull_out_summary_report':
      return hasPermission('pull_out_summary_report') ? <PullOutSummaryReport token={token} /> : <AccessDenied onReturn={onReturn} />;
    default:
      return (
        <div style={{ textAlign: 'center', paddingTop: '48px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#6b7280', textTransform: 'capitalize' }}>
            {module.replace('-', ' ')} - Coming Soon
          </h2>
        </div>
      );
  }
};

const AccessDenied = ({ onReturn }) => {
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '70vh',
      textAlign: 'center',
      fontFamily: 'Arial, sans-serif'
    },
    iconCircle: {
      backgroundColor: '#fee2e2',
      padding: '24px',
      borderRadius: '50%',
      marginBottom: '20px',
      display: 'inline-block'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#111827',
      margin: '0 0 8px 0'
    },
    message: {
      color: '#6b7280',
      maxWidth: '400px',
      margin: '0 auto 24px auto',
      lineHeight: '1.5'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '10px 20px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'background-color 0.2s'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.iconCircle}>
        <ShieldAlert size={48} color="#ef4444" />
      </div>

      <h2 style={styles.title}>Access Denied</h2>

      <p style={styles.message}>
        You don't have the necessary permissions to view this module.
        Please contact your administrator if you believe this is an error.
      </p>

      <button
        style={styles.button}
        onClick={() => onReturn('dashboard')}
        onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
      >
        <ArrowLeft size={18} />
        Return to Dashboard
      </button>
    </div>
  );
};

const Dashboard = ({ token }) => {
  const [stats, setStats] = useState({ lowStockItems: 0, pendingPOs: 0, inTransitDRs: 0, unApprovedAEs: 0, pendingPRs: 0, pendingDEIs: 0, unverifiedSales: 0, unverifiedSRs: 0, pendingDDs: 0, partiallyApprovedDDs: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [inventory, pos, drs, aes, prs, deis, sss, srs, pdds, dds, tickets] = await Promise.all([
          fetchWithAuth(`${API_URL}/inventory/low-stock`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/pending_pos`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/in_transit_drs`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/unapproved_aes`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/pending_prs`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/pending_deis`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/unverified_sss`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/unverified_srs`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/pending_dds`, { headers: { Authorization: `Bearer ${token}` } }),
          fetchWithAuth(`${API_URL}/dashboard/partially_approved_dds`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const [invData, posData, drsData, aesData, prsData, deisData, sssData, srsData, pddsData, ddsData] = await Promise.all([
          inventory.json(), pos.json(), drs.json(), aes.json(), prs.json(), deis.json(), sss.json(), srs.json(), pdds.json(), dds.json()
        ]);

        setStats({
          lowStockItems: invData.length,
          pendingPOs: Number(posData.pending_po) || 0,
          inTransitDRs: Number(drsData.in_transit_dr) || 0,
          unApprovedAEs: Number(aesData.unapproved_ae) || 0,
          pendingPRs: Number(prsData.pending_pr) || 0,
          pendingDEIs: Number(deisData.pending_dei) || 0,
          unverifiedSales: Number(sssData.unverified_ss) || 0,
          unverifiedSRs: Number(srsData.unverified_sr) || 0,
          pendingDDs: Number(pddsData.pending_dd) || 0,
          partiallyApprovedDDs: Number(ddsData.partially_approved_dd) || 0
        });
      } catch (err) {
        console.error('Error:', err);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = [
    { title: 'Low Stock Items', value: stats.lowStockItems, color: '#ef4444', icon: Package },
    { title: 'Unreceived Purchase Orders', value: stats.pendingPOs, color: '#f59e0b', icon: ShoppingCart },
    { title: 'In-Transit Delivery Receipts', value: stats.inTransitDRs, color: '#f59e0b', icon: Truck },
    { title: 'Unapproved Adjustment Entries', value: stats.unApprovedAEs, color: '#f59e0b', icon: Settings },
    { title: 'Pending Pull out requests', value: stats.pendingPRs, color: '#f59e0b', icon: PackageMinus },
    { title: 'Pending Daily Ending Inventory', value: stats.pendingDEIs, color: '#f59e0b', icon: Scale },
    { title: 'Unverified Sales Summary', value: stats.unverifiedSales, color: '#f59e0b', icon: LayoutList },
    { title: 'Unverified Shift Report', value: stats.unverifiedSRs, color: '#f59e0b', icon: UserRoundCheck },
    { title: 'Pending Deposit Drops', value: stats.pendingDDs, color: '#f59e0b', icon: Banknote },
    { title: 'Partially Approved Deposit Drops', value: stats.partiallyApprovedDDs, color: '#f59e0b', icon: Banknote },
  ];

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '32px' }}>Dashboard</h1>
      <div style={styles.grid4}>
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} style={styles.statCard}>
              <div style={styles.statCardContent}>
                <div>
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0' }}>{stat.title}</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>{stat.value}</p>
                </div>
                <div style={{ ...styles.statIcon, backgroundColor: stat.color }}>
                  <Icon style={{ width: '32px', height: '32px', color: 'white' }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Suppliers = ({ token }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentTermForm, setShowPaymentTermForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    payment_term_id: '',
    is_active: 1,
    notes: '',
    branch_ids: []
  });
  const [paymentTermForm, setPaymentTermForm] = useState({
    term_name: '',
    days: 0,
    description: ''
  });

  useEffect(() => {
    fetchSuppliers(currentPage, perPage);
    fetchPaymentTerms();
    fetchBranches();
  }, [currentPage, perPage]);

  // Updated Select Handler
  const handleTermChange = (e) => {
    const val = e.target.value;
    setForm({
      ...form,
      payment_term_id: val === "" ? "" : parseInt(val)
    });
  };

  const fetchSuppliers = async (page = 1, limit = 10) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/suppliers?page=${page}&per_page=${limit}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSuppliers(data.suppliers);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branches?is_active=1&branch_type=branch`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/payment-terms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPaymentTerms(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/suppliers/${editingId}` : `${API_URL}/suppliers`;
      const method = editingId ? 'PUT' : 'POST';

      // Ensure we send null if no term is selected
      const payload = {
        ...form,
        payment_term_id: form.payment_term_id || null
      };

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        fetchSuppliers(currentPage, perPage);
        setShowForm(false);
        setEditingId(null);
        resetForm();
        alert('Supplier saved successfully');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const resetForm = () => {
    setForm({ name: '', contact_person: '', phone: '', email: '', address: '', payment_term_id: '', is_active: 1, notes: '', branch_ids: [] });
    setEditingId(null);
  };

  const handleEdit = (supplier) => {
    const allowedIds = (supplier.branches || []).map(b => Number(b.id));
    setForm({ ...supplier, branch_ids: allowedIds });
    setEditingId(supplier.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await fetchWithAuth(`${API_URL}/suppliers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSuppliers(currentPage, perPage);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleAddPaymentTerm = async () => {
    if (!paymentTermForm.term_name) {
      alert('Please enter payment term name');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/payment-terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(paymentTermForm)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create payment term');
      }

      const data = await res.json();
      await fetchPaymentTerms();
      setForm({ ...form, payment_term_id: data.id });
      setShowPaymentTermForm(false);
      setPaymentTermForm({ term_name: '', days: 0, description: '' });
      alert('Payment term added successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error adding payment term');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Suppliers</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Supplier
        </button>
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>{editingId ? 'Edit' : 'Add'} Supplier</h2>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Supplier Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Contact Person</label>
              <input type="text" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={styles.input} />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Address</label>
            <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} style={{ ...styles.input, minHeight: '60px' }} />
          </div>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Default Payment Terms</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  value={form.payment_term_id || ''}
                  onChange={handleTermChange}
                  style={{ ...styles.input, flex: 1 }}
                >
                  <option value="">Select Payment Term</option>
                  {paymentTerms.map(pt => (
                    <option key={pt.id} value={pt.id}>
                      {pt.term_name} {pt.days > 0 ? `(${pt.days} days)` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowPaymentTermForm(!showPaymentTermForm)}
                  style={{
                    ...styles.button,
                    ...styles.buttonSuccess,
                    padding: '8px 12px',
                    fontSize: '14px'
                  }}
                  title="Add New Payment Term"
                >
                  <Plus style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
            </div>

            {/* Payment Term Form Modal */}
            {showPaymentTermForm && (
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Add New Payment Term
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 3fr', gap: '12px', marginBottom: '12px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Term Name *</label>
                    <input
                      type="text"
                      value={paymentTermForm.term_name}
                      onChange={(e) => setPaymentTermForm({ ...paymentTermForm, term_name: e.target.value })}
                      placeholder="e.g., Net 45"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Days</label>
                    <input
                      type="number"
                      min="0"
                      value={paymentTermForm.days || ''}
                      placeholder="0"
                      onChange={(e) => setPaymentTermForm({ ...paymentTermForm, days: parseInt(e.target.value) || 0 })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Description</label>
                    <input
                      type="text"
                      value={paymentTermForm.description}
                      onChange={(e) => setPaymentTermForm({ ...paymentTermForm, description: e.target.value })}
                      placeholder="Optional description"
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleAddPaymentTerm}
                    style={{ ...styles.button, padding: '6px 12px', fontSize: '13px' }}
                  >
                    Add Term
                  </button>
                  <button
                    onClick={() => {
                      setShowPaymentTermForm(false);
                      setPaymentTermForm({ term_name: '', days: 0, description: '' });
                    }}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <select value={form.is_active} onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })} style={styles.input}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...styles.input, minHeight: '60px' }} />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Allowed Branches</label>
              <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {branches.map(b => (
                  <label key={b.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={form.branch_ids.includes(b.id)}
                      onChange={(e) => {
                        const newIds = e.target.checked
                          ? [...form.branch_ids, b.id]
                          : form.branch_ids.filter(id => id !== b.id);
                        setForm({ ...form, branch_ids: newIds });
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {b.name}
                  </label>
                ))}
                {branches.length === 0 && <span style={{ color: '#6b7280' }}>No active branches available</span>}
              </div>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                Select branches where this supplier operates.
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={handleSubmit} style={styles.button}>{editingId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          </div>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Contact</th>
            <th style={styles.th}>Phone</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Payment Term</th>
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {suppliers.map(supplier => (
            <tr key={supplier.id}>
              <td style={{ ...styles.td, fontWeight: '500', color: '#1f2937' }}>{supplier.name}</td>
              <td style={styles.td}>{supplier.contact_person}</td>
              <td style={styles.td}>{supplier.phone}</td>
              <td style={styles.td}>{supplier.email}</td>
              <td style={styles.td}>
                {supplier.term_name || <span style={{ color: '#9ca3af' }}>Not Set</span>}
              </td>
              <td style={styles.td}>
                <span style={{ ...styles.badge, ...(supplier.is_active ? styles.badgeGreen : styles.badgeRed) }}>
                  {supplier.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                <button onClick={() => handleEdit(supplier)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                  <Edit style={{ width: '16px', height: '16px' }} />
                </button>
                <button onClick={() => handleDelete(supplier.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                  <Trash2 style={{ width: '16px', height: '16px' }} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Contacts = ({ token }) => {
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    company_name: '',
    phone: '',
    email: '',
    address: '',
    is_company: 0,
    is_active: 1,
    notes: ''
  });

  useEffect(() => {
    fetchContacts(currentPage, perPage);
  }, [currentPage, perPage]);

  const fetchContacts = async (page = 1, limit = 10) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/contacts?page=${page}&per_page=${limit}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setContacts(data.contacts);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/contacts/${editingId}` : `${API_URL}/contacts`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchContacts(currentPage, perPage);
        setShowForm(false);
        setEditingId(null);
        resetForm();
        alert('Contact saved successfully');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      company_name: '',
      phone: '',
      email: '',
      address: '',
      is_company: 0,
      is_active: 1,
      notes: ''
    });
    setEditingId(null);
  };

  const handleEdit = (contact) => {
    const allowedIds = (contact.branches || []).map(b => Number(b.id));
    setForm({ ...contact, branch_ids: allowedIds });
    setEditingId(contact.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await fetchWithAuth(`${API_URL}/contacts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts(currentPage, perPage);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Contacts</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Contact
        </button>
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>{editingId ? 'Edit' : 'Add'} Contact</h2>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Contact Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Name</label>
              <input type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={styles.input} />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Address</label>
            <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} style={{ ...styles.input, minHeight: '60px' }} />
          </div>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Is this a company?</label>
              <select value={form.is_company} onChange={(e) => setForm({ ...form, is_company: parseInt(e.target.value) })} style={styles.input}>
                <option value={1}>Company</option>
                <option value={0}>Not a company</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <select value={form.is_active} onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })} style={styles.input}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...styles.input, minHeight: '60px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={handleSubmit} style={styles.button}>{editingId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          </div>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Company Name</th>
            <th style={styles.th}>Phone</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map(contact => (
            <tr key={contact.id}>
              <td style={{ ...styles.td, fontWeight: '500', color: '#1f2937' }}>{contact.name}</td>
              <td style={styles.td}>{contact.company_name}</td>
              <td style={styles.td}>{contact.phone}</td>
              <td style={styles.td}>{contact.email}</td>
              <td style={styles.td}>
                <span style={{ ...styles.badge, ...(contact.is_active ? styles.badgeGreen : styles.badgeRed) }}>
                  {contact.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                <button onClick={() => handleEdit(contact)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                  <Edit style={{ width: '16px', height: '16px' }} />
                </button>
                <button onClick={() => handleDelete(contact.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                  <Trash2 style={{ width: '16px', height: '16px' }} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BranchManagement = ({ token }) => {
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    branch_code: '',
    name: '',
    branch_type: 'branch',
    parent_branch_id: '',
    address: '',
    phone: '',
    email: '',
    legal_company_name: '',
    is_active: 1,
    notes: ''
  });
  const [filterType, setFilterType] = useState('');

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branches`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);

      const areaList = data.filter(branch => branch.branch_type === 'area');
      setAreas(areaList);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const resetForm = () => {
    setForm({
      branch_code: '',
      name: '',
      branch_type: 'branch',
      parent_branch_id: '',
      address: '',
      phone: '',
      email: '',
      legal_company_name: '',
      is_active: 1,
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/branches/${editingId}` : `${API_URL}/branches`;
      const method = editingId ? 'PUT' : 'POST';

      // Adding area prefix for branches
      let fullBranchCode = form.branch_code;
      let finalParentId = form.branch_type === 'area' ? null : (form.parent_branch_id ? parseInt(form.parent_branch_id) : null);

      // Only apply prefixing if a parent is selected and it's not an area type itself
      if (finalParentId && form.branch_type === 'branch') {
        // Find the parent branch in your existing 'branches' state
        const parent = branches.find(i => i.id === parseInt(form.parent_branch_id));
        if (parent) {
          fullBranchCode = `${parent.branch_code}.${form.branch_code}`;
        }
      }

      const payload = {
        ...form,
        branch_code: fullBranchCode, // Send the full prefixed code to backend
        parent_branch_id: finalParentId
      };

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        fetchBranches();
        resetForm();
        alert('Branch saved successfully');
      } else {
        const errorData = await res.json();
        alert(errorData.error || 'Error saving item');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleEdit = (branch) => {
    setForm({
      branch_code: branch.branch_code.includes('.') ? branch.branch_code.split('.').pop() : branch.branch_code,
      name: branch.name,
      branch_type: branch.branch_type,
      parent_branch_id: branch.parent_branch_id || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      legal_company_name: branch.legal_company_name || '',
      is_active: branch.is_active,
      notes: branch.notes || ''
    });
    setEditingId(branch.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (id === 1 || id === 2) {
      alert('Cannot delete Main Office or Production');
      return;
    }
    if (!window.confirm('Delete this branch?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/branches/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchBranches();
        alert('Branch deleted successfully');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting branch. It may be in use.');
    }
  };

  const branchTypes = [
    { value: 'branch', label: 'Branch', color: '#3b82f6' },
    { value: 'area', label: 'Area', color: '#f59e0b' }
  ];

  const filteredBranches = filterType ? branches.filter(a => a.branch_type === filterType) : branches;

  const branchTypeStats = branchTypes.map(type => ({
    ...type,
    count: branches.filter(a => a.branch_type === type.value).length
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Branches</h1>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          style={styles.button}
        >
          <Plus style={{ width: '20px', height: '20px' }} /> Add Branch
        </button>
      </div>

      {/* Statistics Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {branchTypeStats.map(type => (
          <div
            key={type.value}
            onClick={() => setFilterType(filterType === type.value ? '' : type.value)}
            style={{
              padding: '16px',
              backgroundColor: filterType === type.value ? type.color : 'white',
              color: filterType === type.value ? 'white' : '#1f2937',
              borderRadius: '8px',
              border: `2px solid ${type.color}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{type.count}</div>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{type.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Info */}
      {filterType && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
            Showing: {branchTypes.find(t => t.value === filterType)?.label} branches ({filteredBranches.length})
          </span>
          <button onClick={() => setFilterType('')} style={{ ...styles.button, padding: '6px 12px', fontSize: '13px' }}>
            Show All
          </button>
        </div>
      )}

      {/* Branches Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Code</th>
            <th style={styles.th}>Branch Name</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Area</th>
            <th style={styles.th}>Phone</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Address</th>
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredBranches.map(branch => {
            const typeInfo = branchTypes.find(t => t.value === branch.branch_type);
            const parentBranch = branches.find(a => a.id === branch.parent_branch_id);
            return (
              <tr key={branch.id}>
                <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>
                  {branch.branch_code.includes('.') ? branch.branch_code.split('.').pop() : branch.branch_code}
                </td>
                <td style={{ ...styles.td, fontWeight: '500' }}>
                  {branch.parent_branch_id && <span style={{ color: '#6b7280', marginRight: '8px' }}>└─</span>}
                  {branch.name}
                </td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    backgroundColor: typeInfo?.color + '20',
                    color: typeInfo?.color,
                    border: `1px solid ${typeInfo?.color}`
                  }}>
                    {typeInfo?.label}
                  </span>
                </td>
                <td style={styles.td}>
                  {parentBranch ? (
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {parentBranch.branch_code} - {parentBranch.name}
                    </span>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>{branch.phone || '—'}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>{branch.email || '—'}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>{branch.address || '—'}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, ...(branch.is_active ? styles.badgeGreen : styles.badgeRed) }}>
                    {branch.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button
                    onClick={() => handleEdit(branch)}
                    style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}
                    title="Edit"
                  >
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button
                    onClick={() => handleDelete(branch.id)}
                    style={{ ...styles.iconButton, color: '#ef4444' }}
                    title="Delete"
                  >
                    <Trash2 style={{ width: '16px', height: '16px' }} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>


      {filteredBranches.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
          <p style={{ margin: 0 }}>No branches found. Click "Add Branch" to create one.</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            ...styles.card,
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: 0
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                {editingId ? 'Edit Branch' : 'Add New Branch'}
              </h2>
              <button onClick={resetForm} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {/* Form Content */}
            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch Code *</label>
                <input
                  type="text"
                  value={form.branch_code}
                  onChange={(e) => setForm({ ...form, branch_code: e.target.value.toUpperCase() })}
                  style={styles.input}
                  placeholder="e.g., DAGUPAN, SANCARLOS"
                  disabled={editingId !== null}
                />
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  {editingId ? 'Branch code cannot be changed' : 'Unique identifier for the branch'}
                </small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Branch Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., Dagupan Branch, San Carlos Branch"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Branch Type *</label>
                <select
                  value={form.branch_type}
                  onChange={(e) => setForm({ ...form, branch_type: e.target.value, parent_branch_id: '' })}
                  style={styles.input}
                >
                  {branchTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  {form.branch_type === 'area' ? 'Top-level organizational unit' : 'Individual branch location'}
                </small>
              </div>

              {form.branch_type === 'branch' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Area *</label>
                  <select
                    value={form.parent_branch_id}
                    onChange={(e) => setForm({ ...form, parent_branch_id: e.target.value })}
                    style={styles.input}
                  >
                    <option value="">Select an area</option>
                    {areas.map(area => (
                      <option key={area.id} value={area.id}>
                        {area.branch_code} - {area.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Parent area for this branch
                  </small>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  style={styles.input}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={styles.input}
                  placeholder="branch@company.com"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Legal Company Name *</label>
                <input
                  type="text"
                  value={form.legal_company_name}
                  onChange={(e) => setForm({ ...form, legal_company_name: e.target.value })}
                  style={styles.input}
                  placeholder="Yakiniku199 Restaurant Corporation"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Complete branch address"
                />
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Additional notes or information"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <div style={toggleStyles.toggleContainer}>
                  <label style={toggleStyles.toggleSwitch}>
                    <input
                      type="checkbox"
                      checked={form.is_active === 1}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                      style={toggleStyles.toggleInput}
                    />
                    <span style={toggleStyles.toggleSlider(form.is_active === 1)}>
                      <span style={toggleStyles.toggleSliderBefore(form.is_active === 1)}></span>
                    </span>
                  </label>
                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
                    {form.is_active === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginTop: '24px',
              paddingTop: '20px',
              borderTop: '2px solid #e5e7eb'
            }}>
              <button onClick={handleSubmit} style={styles.button}>
                <Check style={{ width: '16px', height: '16px' }} />
                {editingId ? 'Update Branch' : 'Create Branch'}
              </button>
              <button
                onClick={resetForm}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const PurchaseOrders = ({ token, postedPeriods }) => {
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [branchSuppliers, setBranchSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentTermForm, setShowPaymentTermForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('branch');
  const [form, setForm] = useState({
    supplier_id: '',
    branch_id: '',
    order_date: new Date().toLocaleDateString('en-CA'),
    payment_term_id: '',
    notes: '',
    total_amount: 0,
    items: []
  });
  const [paymentTermForm, setPaymentTermForm] = useState({
    term_name: '',
    days: 0,
    description: ''
  });

  useEffect(() => {
    fetchOrders(currentPage, perPage);
    fetchSuppliers();
    fetchBranches();
    fetchInventoryItems();
    fetchPaymentTerms();
    fetchCompanyProfiles();
  }, [currentPage, perPage]);

  const fetchOrders = async (page = 1, limit = 10) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/purchase_orders?page=${page}&per_page=${limit}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOrders(data.orders);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      // const res = await fetchWithAuth(`${API_URL}/suppliers`, { headers: { Authorization: `Bearer ${token}` } });
      const res = await fetchWithAuth(`${API_URL}/suppliersdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSuppliers(data.filter(s => s.is_active));
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data);
      // const userData = JSON.parse(localStorage.getItem('user') || '{}');
      // const userRole = userData.role?.toLowerCase();
      // const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      // const authorizedBranches = data.filter(branch => {
      //   if (userRole === 'administrator') return true;

      //   return allowedIds.includes(Number(branch.id));
      // });
      // setBranches(authorizedBranches);
      // if (authorizedBranches.length === 1) {
      //   setForm(prev => ({ ...prev, branch_id: authorizedBranches[0].id }));
      // }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranchSuppliers = async (branchId) => {
    if (!branchId) {
      setBranchSuppliers([]);
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_URL}/branch-suppliers/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setBranchSuppliers(data);
    } catch (err) {
      console.error('Error fetching branch suppliers:', err);
      setBranchSuppliers([]);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await fetch(`${API_URL}/inventorydropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setInventoryItems(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/payment-terms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPaymentTerms(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchCompanyProfiles = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/company-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCompanyProfiles(data);
    } catch (err) {
      console.error('Error fetching company profiles:', err);
    }
  };

  const resetForm = () => {
    setForm({
      supplier_id: '',
      branch_id: '',
      order_date: new Date().toLocaleDateString('en-CA'),
      payment_term_id: '',
      notes: '',
      total_amount: 0,
      items: []
    });
    setEditingId(null);
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, {
        inventory_item_id: '',
        item_code: '',
        item_name: '',
        quantity: 0,
        unit_of_measurement: '',
        unit_price: 0,
        total_price: 0
      }]
    });
  };

  const removeItem = (idx) => {
    const newItems = form.items.filter((_, i) => i !== idx);
    const total = newItems.reduce((sum, item) => sum + item.total_price, 0);
    setForm({ ...form, items: newItems, total_amount: total });
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...form.items];

    if (field === 'inventory_item_id') {
      const selectedItem = inventoryItems.find(item => item.id == value);
      if (selectedItem) {
        newItems[idx].inventory_item_id = value;
        newItems[idx].item_code = selectedItem.item_code;
        newItems[idx].item_name = selectedItem.item_name;
        newItems[idx].unit_of_measurement = selectedItem.unit_of_measurement;
      }
    } else {
      newItems[idx][field] = value;
    }

    if (field === 'quantity' || field === 'unit_price') {
      newItems[idx].total_price = newItems[idx].quantity * newItems[idx].unit_price;
    }

    const total = newItems.reduce((sum, item) => sum + item.total_price, 0);
    setForm({ ...form, items: newItems, total_amount: total });
  };


  const handleSubmit = async () => {
    if (isPeriodRestricted(form.branch_id, form.order_date, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    if (!form.supplier_id) {
      alert('Please select a supplier');
      return;
    }

    if (!form.branch_id) {
      alert('Please select a branch');
      return;
    }

    if (form.items.length === 0) {
      alert('Please add at least one item');
      return;
    }

    const hasInvalidItems = form.items.some(item => !item.inventory_item_id || item.quantity <= 0 || item.unit_price <= 0);
    if (hasInvalidItems) {
      alert('Please ensure all items have valid inventory selection, quantity, and price');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/purchase_orders/${editingId}` : `${API_URL}/purchase_orders`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save purchase order');
      }

      fetchOrders(currentPage, perPage);
      setShowForm(false);
      resetForm();
      alert(`Purchase order ${editingId ? 'updated' : 'created'} successfully!`);
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error saving purchase order');
    }
  };

  const handleEdit = async (order) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/purchase_orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      // Map items and calculate total_price for each item
      const mappedItems = data.items.map(item => ({
        inventory_item_id: item.inventory_item_id,
        item_code: item.item_code,
        item_name: item.item_name,
        quantity: parseFloat(item.quantity) || 0,
        unit_of_measurement: item.unit_of_measurement,
        unit_price: parseFloat(item.unit_price) || 0,
        total_price: (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)
      }));

      // Calculate total amount from items
      const calculatedTotal = mappedItems.reduce((sum, item) => sum + item.total_price, 0);

      const branchId = data.order.branch_id || '';

      setForm({
        supplier_id: data.order.supplier_id,
        branch_id: branchId,
        order_date: data.order.order_date ? data.order.order_date.split('T')[0] : new Date().toLocaleDateString('en-CA'),
        payment_term_id: data.order.payment_term_id || '',
        notes: data.order.notes || '',
        total_amount: data.order.total_amount,
        items: mappedItems
      });

      // Fetch suppliers for the branch to populate the dropdown
      if (branchId) {
        await fetchBranchSuppliers(branchId);
      }

      setEditingId(order.id);
      setShowForm(true);
      setViewingOrder(null);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading order details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/purchase_orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchOrders(currentPage, perPage);
        alert('Purchase Order deleted successfully');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleView = async (order) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/purchase_orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingOrder(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const exportToPDF = async (poId, profileId) => {
    try {
      // Fetch PO details
      const res = await fetchWithAuth(`${API_URL}/purchase_orders/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch purchase order');
      }

      const data = await res.json();
      const po = data.order;
      const items = data.items || [];

      // Determine Header Data
      let headerName, headerAddress, headerPhone;

      if (profileId === 'branch') {
        // Use the branch associated with the PO 
        headerName = po.branch_legal_name || 'N/A';
        // headerAddress = po.branch_address || 'N/A';
        // headerPhone = po.branch_phone || 'N/A';
      } else {
        // Find the selected company profile from the database records
        const selectedProfile = companyProfiles.find(p => p.id === parseInt(profileId));
        headerName = selectedProfile?.company_name || 'N/A';
        // headerAddress = selectedProfile?.address || 'N/A';
        // headerPhone = selectedProfile?.phone || 'N/A';
      }
      headerAddress = po.branch_address || 'N/A';
      headerPhone = po.branch_phone || 'N/A';

      // Create PDF
      const doc = new jsPDF();

      const pesoFormatter = new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        currencyDisplay: 'code',
      });

      const qtyFormatter = new Intl.NumberFormat('en-PH', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Company Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('PURCHASE ORDER', 105, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(headerName || 'N/A', 105, 28, { align: 'center' });
      doc.text(headerAddress || 'N/A', 105, 33, { align: 'center' });
      doc.text(headerPhone || 'N/A', 105, 38, { align: 'center' });

      // Line separator
      doc.setLineWidth(0.5);
      doc.line(20, 43, 190, 43);

      // PO Information - Left Side
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('PO Number:', 20, 55);
      doc.text('PO Date:', 20, 62);
      doc.text('Status:', 20, 69);
      doc.text('Expected Date:', 20, 76);

      doc.setFont('helvetica', 'normal');
      doc.text(po.id ? po.id.toString() : 'N/A', 50, 55);
      doc.text(po.order_date ? new Date(po.order_date).toLocaleDateString('en-US', dateOptions) : 'N/A', 50, 62);
      doc.text(po.status || 'N/A', 50, 69);
      doc.text(po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-US', dateOptions) : 'N/A', 50, 76);

      // Supplier Information - Right Side
      doc.setFont('helvetica', 'bold');
      doc.text('SUPPLIER:', 120, 55);
      doc.setFont('helvetica', 'normal');
      doc.text(po.supplier_name || 'N/A', 120, 62);
      if (po.contact_person) {
        doc.text('Contact: ' + po.contact_person, 120, 69);
      }
      if (po.phone) {
        doc.text('Phone: ' + po.phone, 120, 76);
      }

      // Items Table
      const tableData = items.map(item => [
        item.item_code.split('.').pop(),
        item.item_name || '',
        qtyFormatter.format(item.quantity || 0),
        item.unit_of_measurement || '',
        pesoFormatter.format(item.unit_price),
        pesoFormatter.format(item.total_price)
      ]);

      autoTable(doc, {
        startY: 85,
        head: [['Item Code', 'Item Name', 'Qty', 'Unit', 'Unit Price', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [79, 70, 229],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 35 },
          2: { cellWidth: 25, halign: 'center' },
          3: { cellWidth: 15, halign: 'center' },
          4: { cellWidth: 40, halign: 'right' },
          5: { cellWidth: 45, halign: 'right' }
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        }
      });

      // Calculate totals
      const totalItems = items.length;
      const totalQuantity = items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
      const total = parseFloat(po.total_amount || 0);

      // Totals section
      const finalY = doc.lastAutoTable.finalY + 10;
      const rightAlignX = 190; // X-coordinate for the values to be right-aligned

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');

      doc.text('Total Items:', 130, finalY);
      doc.text('Total Quantity:', 130, finalY + 7);
      doc.setFontSize(11);
      doc.text('TOTAL:', 130, finalY + 14);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      doc.setFont('helvetica', 'bold');
      doc.text(totalItems.toString(), rightAlignX, finalY, { align: 'right' });
      doc.text(qtyFormatter.format(totalQuantity), rightAlignX, finalY + 7, { align: 'right' });
      doc.text(pesoFormatter.format(total), rightAlignX, finalY + 14, { align: 'right' });

      // Signature Section
      const sigY = finalY + 30;

      // Prepared by section
      const splitPreparedBy = doc.splitTextToSize(po.created_by_name, 170);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Prepared by:', 20, sigY);
      // doc.line(20, sigY + 15, 70, sigY + 15); // Signature line
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(splitPreparedBy, 20, sigY + 7);
      doc.text(`Date: ${new Date().toLocaleDateString('en-US', dateOptions)}`, 20, sigY + 14);

      // Notes section
      if (po.notes) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 20, sigY + 50);
        doc.setFont('helvetica', 'normal');
        const splitNotes = doc.splitTextToSize(po.notes, 170);
        doc.text(splitNotes, 20, sigY + 55);
      }

      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text('Thank you for your business!', 105, pageHeight - 15, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, pageHeight - 10, { align: 'center' });

      // Save PDF
      doc.save(`PO_${po.po_number || poId}.pdf`);

    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF');
    }
  };

  const filteredInventoryItems = form.branch_id
    ? inventoryItems.filter(item => item.branch_id === parseInt(form.branch_id))
    : inventoryItems;

  const handleAddPaymentTerm = async () => {
    if (!paymentTermForm.term_name) {
      alert('Please enter payment term name');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/payment-terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(paymentTermForm)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create payment term');
      }

      const data = await res.json();
      await fetchPaymentTerms();
      setForm({ ...form, payment_term_id: data.id });
      setShowPaymentTermForm(false);
      setPaymentTermForm({ term_name: '', days: 0, description: '' });
      alert('Payment term added successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error adding payment term');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Purchase Orders</h1>
        <button onClick={() => { setShowForm(!showForm); resetForm(); }} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Create PO
        </button>
      </div>

      {/* {postings && postings.length > 0 && isPeriodRestricted(form.branch_id, form.order_date, postedPeriods) && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '500',
          border: '1px solid #fecaca'
        }}>
          <Lock style={{ width: '16px', height: '16px' }} />
          This branch period is posted. No changes can be made.
        </div>
      )} */}

      {/* View Order Modal */}
      {viewingOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...styles.card, maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Purchase Order #{viewingOrder.order.id}</h2>
              <button onClick={() => setViewingOrder(null)} style={styles.iconButton}>
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Supplier</p>
                <p style={{ fontSize: '16px', fontWeight: '600' }}>{viewingOrder.order.supplier_name}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Branch</p>
                <p style={{ fontSize: '16px', fontWeight: '600' }}>{viewingOrder.order.branch_name || 'Main Office'}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Order Date</p>
                <p style={{ fontSize: '16px', fontWeight: '600' }}>{new Date(viewingOrder.order.order_date || viewingOrder.order.created_at).toLocaleDateString('en-US', dateOptions)}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Status</p>
                <span style={{ ...styles.badge, ...(viewingOrder.order.status === 'pending' ? styles.badgeYellow : styles.badgeGreen) }}>
                  {viewingOrder.order.status}
                </span>
              </div>
            </div>

            <div style={styles.grid2}>
              {viewingOrder.order.payment_term_name && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Payment Terms</p>
                  <p style={{ fontSize: '16px' }}>{viewingOrder.order.payment_term_name}</p>
                </div>
              )}

              {viewingOrder.order.created_by_name && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Prepared by</p>
                  <p style={{ fontSize: '16px' }}>{viewingOrder.order.created_by_name}</p>
                </div>
              )}
            </div>

            {viewingOrder.order.notes && (
              <div style={{ marginTop: '16px' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Notes</p>
                <p style={{ fontSize: '16px' }}>{viewingOrder.order.notes}</p>
              </div>
            )}

            <div style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Items</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ ...styles.th, textAlign: 'left' }}>Code</th>
                    <th style={{ ...styles.th, textAlign: 'left' }}>Name</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Unit Price</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Unit of Measurement</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewingOrder.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ ...styles.td, textAlign: 'left' }}>{item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code}</td>
                      <td style={{ ...styles.td, textAlign: 'left' }}>{item.item_name}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(item.quantity)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>₱{formatCurrency(item.unit_price)}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{item.unit_of_measurement}</td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>₱{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            {viewingOrder.items.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginTop: '12px',
                marginBottom: '12px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Items:</span>
                    <span style={{ fontWeight: '600' }}>{viewingOrder.items.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Quantity:</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(viewingOrder.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0).toFixed(2))}
                    </span>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '16px',
                  paddingTop: '12px',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <span style={{ fontWeight: 'bold' }}>Total Amount:</span>
                  <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                    ₱{formatCurrency(
                      viewingOrder.items.reduce((sum, item) =>
                        sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0
                      ).toFixed(2)
                    )}
                  </span>
                </div>

              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={() => handleEdit(viewingOrder.order)} style={styles.button}>
                <Edit style={{ width: '16px', height: '16px' }} /> Edit
              </button>
              <button onClick={() => setViewingOrder(null)} style={{ ...styles.button, ...styles.buttonSecondary }}>Close</button>
              {/* <button
                onClick={() => exportToPDF(viewingOrder.order.id)}
                style={{
                  ...styles.button,
                  backgroundColor: '#10b981'
                }}
              >
                <Download style={{ width: '16px', height: '16px' }} />
                Export PDF
              </button> */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '8px', height: '30px' }}>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}> */}
                <div>
                  {/* <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                    Select Header Profile:
                  </label> */}
                  <select
                    value={selectedProfileId}
                    onChange={(e) => setSelectedProfileId(e.target.value)}
                    style={{ ...styles.input, padding: '4px 8px', fontSize: '13px' }}
                  >
                    <option value="branch">Header Profile (PO Branch: {viewingOrder.order.branch_name})</option>
                    <optgroup label="Company Profiles">
                      {companyProfiles.map(profile => (
                        <option key={profile.id} value={profile.id}>
                          {profile.company_name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <button
                  onClick={() => exportToPDF(viewingOrder.order.id, selectedProfileId)}
                  style={{
                    ...styles.button,
                    backgroundColor: '#10b981'
                  }}
                >
                  <Download style={{ width: '16px', height: '16px' }} />
                  Export PDF
                </button>
              </div>

            </div>


          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ ...styles.card, maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                {editingId ? `Edit Purchase Order #${editingId}` : 'Create Purchase Order'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={styles.iconButton}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => {
                    const branchId = e.target.value;
                    setForm({
                      ...form,
                      branch_id: branchId,
                      supplier_id: '', // Clear supplier when branch changes
                      items: []
                    });
                    // Fetch suppliers for the selected branch
                    fetchBranchSuppliers(branchId);
                  }}
                  style={styles.input}
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Supplier *</label>
                <select
                  value={form.supplier_id}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    // Find the supplier object to get their default payment term
                    const selectedSupplier = branchSuppliers.find(s => s.id === parseInt(selectedId));

                    setForm({
                      ...form,
                      supplier_id: selectedId,
                      // Autopopulate the term if the supplier has one, otherwise keep current
                      payment_term_id: selectedSupplier?.payment_term_id || form.payment_term_id || ''
                    });
                  }}
                  style={{
                    ...styles.input,
                    backgroundColor: !form.branch_id ? '#f3f4f6' : 'white',
                    cursor: !form.branch_id ? 'not-allowed' : 'pointer'
                  }}
                  disabled={!form.branch_id}
                >
                  <option value="">
                    {!form.branch_id ? 'Select a branch first' : 'Select Supplier'}
                  </option>
                  {branchSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Order Date *</label>
                <input
                  type="date"
                  min={getMinDate(form.branch_id, postedPeriods)}
                  value={form.order_date}
                  onChange={(e) => setForm({ ...form, order_date: e.target.value })}
                  style={{
                    ...styles.input,
                    borderColor: isPeriodRestricted(form.branch_id, form.order_date, postedPeriods) ? '#ef4444' : '#d1d5db'
                  }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Terms *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={form.payment_term_id}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        payment_term_id: e.target.value
                      });
                    }}
                    style={{ ...styles.input, flex: 1 }}
                  >
                    <option value="">Select Payment Term</option>
                    {paymentTerms.map(pt => (
                      <option key={pt.id} value={pt.id}>
                        {pt.term_name} {pt.days > 0 ? `(${pt.days} days)` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowPaymentTermForm(!showPaymentTermForm)}
                    style={{
                      ...styles.button,
                      ...styles.buttonSuccess,
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                    title="Add New Payment Term"
                  >
                    <Plus style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Term Form Modal */}
            {showPaymentTermForm && (
              <div style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Add New Payment Term
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 3fr', gap: '12px', marginBottom: '12px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Term Name *</label>
                    <input
                      type="text"
                      value={paymentTermForm.term_name}
                      onChange={(e) => setPaymentTermForm({ ...paymentTermForm, term_name: e.target.value })}
                      placeholder="e.g., Net 45"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Days</label>
                    <input
                      type="number"
                      value={paymentTermForm.days}
                      onChange={(e) => setPaymentTermForm({ ...paymentTermForm, days: parseInt(e.target.value) || 0 })}
                      style={styles.input}
                      min="0"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Description</label>
                    <input
                      type="text"
                      value={paymentTermForm.description}
                      onChange={(e) => setPaymentTermForm({ ...paymentTermForm, description: e.target.value })}
                      placeholder="Optional description"
                      style={styles.input}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleAddPaymentTerm}
                    style={{ ...styles.button, padding: '6px 12px', fontSize: '13px' }}
                  >
                    Add Term
                  </button>
                  <button
                    onClick={() => {
                      setShowPaymentTermForm(false);
                      setPaymentTermForm({ term_name: '', days: 0, description: '' });
                    }}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes or instructions"
                style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Items</h3>
                <button
                  onClick={addItem}
                  style={{
                    ...styles.button,
                    ...styles.buttonSuccess,
                    padding: '6px 12px',
                    fontSize: '13px',
                    opacity: form.branch_id ? 1 : 0.5,
                    cursor: form.branch_id ? 'pointer' : 'not-allowed'
                  }}
                  disabled={!form.branch_id}
                  title={form.branch_id ? 'Add Item' : 'Select branch first'}
                >
                  <Plus style={{ width: '16px', height: '16px' }} /> Add Item
                </button>
              </div>

              {!form.branch_id && form.items.length === 0 && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #f59e0b'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                    ⚠️ Please select a branch first to see available inventory items
                  </p>
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: '25%' }}>Inventory Item *</th>
                      <th style={{ ...styles.th, width: '15%', textAlign: 'center' }}>Qty</th>
                      <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Unit</th>
                      <th style={{ ...styles.th, width: '20%', textAlign: 'center' }}>Unit Price</th>
                      <th style={{ ...styles.th, width: '20%', textAlign: 'center' }}>Line Total</th>
                      <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => {
                      const lineTotal = (item.quantity || 0) * (item.unit_price || 0);

                      return (
                        <tr key={idx}>
                          <td style={styles.td}>
                            <select
                              value={item.inventory_item_id || ''}
                              onChange={(e) => updateItem(idx, 'inventory_item_id', e.target.value)}
                              style={{ ...styles.input, padding: '6px 8px', fontSize: '13px' }}
                              disabled={!form.branch_id}
                            >
                              <option value="">{form.branch_id ? 'Select Item...' : 'Select branch first...'}</option>
                              {filteredInventoryItems.map(invItem => {
                                const shortItemCode = invItem.item_code?.includes('.') ? invItem.item_code.split('.').pop() : invItem.item_code;
                                return (
                                  <option key={invItem.id} value={invItem.id}>
                                    {shortItemCode} - {invItem.item_name}
                                  </option>
                                );
                              })}
                            </select>
                            {item.inventory_item_id && (
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                {item.item_name}
                              </div>
                            )}
                          </td>
                          <td style={styles.td}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.quantity || ''}
                              placeholder="0.00"
                              onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value))}
                              style={{ ...styles.input, textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <span style={{ fontSize: '13px' }}>{item.unit_of_measurement}</span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <input type="number" min="0" step="0.01" placeholder="0.00" value={item.unit_price || ''} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} style={{ ...styles.input, textAlign: 'right' }} />
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <span style={{ fontWeight: '600', color: '#4f46e5' }}>
                              ₱{formatCurrency(lineTotal.toFixed(2))}
                            </span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <button onClick={() => removeItem(idx)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                              <Trash2 style={{ width: '16px', height: '16px' }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {form.items.length === 0 && form.branch_id && (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderRadius: '6px'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  No items added yet. Click "Add Item" to start.
                </p>
              </div>
            )}

            {/* Summary */}
            {form.items.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Items:</span>
                    <span style={{ fontWeight: '600' }}>{form.items.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Quantity:</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(form.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0).toFixed(2))}
                    </span>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '16px',
                  paddingTop: '12px',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <span style={{ fontWeight: 'bold' }}>Total Amount:</span>
                  <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                    ₱{formatCurrency(
                      form.items.reduce((sum, item) =>
                        sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0
                      ).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleSubmit} style={styles.button}>
                {editingId ? 'Update Order' : 'Create Order'}
              </button>
              <button onClick={() => { setShowForm(false); resetForm(); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Supplier</th>
            <th style={styles.th}>Branch</th>
            <th style={styles.th}>Order Date</th>
            <th style={styles.th}>Total</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(order => (
            <tr key={order.id}>
              <td style={{ ...styles.td, fontWeight: '500' }}>PO-{order.id}</td>
              <td style={styles.td}>{order.supplier_name}</td>
              <td style={styles.td}>{order.branch_name || 'Main Office'}</td>
              <td style={styles.td}>{new Date(order.order_date || order.created_at).toLocaleDateString('en-US', dateOptions)}</td>
              <td style={{ ...styles.td, fontWeight: '600' }}>₱{formatCurrency(order.total_amount)}</td>
              <td style={styles.td}>
                <span style={{ ...styles.badge, ...(order.status === 'pending' ? styles.badgeYellow : styles.badgeGreen) }}>
                  {order.status}
                </span>
              </td>
              <td style={styles.td}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button onClick={() => handleView(order)} style={{ ...styles.iconButton, color: '#3b82f6' }} title="View">
                    <Eye style={{ width: '16px', height: '16px' }} />
                  </button>
                  {/* {!isPeriodRestricted(order.branch_id, (order.order_date || order.created_at), postedPeriods) && (
                    <>
                      <button onClick={() => handleEdit(order)} style={{ ...styles.iconButton, color: '#10b981' }} title="Edit">
                        <Edit style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button onClick={() => handleDelete(order.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Delete">
                        <Trash2 style={{ width: '16px', height: '16px' }} />
                      </button>
                    </>
                  )} */}
                  <button onClick={() => handleEdit(order)} style={{ ...styles.iconButton, color: '#10b981' }} title="Edit">
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button onClick={() => handleDelete(order.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Delete">
                    <Trash2 style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

const AdjustmentEntries = ({ token, postedPeriods }) => {
  // const { canApprove, canDelete } = usePermissions();
  const [entries, setEntries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(null);
  const [adjustmentCategories, setAdjustmentCategories] = useState([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [form, setForm] = useState({
    branch_id: '',
    adjustment_date: new Date().toLocaleDateString('en-CA'),
    reason: '',
    items: [{
      inventory_item_id: '',
      batch_id: '',
      item_code: '',
      item_name: '',
      quantity: 0,
      adjustment_type: 'add',
      unit_of_measurement: '',
      unit_price: 0,
      available_batches: []
    }]
  });
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchEntries();
    fetchBranches();
    fetchInventoryItems();
    fetchAdjustmentCategories();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment_entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEntries(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);

      if (authorizedBranches.length === 1) {
        setForm(prev => ({ ...prev, branch_id: authorizedBranches[0].id }));
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventorydropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInventoryItems(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchAdjustmentCategories = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAdjustmentCategories(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBatchesForItem = async (inventoryId, itemIndex) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventory/${inventoryId}/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const batches = await res.json();

      const updatedItems = [...form.items];
      updatedItems[itemIndex].available_batches = batches;
      setForm({ ...form, items: updatedItems });
    } catch (err) {
      console.error('Error fetching batches:', err);
    }
  };

  const handleSubmit = async () => {
    if (!form.branch_id || !form.adjustment_date) {
      alert('Please select branch and date');
      return;
    }

    if (isPeriodRestricted(form.branch_id, form.adjustment_date, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    const hasInvalidItems = form.items.some(item => {
      // Check basic validation
      if (!item.inventory_item_id || item.quantity <= 0) {
        return true;
      }
      // Batch is required only for subtract type
      if (item.adjustment_type === 'subtract' && !item.batch_id) {
        return true;
      }
      // Unit price is required for add type
      if (item.adjustment_type === 'add' && (!item.unit_price || item.unit_price <= 0)) {
        return true;
      }
      return false;
    });

    if (hasInvalidItems) {
      alert('Please ensure:\n- All items have valid selection and quantity > 0\n- Batch selected for subtractions\n- Unit price > 0 for additions');
      return;
    }

    try {
      const url = editingId
        ? `${API_URL}/adjustment_entries/${editingId}`
        : `${API_URL}/adjustment_entries`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchEntries();
        resetForm();
        alert(editingId ? 'Adjustment entry updated successfully' : 'Adjustment entry created and pending approval');
      } else {
        const data = await res.json();
        alert(data.error || 'Error saving adjustment entry');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error saving adjustment entry');
    }
  };

  const handleApprove = async (entryId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment_entries/${entryId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        fetchEntries();
        setShowApprovalModal(null);
        alert('Adjustment entry approved successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error approving adjustment entry');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error approving adjustment entry');
    }
  };

  const handleReject = async (entryId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment_entries/${entryId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rejection_reason: rejectionReason })
      });

      if (res.ok) {
        fetchEntries();
        setShowApprovalModal(null);
        setRejectionReason('');
        alert('Adjustment entry rejected');
      } else {
        const data = await res.json();
        alert(data.error || 'Error rejecting adjustment entry');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error rejecting adjustment entry');
    }
  };

  const handleEdit = async (entry) => {
    if (entry.status !== 'pending') {
      alert('Only pending entries can be edited');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment_entries/${entry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      // Fetch batches for each item
      const itemsWithBatches = await Promise.all(
        data.items.map(async (item) => {
          const batchRes = await fetchWithAuth(`${API_URL}/inventory/${item.inventory_item_id}/batches`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const batches = await batchRes.json();

          return {
            inventory_item_id: item.inventory_item_id,
            batch_id: item.batch_id || "",
            item_code: item.item_code,
            item_description: item.item_name,
            quantity: Math.abs(item.quantity),
            adjustment_type: item.adjustment_type || (item.quantity >= 0 ? 'add' : 'subtract'),
            unit_of_measurement: item.unit_of_measurement,
            unit_price: item.unit_price,
            available_batches: batches
          };
        })
      );

      setForm({
        branch_id: data.entry.branch_id,
        adjustment_date: data.entry.adjustment_date,
        adjustment_category_id: data.entry.adjustment_category_id,
        reason: data.entry.reason || '',
        items: itemsWithBatches
      });
      setEditingId(entry.id);
      setShowForm(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading adjustment entry');
    }
  };

  const handleDelete = async (id) => {
    const entry = entries.find(e => e.id === id);
    if (entry && entry.status !== 'pending') {
      alert('Only pending entries can be deleted');
      return;
    }

    if (!window.confirm('Delete this adjustment entry?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment_entries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchEntries();
        alert('Adjustment entry deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting adjustment entry');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting adjustment entry');
    }
  };

  const viewEntry = async (entryId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/adjustment_entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingEntry(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading entry details');
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [
        ...form.items,
        {
          inventory_item_id: '',
          batch_id: '',
          item_code: '',
          item_name: '',
          quantity: 0,
          adjustment_type: 'add',
          unit_of_measurement: '',
          unit_price: 0,
          available_batches: []
        }
      ]
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = async (index, field, value) => {
    const updatedItems = [...form.items];

    if (field === 'inventory_item_id') {
      const selectedItem = inventoryItems.find(
        item => item.id == value && item.branch_id == form.branch_id
      );
      if (selectedItem) {
        updatedItems[index].inventory_item_id = value;
        updatedItems[index].item_code = selectedItem.item_code;
        updatedItems[index].item_name = selectedItem.item_name;
        updatedItems[index].unit_of_measurement = selectedItem.unit_of_measurement;
        updatedItems[index].batch_id = '';
        updatedItems[index].unit_price = selectedItem.unit_price;
        updatedItems[index].available_batches = [];

        setForm({ ...form, items: updatedItems });

        // Fetch batches for this item
        await fetchBatchesForItem(value, index);
      }
    } else {
      updatedItems[index][field] = value;
      setForm({ ...form, items: updatedItems });
    }

    setForm({ ...form, items: updatedItems });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({
      branch_id: '',
      adjustment_date: new Date().toLocaleDateString('en-CA'),
      reason: '',
      items: [{
        inventory_item_id: '',
        batch_id: '',
        item_code: '',
        item_name: '',
        quantity: 0,
        adjustment_type: 'add',
        unit_of_measurement: '',
        unit_price: 0,
        available_batches: []
      }]
    });
  };

  const branchInventoryItems = form.branch_id
    ? inventoryItems.filter(item => item.branch_id === parseInt(form.branch_id))
    : [];

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#fef3c7', color: '#92400e', border: '#f59e0b', text: 'Pending' },
      approved: { bg: '#d1fae5', color: '#065f46', border: '#10b981', text: 'Approved' },
      rejected: { bg: '#fee2e2', color: '#991b1b', border: '#ef4444', text: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span style={{
        ...styles.badge,
        backgroundColor: badge.bg,
        color: badge.color,
        border: `1px solid ${badge.border}`
      }}>
        {badge.text}
      </span>
    );
  };

  const styles = {
    container: { padding: '24px', backgroundColor: '#f3f4f6', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
    button: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
    buttonSecondary: { backgroundColor: '#6b7280' },
    buttonSuccess: { backgroundColor: '#10b981' },
    buttonDanger: { backgroundColor: '#ef4444' },
    buttonWarning: { backgroundColor: '#f59e0b' },
    card: { backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    table: { width: '100%', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgeGreen: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #10b981' },
    badgeRed: { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #ef4444' },
    badgeBlue: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Adjustment Entries</h1>
        {canCreate('adjustment_entries') && (
          <button onClick={() => setShowForm(!showForm)} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} />
            Add Adjustment Entry
          </button>
        )}
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
            {editingId ? 'Edit Adjustment Entry' : 'New Adjustment Entry'}
          </h2>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Branch *</label>
              <select
                value={form.branch_id}
                onChange={(e) => setForm({ ...form, branch_id: e.target.value, items: [{ inventory_item_id: '', batch_id: '', item_code: '', item_description: '', quantity: 0, adjustment_type: 'add', unit_of_measurement: '', available_batches: [] }] })}
                style={styles.input}
              >
                <option value="">Select Branch</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Adjustment Date *</label>
              <input
                type="date"
                min={getMinDate(form.branch_id, postedPeriods)}
                value={form.adjustment_date}
                onChange={(e) => setForm({ ...form, adjustment_date: e.target.value })}
                style={{
                  ...styles.input,
                  borderColor: isPeriodRestricted(form.branch_id, form.order_date, postedPeriods) ? '#ef4444' : '#d1d5db'
                }}
              />
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category *</label>
              <select
                value={form.adjustment_category_id}
                onChange={(e) => {
                  setForm({
                    ...form,
                    adjustment_category_id: e.target.value
                  });
                }}
                style={styles.input}
              >
                <option value="">Select category</option>
                {adjustmentCategories.map(ac => (
                  <option key={ac.id} value={ac.id}>
                    {ac.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Reason</label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                style={{ ...styles.input, minHeight: '60px' }}
                placeholder="Reason for adjustment (optional)"
              />
            </div>
          </div>

          <div style={{ marginTop: '24px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Items</h3>
              <button
                onClick={addItem}
                style={{ ...styles.button, ...styles.buttonSuccess, padding: '6px 12px', fontSize: '13px' }}
                disabled={!form.branch_id}
              >
                <Plus style={{ width: '16px', height: '16px' }} /> Add Item
              </button>
            </div>

            {!form.branch_id && (
              <p style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>
                Please select a branch first
              </p>
            )}

            <div style={{ ...styles.alert, backgroundColor: '#dbeafe', color: '#1e3a8a', border: '1px solid #3b82f6', marginBottom: '12px' }}>
              ℹ️ <strong>Note:</strong> For "Add" adjustments, a new batch will be automatically created upon approval. Batch selection is only required for "Subtract" adjustments.
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Batch</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Unit</th>
                    <th style={styles.th}>Unit Price</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={styles.td}>
                        <select
                          value={item.inventory_item_id}
                          onChange={(e) => updateItem(idx, 'inventory_item_id', e.target.value)}
                          style={{ ...styles.input, padding: '6px 8px', fontSize: '13px', minWidth: '200px' }}
                          disabled={!form.branch_id}
                        >
                          <option value="">Select Item...</option>
                          {branchInventoryItems.map(invItem => (
                            <option key={invItem.id} value={invItem.id}>
                              {invItem.item_code.includes('.') ? invItem.item_code.split('.').pop() : invItem.item_code} - {invItem.item_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <select
                          value={item.adjustment_type}
                          onChange={(e) => updateItem(idx, 'adjustment_type', e.target.value)}
                          style={{ ...styles.input, padding: '6px 8px', fontSize: '13px', width: '120px' }}
                        >
                          <option value="add">Add (+)</option>
                          <option value="subtract">Subtract (-)</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <select
                          value={item.batch_id}
                          onChange={(e) => {
                            const selectedBatchId = e.target.value;
                            // Find the actual batch object from the available_batches array
                            const selectedBatch = item.available_batches.find(
                              (b) => b.id.toString() === selectedBatchId
                            );

                            // Update the item state with BOTH the ID and the unit_price from that batch
                            const updatedItems = [...form.items];
                            updatedItems[idx] = {
                              ...updatedItems[idx],
                              batch_id: selectedBatchId,
                              // We parse the unit_price from the API response you provided
                              unit_price: selectedBatch ? parseFloat(selectedBatch.unit_price) : 0
                            };
                            setForm({ ...form, items: updatedItems });
                          }}
                          style={{ ...styles.input, padding: '6px 8px', fontSize: '13px', minWidth: '150px' }}
                          disabled={!item.inventory_item_id || item.adjustment_type === 'add'}
                        >
                          <option value="">
                            {item.adjustment_type === 'add' ? 'New batch will be created' : 'Select Batch...'}
                          </option>
                          {item.adjustment_type === 'subtract' && item.available_batches.map(batch => (
                            <option key={batch.id} value={batch.id}>
                              {batch.batch_number} (Stock: {batch.remaining_quantity})
                              {batch.expiry_date ? ` - Exp: ${new Date(batch.expiry_date).toLocaleDateString('en-US', dateOptions)}` : ''}
                            </option>
                          ))}
                        </select>
                        {item.adjustment_type === 'add' && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                            Auto-generated on approval
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity || ''}
                          placeholder="0.00"
                          onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          style={{ ...styles.input, textAlign: 'right', width: '100px' }}
                        />
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                          {item.unit_of_measurement || '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {item.adjustment_type === 'add' ? (
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price || ''}
                            placeholder="0.00"
                            onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                            style={{ ...styles.input, textAlign: 'right', width: '100px' }}
                          />
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>₱{formatCurrency(item.unit_price)}</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        <button
                          onClick={() => removeItem(idx)}
                          style={{ ...styles.iconButton, color: '#ef4444' }}
                          disabled={form.items.length === 1}
                        >
                          <Trash2 style={{ width: '16px', height: '16px' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={handleSubmit} style={styles.button}>
              {editingId ? 'Update' : 'Create'} Adjustment Entry
            </button>
            <button onClick={resetForm} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Items</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Approver</th>
                <th style={styles.th}>Created</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>
                    AE-{entry.id}
                  </td>
                  <td style={styles.td}>{entry.branch_name}</td>
                  <td style={styles.td}>
                    {new Date(entry.adjustment_date).toLocaleDateString('en-US', dateOptions)}
                  </td>
                  <td style={styles.td}>
                    <span>{entry.item_count} items</span>
                  </td>
                  <td style={styles.td}>
                    {getStatusBadge(entry.status || 'pending')}
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {entry.approver_name || '—'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {new Date(entry.created_at).toLocaleDateString('en-US', dateOptions)}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => viewEntry(entry.id)}
                      style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}
                      title="View Details"
                    >
                      <Eye style={{ width: '16px', height: '16px' }} />
                    </button>
                    {entry.status === 'pending' && (
                      <>
                        {canApprove('adjustment_entries') && (
                          <button
                            onClick={() => setShowApprovalModal(entry)}
                            style={{ ...styles.iconButton, color: '#10b981', marginRight: '8px' }}
                            title="Approve/Reject"
                          >
                            <Check style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canUpdate('adjustment_entries') && (
                          <button
                            onClick={() => handleEdit(entry)}
                            style={{ ...styles.iconButton, color: '#f59e0b', marginRight: '8px' }}
                            title="Edit"
                          >
                            <Edit style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canDelete('adjustment_entries') && (
                          <button
                            onClick={() => handleDelete(entry.id)}
                            style={{ ...styles.iconButton, color: '#ef4444' }}
                            title="Delete"
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entries.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No adjustment entries found</p>
          </div>
        )}
      </div>

      {/* View Entry Modal */}
      {viewingEntry && (
        <div style={styles.modal} onClick={() => setViewingEntry(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Adjustment Entry AE-{viewingEntry.entry.id}
              </h2>
              <button onClick={() => setViewingEntry(null)} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px', marginBottom: '24px' }}>
              <strong>Branch:</strong>
              <span>{viewingEntry.entry.branch_name}</span>

              <strong>Date:</strong>
              <span>{new Date(viewingEntry.entry.adjustment_date).toLocaleDateString('en-US', dateOptions)}</span>

              <strong>Status:</strong>
              <div>{getStatusBadge(viewingEntry.entry.status || 'pending')}</div>

              <strong>Category:</strong>
              <span>{viewingEntry.entry.category_name || 'N/Asss'}</span>

              <strong>Reason:</strong>
              <span>{viewingEntry.entry.reason || 'N/A'}</span>

              {viewingEntry.entry.approver_name && (
                <>
                  <strong>Approved By:</strong>
                  <span>{viewingEntry.entry.approver_name}</span>

                  <strong>Approved At:</strong>
                  <span>{new Date(viewingEntry.entry.approved_at).toLocaleString()}</span>
                </>
              )}

              {viewingEntry.entry.rejection_reason && (
                <>
                  <strong>Rejection Reason:</strong>
                  <span style={{ color: '#ef4444' }}>{viewingEntry.entry.rejection_reason}</span>
                </>
              )}

              <strong>Created:</strong>
              <span>{new Date(viewingEntry.entry.created_at).toLocaleDateString('en-US', dateOptions)}</span>
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>Items</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item Code</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Batch</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Unit</th>
                    <th style={styles.th}>Unit Price</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingEntry.items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code}</td>
                      <td style={styles.td}>{item.item_name}</td>
                      <td style={styles.td}>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                          {item.batch_number || '—'}
                          {item.expiry_date && ` (Exp: ${new Date(item.expiry_date).toLocaleDateString('en-US', dateOptions)})`}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          ...(item.adjustment_type === 'add' ? styles.badgeGreen : styles.badgeRed)
                        }}>
                          {item.adjustment_type === 'add' ? 'Add' : 'Subtract'}
                        </span>
                      </td>
                      <td style={{ ...styles.td, fontWeight: '600' }}>
                        {item.adjustment_type === 'add' ? '+' : '-'}{Math.abs(item.quantity)}
                      </td>
                      <td style={styles.td}>{item.unit_of_measurement}</td>
                      <td style={{ ...styles.td, fontWeight: '600' }}>{item.unit_price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => setViewingEntry(null)}
              style={{ ...styles.button, marginTop: '16px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div style={styles.modal} onClick={() => { setShowApprovalModal(null); setRejectionReason(''); }}>
          <div style={{ ...styles.modalContent, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Review Adjustment Entry
              </h2>
              <button onClick={() => { setShowApprovalModal(null); setRejectionReason(''); }} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ fontSize: '14px', marginBottom: '24px' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Entry ID:</strong> AE-{showApprovalModal.id}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Branch:</strong> {showApprovalModal.branch_name}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Date:</strong> {new Date(showApprovalModal.adjustment_date).toLocaleDateString('en-US', dateOptions)}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Items:</strong> {showApprovalModal.item_count}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...styles.label, display: 'block' }}>Rejection Reason (optional for rejection)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', width: '90%' }}
                placeholder="Provide reason if rejecting..."
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleApprove(showApprovalModal.id)}
                style={{ ...styles.button, ...styles.buttonSuccess, flex: 1 }}
              >
                <Check style={{ width: '18px', height: '18px' }} />
                Approve
              </button>
              <button
                onClick={() => handleReject(showApprovalModal.id)}
                style={{ ...styles.button, ...styles.buttonDanger, flex: 1 }}
              >
                <XCircle style={{ width: '18px', height: '18px' }} />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const Pullout = ({ token, postedPeriods }) => {
  const getLocalDateTime = () => {
    return getPHTDateTimeString();
  };

  const formatDateLocal = (dateString) => {
    if (!dateString) return "";
    try {
      // Handle "YYYY-MM-DD HH:mm:ss" or ISO strings by extracting parts directly
      const parts = dateString.split(/T| /);
      const datePart = parts[0];
      const timePart = parts[1] ? parts[1].substring(0, 5) : "";

      if (!datePart) return dateString;

      const [year, month, day] = datePart.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = months[parseInt(month, 10) - 1];

      return `${monthLabel} ${day}, ${year}, ${timePart}`;
    } catch (e) {
      return dateString;
    }
  };

  const [requests, setRequests] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [form, setForm] = useState({
    branch_id: '',
    request_date: getLocalDateTime(),
    reason: '',
    items: [{
      inventory_item_id: '',
      item_code: '',
      item_name: '',
      quantity: 0,
      unit_of_measurement: '',
      available_batches: []
    }]
  });
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchRequests();
    fetchBranches();
    fetchInventoryItems();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pull_outs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRequests(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);

      if (authorizedBranches.length === 1) {
        setForm(prev => ({ ...prev, branch_id: authorizedBranches[0].id }));
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventorydropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInventoryItems(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    if (isPeriodRestricted(form.branch_id, form.order_date, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    if (!form.branch_id || !form.request_date) {
      alert('Please select branch and date');
      return;
    }

    const hasInvalidItems = form.items.some(item => {
      // Check basic validation
      if (!item.inventory_item_id || item.quantity <= 0) {
        return true;
      }
      return false;
    });

    if (hasInvalidItems) {
      alert('Please ensure:\n- All items have valid selection and quantity > 0');
      return;
    }

    try {
      const url = editingId
        ? `${API_URL}/pull_outs/${editingId}`
        : `${API_URL}/pull_outs`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchRequests();
        resetForm();
        alert(editingId ? 'Pull out request updated successfully' : 'Pull out request created and pending approval');
      } else {
        const data = await res.json();
        alert(data.error || 'Error saving pull out request');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error saving pull out request');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pull_outs/${requestId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        fetchRequests();
        setShowApprovalModal(null);
        alert('Pull out request approved successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error approving pull out request');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error approving pull out request');
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/pull_outs/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rejection_reason: rejectionReason })
      });

      if (res.ok) {
        fetchRequests();
        setShowApprovalModal(null);
        setRejectionReason('');
        alert('Pull out request rejected');
      } else {
        const data = await res.json();
        alert(data.error || 'Error rejecting pull out request');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error rejecting pull out request');
    }
  };

  const handleEdit = async (request) => {
    if (request.status !== 'pending') {
      alert('Only pending requests can be edited');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/pull_outs/${request.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      console.log("edit data: ", data)
      // Fetch PR item
      const pulloutItems = await Promise.all(
        data.items.map(async (item) => {
          // Fetch current available stock for each item
          let availableStock = 0;
          try {
            // We need selectedItem.item_id. But 'item' from pull_outs items might not have it.
            // Actually pull_out_items joined with inventory should have item_id if we adjust the GET route OR 
            // we look it up in inventoryItems.
            const invItem = inventoryItems.find(i => i.id == item.inventory_item_id);
            if (invItem) {
              const res = await fetchWithAuth(`${API_URL}/inventory/available-stock?item_id=${invItem.item_id}&branch_id=${data.request.branch_id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (res.ok) {
                const stockData = await res.json();
                availableStock = stockData.available;
              }
            }
          } catch (err) {
            console.error('Error fetching stock during edit:', err);
          }

          return {
            inventory_item_id: item.inventory_item_id,
            item_code: item.item_code,
            item_name: item.item_name,
            quantity: Math.abs(item.quantity),
            unit_of_measurement: item.unit_of_measurement,
            available_stock: availableStock
          };
        })
      );

      // Convert the saved datetime to local datetime-local format
      const localDateTime = getPHTDateTimeString(data.request.request_date);

      setForm({
        branch_id: data.request.branch_id,
        request_date: localDateTime,
        reason: data.request.reason || '',
        items: pulloutItems
      });
      setEditingId(request.id);
      setShowForm(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading pull out request');
    }
  };

  const handleDelete = async (id) => {
    const request = requests.find(e => e.id === id);
    if (request && request.status !== 'pending') {
      alert('Only pending requests can be deleted');
      return;
    }

    if (!window.confirm('Delete this pull out request?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/pull_outs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchRequests();
        alert('Pull out request deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting pull out request');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting pull out request');
    }
  };

  const viewRequest = async (requestId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pull_outs/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      // If pending, fetch latest available stock for the branch
      if (data.request.status === 'pending') {
        const itemsWithStock = await Promise.all(
          data.items.map(async (item) => {
            const invItem = inventoryItems.find(i => i.id == item.inventory_item_id);
            if (invItem) {
              const stockRes = await fetchWithAuth(
                `${API_URL}/inventory/available-stock?item_id=${invItem.item_id}&branch_id=${data.request.branch_id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (stockRes.ok) {
                const stockData = await stockRes.json();
                return { ...item, available_stock: stockData.available };
              }
            }
            return { ...item, available_stock: 0 };
          })
        );
        data.items = itemsWithStock;
      }

      setViewingRequest(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading request details');
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [
        ...form.items,
        {
          inventory_item_id: '',
          batch_id: '',
          item_code: '',
          item_name: '',
          quantity: 0,
          unit_of_measurement: '',
          available_batches: []
        }
      ]
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = async (index, field, value) => {
    const updatedItems = [...form.items];

    if (field === 'inventory_item_id') {
      const selectedItem = inventoryItems.find(
        item => item.id == value && item.branch_id == form.branch_id
      );
      if (selectedItem) {
        updatedItems[index].inventory_item_id = value;
        updatedItems[index].item_code = selectedItem.item_code;
        updatedItems[index].item_name = selectedItem.item_name;
        updatedItems[index].unit_of_measurement = selectedItem.unit_of_measurement;
        updatedItems[index].batch_id = '';
        updatedItems[index].available_batches = [];

        // Fetch available stock
        try {
          const res = await fetchWithAuth(`${API_URL}/inventory/available-stock?item_id=${selectedItem.item_id}&branch_id=${form.branch_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            updatedItems[index].available_stock = data.available;
          }
        } catch (err) {
          console.error('Error fetching stock:', err);
          updatedItems[index].available_stock = 0;
        }

        setForm({ ...form, items: updatedItems });
      }
    } else {
      updatedItems[index][field] = value;
      setForm({ ...form, items: updatedItems });
    }

    setForm({ ...form, items: updatedItems });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({
      branch_id: '',
      request_date: getLocalDateTime(),
      reason: '',
      items: [{
        inventory_item_id: '',
        batch_id: '',
        item_code: '',
        item_name: '',
        quantity: 0,
        unit_of_measurement: '',
        available_batches: []
      }]
    });
  };

  const branchInventoryItems = form.branch_id
    ? inventoryItems.filter(item => item.branch_id === parseInt(form.branch_id))
    : [];

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#fef3c7', color: '#92400e', border: '#f59e0b', text: 'Pending' },
      approved: { bg: '#d1fae5', color: '#065f46', border: '#10b981', text: 'Approved' },
      rejected: { bg: '#fee2e2', color: '#991b1b', border: '#ef4444', text: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span style={{
        ...styles.badge,
        backgroundColor: badge.bg,
        color: badge.color,
        border: `1px solid ${badge.border}`
      }}>
        {badge.text}
      </span>
    );
  };

  const styles = {
    container: { padding: '24px', backgroundColor: '#f3f4f6', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
    button: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
    buttonSecondary: { backgroundColor: '#6b7280' },
    buttonSuccess: { backgroundColor: '#10b981' },
    buttonDanger: { backgroundColor: '#ef4444' },
    buttonWarning: { backgroundColor: '#f59e0b' },
    card: { backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    table: { width: '100%', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgeGreen: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #10b981' },
    badgeRed: { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #ef4444' },
    badgeBlue: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Pull out Requests</h1>
        {canCreate('pull_out') && (
          <button onClick={() => setShowForm(!showForm)} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} />
            Add Pull out Request
          </button>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ ...styles.card, maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
                {editingId ? 'Edit Pull out Request' : 'New Pull out Request'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={styles.iconButton}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value, items: [] })}
                  style={styles.input}
                  disabled={editingId}
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Pull Out Date & Time *</label>
                <input
                  type="datetime-local"
                  value={form.request_date}
                  onChange={(e) => setForm({ ...form, request_date: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Reason</label>
                <input
                  type="text"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Reason for pull out (optional)"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold' }}>Items</h3>
                <button
                  onClick={addItem}
                  style={styles.button}
                  disabled={!form.branch_id}
                >
                  <Plus style={{ width: '16px', height: '16px' }} /> Add Item
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Inventory Item *</th>
                      <th style={styles.th}>Available Stock</th>
                      <th style={styles.th}>Pull out Quantity *</th>
                      <th style={styles.th}>Unit</th>
                      <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>
                          <select
                            value={item.inventory_item_id}
                            onChange={(e) => updateItem(idx, 'inventory_item_id', parseInt(e.target.value))}
                            style={styles.input}
                          >
                            <option value="">Select Item...</option>
                            {branchInventoryItems.map(invItem => (
                              <option key={invItem.id} value={invItem.id}>
                                {invItem.item_code} - {invItem.item_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: '500' }}>{item.available_stock || 0}</span>
                        </td>
                        <td style={styles.td}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            max={item.available_stock}
                            value={item.quantity || ''}
                            onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value))}
                            style={styles.input}
                          />
                        </td>
                        <td style={styles.td}>
                          {item.unit_of_measurement}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            onClick={() => removeItem(idx)}
                            style={{ ...styles.iconButton, color: '#ef4444' }}
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={handleSubmit} style={styles.button}>
                {editingId ? 'Update' : 'Create'} Pull out request
              </button>
              <button onClick={resetForm} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Pull Out Date & Time</th>
                <th style={styles.th}>Items</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Requester</th>
                <th style={styles.th}>Approver</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => (
                <tr key={request.id}>
                  <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>
                    PR-{request.id}
                  </td>
                  <td style={styles.td}>{request.branch_name}</td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '500' }}>{formatDateLocal(request.request_date)}</div>
                    {request.entry_delay > 0 && (
                      <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: '600', marginTop: '2px' }}>
                        Entry Delay: {request.entry_delay} {request.entry_delay === 1 ? 'day' : 'days'}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '12px' }}>
                      {request.items && request.items.length > 0 ? (
                        <>
                          <div style={{ fontWeight: '500', color: '#374151' }}>
                            {request.items.length} {request.items.length === 1 ? 'item' : 'items'}
                          </div>
                          <div style={{ color: '#6b7280' }}>
                            Total Qty: {request.items.reduce((sum, item) => sum + Math.abs(item.quantity || 0), 0).toFixed(2)}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>No items</span>
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {getStatusBadge(request.status || 'pending')}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{request.requested_by_name}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{formatPHDatetime(request.created_at)}</div>
                  </td>
                  <td style={styles.td}>
                    {request.approved_by_name ? (
                      <>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>{request.approved_by_name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {formatPHDatetime(request.updated_at)}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => viewRequest(request.id)}
                      style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}
                      title="View Details"
                    >
                      <Eye style={{ width: '16px', height: '16px' }} />
                    </button>
                    {request.status === 'pending' && (
                      <>
                        {canApprove('pull_out') && currentUser.id !== request.requested_by && (
                          <button
                            onClick={() => setShowApprovalModal(request)}
                            style={{ ...styles.iconButton, color: '#10b981', marginRight: '8px' }}
                            title="Approve/Reject"
                          >
                            <Check style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canUpdate('pull_out') && (
                          <button
                            onClick={() => handleEdit(request)}
                            style={{ ...styles.iconButton, color: '#f59e0b', marginRight: '8px' }}
                            title="Edit"
                          >
                            <Edit style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canDelete('pull_out') && (
                          <button
                            onClick={() => handleDelete(request.id)}
                            style={{ ...styles.iconButton, color: '#ef4444' }}
                            title="Delete"
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {requests.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No pull out requests found</p>
          </div>
        )}
      </div>

      {/* View Request Modal */}
      {viewingRequest && (
        <div style={styles.modal} onClick={() => setViewingRequest(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Pull out Request PR-{viewingRequest.request.id}
              </h2>
              <button onClick={() => setViewingRequest(null)} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px', marginBottom: '24px' }}>
              <strong>Branch:</strong>
              <span>{viewingRequest.request.branch_name}</span>

              <strong>Date & Time:</strong>
              <span>{formatDateLocal(viewingRequest.request.request_date)}</span>

              <strong>Status:</strong>
              <div>{getStatusBadge(viewingRequest.request.status || 'pending')}</div>

              <strong>Reason:</strong>
              <span>{viewingRequest.request.reason || 'N/A'}</span>

              <strong>Requested By:</strong>
              <span>{viewingRequest.request.requested_by_name || 'N/A'}</span>

              {viewingRequest.request.status !== 'pending' && viewingRequest.request.requested_by_name && (
                <>
                  <strong>{viewingRequest.request.status === 'approved' ? 'Approved By:' : 'Rejected By:'}</strong>
                  <span>{viewingRequest.request.approved_by_name || '—'}</span>

                  {viewingRequest.request.approved_at && (
                    <>
                      <strong>{viewingRequest.request.status === 'approved' ? 'Approved At:' : 'Rejected At:'}</strong>
                      <span>{formatDateTime(viewingRequest.request.approved_at)}</span>
                    </>
                  )}
                </>
              )}

              {viewingRequest.request.rejection_reason && (
                <>
                  <strong>Rejection Reason:</strong>
                  <span style={{ color: '#ef4444' }}>{viewingRequest.request.rejection_reason}</span>
                </>
              )}
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>Items</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item Code</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Quantity</th>
                    {viewingRequest.request.status === 'pending' && (
                      <th style={{ ...styles.th, textAlign: 'center' }}>Available Stock</th>
                    )}
                    <th style={styles.th}>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingRequest.items.map((item, idx) => {
                    const isPending = viewingRequest.request.status === 'pending';
                    const insufficient = isPending && Math.abs(item.quantity) > (item.available_stock || 0);
                    return (
                      <tr key={idx} style={insufficient ? { backgroundColor: '#fef2f2' } : {}}>
                        <td style={{ ...styles.td, fontFamily: 'monospace' }}>
                          {item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code}
                        </td>
                        <td style={styles.td}>{item.item_name}</td>
                        <td style={{ ...styles.td, fontWeight: '600' }}>
                          {'-'}{Math.abs(item.quantity || 0).toFixed(2)}
                        </td>
                        {isPending && (
                          <td style={{
                            ...styles.td,
                            color: insufficient ? '#ef4444' : '#10b981',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}>
                            {item.available_stock || 0}
                          </td>
                        )}
                        <td style={styles.td}>{item.unit_of_measurement}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => setViewingRequest(null)}
              style={{ ...styles.button, marginTop: '16px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div style={styles.modal} onClick={() => { setShowApprovalModal(null); setRejectionReason(''); }}>
          <div style={{ ...styles.modalContent, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Review Pull out Request
              </h2>
              <button onClick={() => { setShowApprovalModal(null); setRejectionReason(''); }} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ fontSize: '14px', marginBottom: '24px' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Request ID:</strong> PR-{showApprovalModal.id}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Branch:</strong> {showApprovalModal.branch_name}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Date:</strong> {formatDateTime(showApprovalModal.request_date)}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Items:</strong> {showApprovalModal.item_count}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...styles.label, display: 'block' }}>Rejection Reason (optional for rejection)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', width: '90%' }}
                placeholder="Provide reason if rejecting..."
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleApprove(showApprovalModal.id)}
                style={{ ...styles.button, ...styles.buttonSuccess, flex: 1 }}
              >
                <Check style={{ width: '18px', height: '18px' }} />
                Approve
              </button>
              <button
                onClick={() => handleReject(showApprovalModal.id)}
                style={{ ...styles.button, ...styles.buttonDanger, flex: 1 }}
              >
                <XCircle style={{ width: '18px', height: '18px' }} />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const Items = ({ token }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branchCategories, setBranchCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uomList, setUomList] = useState([]);
  const [showUomForm, setShowUomForm] = useState(false);
  const [uomFormValue, setUomFormValue] = useState('');
  const [form, setForm] = useState({
    item_code: '',
    item_name: '',
    item_type: 'item',
    parent_item_id: '',
    branch_parent_item_id: '',
    loyverse_sku: '',
    markup_percentage: '',
    is_active: 1,
    description: ''
  });
  const [filterType, setFilterType] = useState('');
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => { fetchItems(); fetchUoms(); }, []);

  const fetchUoms = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/units-of-measurement`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUomList(data);
    } catch (err) {
      console.error('Error fetching UOMs:', err);
    }
  };

  const handleAddUom = async () => {
    if (!uomFormValue.trim()) {
      alert('Please enter a unit of measurement code');
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_URL}/units-of-measurement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ uom_code: uomFormValue.trim() })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create UOM');
      }
      const data = await res.json();
      await fetchUoms();
      setForm({ ...form, unit_of_measurement: data.uom_code });
      setShowUomForm(false);
      setUomFormValue('');
      alert('Unit of measurement added successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error adding unit of measurement');
    }
  };

  const fetchItems = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/items`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(data);

      const categoryList = data.filter(item => item.item_type === 'category');
      const branchCategoryList = data.filter(item => item.item_type === 'category-br');
      setCategories(categoryList);
      setBranchCategories(branchCategoryList);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/items/${editingId}` : `${API_URL}/items`;
      const method = editingId ? 'PUT' : 'POST';

      // Adding category prefix for items
      let fullItemCode = form.item_code;
      let finalParentId = form.item_type === 'category' ? null : (form.parent_item_id ? parseInt(form.parent_item_id) : null);
      let finalBranchParentId = form.item_type === 'category-br' ? null : (form.branch_parent_item_id ? parseInt(form.branch_parent_item_id) : null);

      // Only apply prefixing if a parent is selected and it's not a category type itself
      // if (finalParentId && form.item_type === 'item') {
      //   // Find the parent item in your existing 'items' state
      //   const parent = items.find(i => i.id === parseInt(form.parent_item_id));
      //   if (parent) {
      //     fullItemCode = `${parent.item_code}.${form.item_code}`;
      //   }
      // }

      // Only apply prefixing if a branch parent is selected and it's not a category type itself
      // if (finalBranchParentId && form.item_type === 'item') {
      //   // Find the branch parent item in your existing 'items' state
      //   const branch_parent = items.find(i => i.id === parseInt(form.branch_parent_item_id));
      //   if (branch_parent) {
      //     fullItemCode = `${branch_parent.item_code}.${form.item_code}`;
      //   }
      // }

      const payload = {
        ...form,
        item_code: fullItemCode, // Send the full prefixed code to backend
        parent_item_id: finalParentId,
        branch_parent_item_id: finalBranchParentId
      };

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      const data = await res.json(); // Read once to avoid "body stream already read"

      if (res.ok) {
        fetchItems();
        setShowForm(false);
        setEditingId(null);
        setForm({ item_code: '', item_name: '', item_type: 'item', parent_item_id: '', branch_parent_item_id: '', is_active: 1, description: '' });
        alert('Item saved successfully');
      } else {
        alert(data.error || 'Error saving item');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleEdit = (item) => {
    const ShortItemCode = item.item_code.includes('.') ? item.item_code.split('.').pop() : item.item_code;
    setForm({
      item_code: ShortItemCode,
      item_name: item.item_name,
      item_type: item.item_type,
      parent_item_id: item.parent_item_id || '',
      branch_parent_item_id: item.branch_parent_item_id || '',
      unit_of_measurement: item.unit_of_measurement || '',
      loyverse_sku: item.loyverse_sku,
      markup_percentage: item.markup_percentage,
      is_active: item.is_active,
      description: item.description || ''
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/items/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchItems();
        alert('Item deleted successfully');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting item. It may be in use.');
    }
  };

  const itemTypes = [
    { value: 'item', label: 'Item', color: '#3b82f6' },
    { value: 'category', label: 'Category', color: '#f59e0b' },
    { value: 'category-br', label: 'Branch Category', color: '#049345ff' }
  ];

  const filteredItems = items.filter(item => {
    const matchesType = filterType ? item.item_type === filterType : true;
    const matchesSearch = searchQuery.toLowerCase().trim() === '' ||
      item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.item_code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const itemTypeStats = itemTypes.map(type => ({
    ...type,
    count: items.filter(a => a.item_type === type.value).length
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Items</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search by item code or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ ...styles.input, paddingLeft: '36px', minWidth: '250px' }}
            />
          </div>
          {canCreate('items') && (
            <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ item_code: '', item_name: '', item_type: 'item', parent_item_id: '', branch_parent_item_id: '', unit_of_measurement: '', is_active: 1, description: '' }); }} style={styles.button}>
              <Plus style={{ width: '20px', height: '20px' }} /> Add Item
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {itemTypeStats.map(type => (
          <div
            key={type.value}
            onClick={() => setFilterType(filterType === type.value ? '' : type.value)}
            style={{
              padding: '16px',
              backgroundColor: filterType === type.value ? type.color : 'white',
              color: filterType === type.value ? 'white' : '#1f2937',
              borderRadius: '8px',
              border: `2px solid ${type.color}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{type.count}</div>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{type.label}</div>
          </div>
        ))}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingId(null); setForm({ item_code: '', item_name: '', item_type: 'item', parent_item_id: '', branch_parent_item_id: '', unit_of_measurement: '', loyverse_sku: '', markup_percentage: '', is_active: 1, description: '' }); }}
        title={editingId ? 'Edit Item' : 'Add Item'}
      >
        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Item Code *</label>
            <input
              type="text"
              value={form.item_code}
              onChange={(e) => setForm({ ...form, item_code: e.target.value })}
              style={styles.input}
              placeholder="e.g., CHE, BB"
              disabled={editingId !== null}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Item Name *</label>
            <input type="text" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} style={styles.input} placeholder="e.g., Cheese" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Item Type *</label>
            <select
              value={form.item_type}
              onChange={(e) => setForm({ ...form, item_type: e.target.value })}
              style={styles.input}
              disabled={editingId !== null}
            >
              {itemTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          {/* Only show the Category dropdown if the type is 'item' */}
          {form.item_type === 'item' && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  value={form.parent_item_id}
                  onChange={(e) => setForm({ ...form, parent_item_id: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select a category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.item_code} - {c.item_name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Branch Category</label>
                <select
                  value={form.branch_parent_item_id}
                  onChange={(e) => setForm({ ...form, branch_parent_item_id: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select a branch category</option>
                  {branchCategories.map(cb => (
                    <option key={cb.id} value={cb.id}>{cb.item_code} - {cb.item_name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Unit of Measurement</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={form.unit_of_measurement || ''}
                    onChange={(e) => setForm({ ...form, unit_of_measurement: e.target.value })}
                    style={{ ...styles.input, flex: 1 }}
                  >
                    <option value="">Select UOM</option>
                    {uomList.map(u => (
                      <option key={u.uom_code} value={u.uom_code}>{u.uom_code}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowUomForm(!showUomForm)}
                    style={{
                      ...styles.button,
                      ...styles.buttonSuccess,
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                    title="Add New Unit of Measurement"
                  >
                    <Plus style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              </div>

              {showUomForm && (
                <div style={{
                  gridColumn: '1 / -1',
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                    Add New Unit of Measurement
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                    <div style={{ ...styles.formGroup, flex: 1, marginBottom: 0 }}>
                      <label style={styles.label}>UOM Code *</label>
                      <input
                        type="text"
                        value={uomFormValue}
                        onChange={(e) => setUomFormValue(e.target.value)}
                        placeholder="e.g., kg, gal, pc, box"
                        style={styles.input}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handleAddUom}
                        style={{ ...styles.button, padding: '8px 16px', fontSize: '13px' }}
                      >
                        Add UOM
                      </button>
                      <button
                        onClick={() => { setShowUomForm(false); setUomFormValue(''); }}
                        style={{ ...styles.button, ...styles.buttonSecondary, padding: '8px 16px', fontSize: '13px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Loyverse SKU</label>
                <input
                  type="text"
                  value={form.loyverse_sku || ''}
                  onChange={(e) => setForm({ ...form, loyverse_sku: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., 10046"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Markup percentage</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.markup_percentage || ''}
                  placeholder="e.g., 10"
                  onChange={(e) => setForm({ ...form, markup_percentage: e.target.value })}
                  style={styles.input}
                />
              </div>
            </>
          )}
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...styles.input, minHeight: '60px' }} placeholder="Optional notes about this item..." />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Status</label>
            <div style={toggleStyles.toggleContainer}>
              <label style={toggleStyles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={form.is_active === 1}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                  style={toggleStyles.toggleInput}
                />
                <span style={toggleStyles.toggleSlider(form.is_active === 1)}>
                  <span style={toggleStyles.toggleSliderBefore(form.is_active === 1)}></span>
                </span>
              </label>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                {form.is_active === 1 ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
          <button onClick={handleSubmit} style={styles.button}>{editingId ? 'Update' : 'Create'}</button>
          <button onClick={() => { setShowForm(false); setEditingId(null); setForm({ item_code: '', item_name: '', item_type: 'item', parent_item_id: '', branch_parent_item_id: '', unit_of_measurement: '', is_active: 1, description: '' }); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
        </div>
      </Modal>

      {filterType && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
            Showing: {itemTypes.find(t => t.value === filterType)?.label} items ({filteredItems.length})
          </span>
          <button onClick={() => setFilterType('')} style={{ ...styles.button, padding: '6px 12px', fontSize: '13px' }}>
            Show All
          </button>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Code</th>
            <th style={styles.th}>Item Name</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Branch Category</th>
            <th style={styles.th}>Unit of Measurement</th>
            <th style={styles.th}>SKU</th>
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.map(item => {
            const typeInfo = itemTypes.find(t => t.value === item.item_type);
            const parentItem = items.find(a => a.id === item.parent_item_id);
            const branchParentItem = items.find(a => a.id === item.branch_parent_item_id);
            return (
              <tr key={item.id}>
                <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>{item.item_code.includes('.') ? item.item_code.split('.').pop() : item.item_code}</td>
                <td style={{ ...styles.td, fontWeight: '500' }}>
                  {item.parent_item_id && <span style={{ color: '#6b7280', marginRight: '8px' }}>└─</span>}
                  {item.item_name}
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: typeInfo?.color + '20', color: typeInfo?.color, border: `1px solid ${typeInfo?.color}` }}>
                    {typeInfo?.label}
                  </span>
                </td>
                <td style={styles.td}>
                  {parentItem ? (
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {parentItem.item_code} - {parentItem.item_name}
                    </span>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.td}>
                  {branchParentItem ? (
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {branchParentItem.item_code} - {branchParentItem.item_name}
                    </span>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>{item.unit_of_measurement}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>{item.loyverse_sku}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, ...(item.is_active ? styles.badgeGreen : styles.badgeRed) }}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button onClick={() => handleEdit(item)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                    <Trash2 style={{ width: '16px', height: '16px' }} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>


      {filteredItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
          <p style={{ margin: 0 }}>No items found. Click "Add Item" to create one.</p>
        </div>
      )}
    </div>
  );
};


const InventoryBatches = ({ token }) => {
  const [batches, setBatches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    branch_id: '',
    search: '',
    show_consumed: false
  });
  const [form, setForm] = useState({
    inventory_item_id: '',
    branch_id: '',
    batch_number: '',
    batch_date: new Date().toLocaleDateString('en-CA'),
    quantity: 0,
    unit_price: 0
  });

  useEffect(() => {
    fetchBatches();
    fetchInventoryItems();
    fetchBranches();
  }, [filters.branch_id, filters.show_consumed]);

  const fetchBatches = async () => {
    try {
      let url = `${API_URL}/inventory-batches`;
      const params = new URLSearchParams();

      if (filters.branch_id) params.append('branch_id', filters.branch_id);
      params.append('show_consumed', filters.show_consumed ? '1' : '0');

      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetchWithAuth(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setBatches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error:', err);
      setBatches([]);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInventoryItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error:', err);
      setInventoryItems([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;
        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
    } catch (err) {
      console.error('Error:', err);
      setBranches([]);
    }
  };

  const handleSubmit = async () => {
    if (!form.inventory_item_id || !form.batch_number || !form.batch_date || form.quantity <= 0 || form.unit_price < 0) {
      alert('Please fill all required fields with valid values');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/inventory-batches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchBatches();
        resetForm();
        alert('Batch created successfully');
      } else {
        const error = await res.json();
        alert(error.error || 'Error creating batch');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error creating batch');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this batch? This action cannot be undone.')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/inventory-batches/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchBatches();
        alert('Batch deleted successfully');
      } else {
        const error = await res.json();
        alert(error.error || 'Error deleting batch');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting batch');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setForm({
      inventory_item_id: '',
      branch_id: '',
      batch_number: '',
      batch_date: new Date().toLocaleDateString('en-CA'),
      quantity: 0,
      unit_price: 0,
      selling_price: 0
    });
  };

  const clearFilters = () => {
    setFilters({
      branch_id: '',
      search: '',
      show_consumed: false
    });
  };

  const filteredBatches = batches.filter(batch => {
    const searchLower = filters.search.toLowerCase();
    const shortItemCode = batch.item_code?.includes('.') ? batch.item_code.split('.').pop() : batch.item_code;

    return !filters.search ||
      shortItemCode?.toLowerCase().includes(searchLower) ||
      batch.item_code?.toLowerCase().includes(searchLower) ||
      batch.item_name?.toLowerCase().includes(searchLower) ||
      batch.batch_number?.toLowerCase().includes(searchLower);
  });

  const selectedItem = inventoryItems.find(item => item.id === parseInt(form.inventory_item_id));
  const filteredInventoryItems = form.branch_id
    ? inventoryItems.filter(item => item.branch_id === parseInt(form.branch_id))
    : inventoryItems;

  const styles = {
    //container: { padding: '24px', backgroundColor: '#f3f4f6', minHeight: '100vh' },
    //header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    //title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
    button: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' },
    //buttonSecondary: { backgroundColor: '#6b7280' },
    //buttonSuccess: { backgroundColor: '#10b981' },
    card: { backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    table: { width: '100%', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgeGreen: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #10b981' },
    badgeRed: { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #ef4444' },
    badgeYellow: { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' },
    // badgeBlue: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    infoBox: { padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid' },
    infoBoxBlue: { backgroundColor: '#eff6ff', borderColor: '#3b82f6', color: '#1e40af' },
    infoBoxYellow: { backgroundColor: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Inventory Batches</h1>
      </div>

      {/* Info Box */}
      <div style={{ ...styles.infoBox, ...styles.infoBoxBlue }}>
        <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0 }} />
        <div style={{ fontSize: '14px' }}>
          <strong>About Inventory Batches:</strong> Track individual batches with specific costs for accurate FIFO costing.
          Create a new batch whenever you receive inventory with a different unit price.
        </div>
      </div>

      {/* Filters */}
      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter style={{ width: '20px', height: '20px', color: '#6b7280' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Filters</h3>
        </div>

        <div style={styles.grid3}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch</label>
            <select
              value={filters.branch_id}
              onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
              style={styles.input}
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Search Item</label>
            <input
              type="text"
              placeholder="Search by item code or name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Status</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={filters.show_consumed}
                onChange={(e) => setFilters({ ...filters, show_consumed: e.target.checked })}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '14px' }}>Show consumed batches</span>
            </label>
          </div>
        </div>

        <button onClick={clearFilters} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px' }}>
          <X style={{ width: '16px', height: '16px' }} />
          Clear Filters
        </button>
      </div>

      {/* Batches Table */}
      <div style={styles.card}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Batch Inventory</h3>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Batch Number</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Original Qty</th>
              <th style={styles.th}>Remaining</th>
              <th style={styles.th}>Unit Price</th>
              <th style={styles.th}>Total Value</th>
              <th style={styles.th}>Selling Price</th>
              <th style={styles.th}>Landed Cost</th>
              <th style={styles.th}>Status</th>
              {/* <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {filteredBatches.map(batch => {
              const totalValue = parseFloat(batch.remaining_quantity || 0) * parseFloat(batch.unit_price || 0);
              const usagePercent = ((parseFloat(batch.quantity || 0) - parseFloat(batch.remaining_quantity || 0)) / parseFloat(batch.quantity || 1)) * 100;

              return (
                <tr key={batch.id}>
                  <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: '600' }}>
                    {batch.batch_number}
                  </td>
                  <td style={styles.td}>
                    {new Date(batch.batch_date).toLocaleDateString('en-US', dateOptions)}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '500' }}>{batch.item_code?.includes('.') ? batch.item_code.split('.').pop() : batch.item_code}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{batch.item_name}</div>
                  </td>
                  <td style={styles.td}>{batch.branch_name || '—'}</td>
                  <td style={styles.td}>
                    {formatCurrency(parseFloat(batch.quantity))} {batch.unit_of_measurement}
                  </td>
                  <td style={{ ...styles.td, fontWeight: '600', color: '#4f46e5' }}>
                    {formatCurrency(parseFloat(batch.remaining_quantity))} {batch.unit_of_measurement}
                    {usagePercent > 0 && (
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                        {usagePercent.toFixed(0)}% used
                      </div>
                    )}
                  </td>
                  <td style={{ ...styles.td, fontWeight: '600', color: '#4f46e5' }}>
                    ₱{formatCurrency(parseFloat(batch.unit_price))}
                  </td>
                  <td style={styles.td}>
                    ₱{formatCurrency(totalValue)}
                  </td>
                  <td style={styles.td}>
                    {formatCurrency(batch.selling_price)}
                  </td>
                  <td style={styles.td}>
                    {formatCurrency(batch.landed_cost)}
                  </td>
                  <td style={styles.td}>
                    {parseFloat(batch.remaining_quantity) <= 0 ? (
                      <span style={styles.badgeRed}>Consumed</span>
                    ) : parseFloat(batch.remaining_quantity) >= parseFloat(batch.quantity) ? (
                      <span style={styles.badgeGreen}>Available</span>
                    ) : (
                      <span style={styles.badgeYellow}>Partial</span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredBatches.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <Package style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No batches found</p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
              Create a batch to start tracking inventory costs
            </p>
          </div>
        )}
      </div>
    </div>
  );
};



const Inventory = ({ token }) => {
  const [inventory, setInventory] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    branch_id: '',
    item_id: '',
    min_quantity: 0,
    max_quantity: 0,
    unit_of_measurement: ''
  });
  const [filter, setFilter] = useState({
    branch_id: '',
    search: ''
  });
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchBranches();
    fetchInventory();
  }, []);

  useEffect(() => {
    if (form.branch_id && showForm && !editingId) {
      fetchAvailableItems(form.branch_id);
    }
  }, [form.branch_id, showForm, editingId]);

  const fetchInventory = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInventory(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchAvailableItems = async (branchId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/items/available?branch_id=${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAvailableItems(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const resetForm = () => {
    setForm({
      branch_id: '',
      item_id: '',
      min_quantity: 0,
      max_quantity: 0,
      unit_of_measurement: ''
    });
    setEditingId(null);
    setAvailableItems([]);
  };

  const handleSubmit = async () => {
    if (!form.branch_id || !form.item_id) {
      alert('Please select both branch and item');
      return;
    }

    try {
      const url = editingId
        ? `${API_URL}/inventory/${editingId}`
        : `${API_URL}/inventory`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save inventory item');
      }

      fetchInventory();
      setShowForm(false);
      resetForm();
      alert(`Inventory item ${editingId ? 'updated' : 'added'} successfully!`);
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error saving inventory item');
    }
  };

  const handleEdit = (item) => {
    setForm({
      branch_id: item.branch_id,
      item_id: item.item_id,
      min_quantity: item.min_quantity || 0,
      max_quantity: item.max_quantity || 0,
      unit_of_measurement: item.unit_of_measurement || ''
    });
    // Temporarily put the current item into the availableItems list so the dropdown has an option to display while disabled.
    setAvailableItems([{
      id: item.item_id,
      item_code: item.item_code,
      item_name: item.item_name || item.item_description,
      unit_of_measurement: item.unit_of_measurement
    }]);

    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this item from inventory?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/inventory/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete inventory item');
      }

      fetchInventory();
      alert('Inventory item removed successfully');
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error deleting inventory item');
    }
  };

  const syncItems = async () => {
    if (!window.confirm('Sync all inventory items with the latest data from Items master list?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/inventory/sync-items`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      alert(data.message);
      fetchInventory();
    } catch (err) {
      console.error('Error:', err);
      alert('Error syncing inventory items');
    }
  };

  const filteredInventory = inventory.filter(item => {
    const matchesBranch = !filter.branch_id || item.branch_id === parseInt(filter.branch_id);
    const shortItemCode = item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code;

    const matchesSearch = !filter.search ||
      shortItemCode?.toLowerCase().includes(filter.search.toLowerCase()) || // Search short item code
      item.item_code?.toLowerCase().includes(filter.search.toLowerCase()) || // Still allow searching full code
      item.item_description?.toLowerCase().includes(filter.search.toLowerCase()) ||
      item.item_name?.toLowerCase().includes(filter.search.toLowerCase());
    return matchesBranch && matchesSearch;
  });

  const getStockStatus = (item) => {
    if (item.quantity_on_hand < item.min_quantity && item.min_quantity > 0) {
      return { label: 'Low Stock', color: '#ef4444' };
    }
    if (item.quantity_on_hand > item.max_quantity && item.max_quantity > 0) {
      return { label: 'Overstock', color: '#f59e0b' };
    }
    return { label: 'Normal', color: '#10b981' };
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          Branch Inventory
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canCreate('inventory') && (
            <button
              onClick={() => { setShowForm(!showForm); resetForm(); }}
              style={styles.button}
            >
              <Plus style={{ width: '20px', height: '20px' }} />
              Add to Inventory
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{
        ...styles.card,
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
      }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Filter by Branch</label>
          <select
            value={filter.branch_id}
            onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}
            style={styles.input}
          >
            <option value="">All Branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by item code or name..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            style={styles.input}
          />
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ ...styles.card, maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
                {editingId ? 'Edit Inventory Item' : 'Add Item to Inventory'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={styles.iconButton}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value, item_id: '' })}
                  style={styles.input}
                  disabled={editingId}
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Item *</label>
                <select
                  value={form.item_id}
                  onChange={(e) => {
                    const selectedId = parseInt(e.target.value);
                    const selectedItem = availableItems.find(i => i.id === selectedId);

                    setForm({
                      ...form,
                      item_id: selectedId,
                      unit_of_measurement: selectedItem ? selectedItem.unit_of_measurement : ''
                    });
                  }}
                  style={styles.input}
                  disabled={editingId || !form.branch_id}
                >
                  <option value="">
                    {!form.branch_id ? 'Select branch first...' : 'Select Item'}
                  </option>
                  {availableItems.map(item => {
                    const ShortItemCode = item.item_code.includes('.') ? item.item_code.split('.').pop() : item.item_code;

                    return (
                      <option key={item.id} value={item.id}>
                        {ShortItemCode} - {item.item_name}
                      </option>
                    );
                  })}
                </select>
                {!form.branch_id && (
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Select a branch to see available items
                  </small>
                )}
              </div>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Minimum Quantity (in {form.unit_of_measurement || 'unit'})</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.min_quantity || ''}
                  placeholder="0"
                  onChange={(e) => setForm({ ...form, min_quantity: parseFloat(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Maximum Quantity (in {form.unit_of_measurement || 'unit'})</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.max_quantity || ''}
                  placeholder="0"
                  onChange={(e) => setForm({ ...form, max_quantity: parseFloat(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={handleSubmit} style={styles.button}>
                {editingId ? 'Update' : 'Add to Inventory'}
              </button>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Item Code</th>
              <th style={styles.th}>Item Name</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>On Hand</th>
              <th style={styles.th}>Unit</th>
              <th style={styles.th}>Min</th>
              <th style={styles.th}>Max</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ ...styles.td, textAlign: 'center', color: '#6b7280' }}>
                  No inventory items found
                </td>
              </tr>
            ) : (
              filteredInventory.map(item => {
                const status = getStockStatus(item);
                return (
                  <tr key={item.id}>
                    <td style={styles.td}>{item.branch_name}</td>
                    <td style={{ ...styles.td, fontWeight: '500' }}>{item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code}</td>
                    <td style={styles.td}>{item.item_name || item.item_description}</td>
                    <td style={styles.td}>{item.category_name || '-'}</td>
                    <td style={{ ...styles.td, fontWeight: '600', textAlign: 'center' }}>
                      {item.quantity_on_hand}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {item.unit_of_measurement}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {item.min_quantity}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {item.max_quantity}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: `${status.color}20`,
                        color: status.color
                      }}>
                        {status.label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {canUpdate('inventory') && (
                          <button
                            onClick={() => handleEdit(item)}
                            style={{ ...styles.iconButton, color: '#10b981' }}
                            title="Edit"
                          >
                            <Edit style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canDelete('inventory') && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            style={{ ...styles.iconButton, color: '#ef4444' }}
                            title="Delete"
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DeliveryReceipts = ({ token, postedPeriods }) => {
  const [receipts, setReceipts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [receiptItems, setReceiptItems] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [sourceBranchId, setSourceBranchId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryFee, setDeliveryFee] = useState('');
  const [readyToDispatch, setReadyToDispatch] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  useEffect(() => {
    fetchReceipts();
    fetchBranches();
    fetchInventoryItems();
  }, []);

  useEffect(() => {
    if (viewingReceipt?.id && sourceBranchId) {
      const refreshStockLevels = async () => {
        try {
          const res = await fetchWithAuth(`${API_URL}/delivery_receipts/${viewingReceipt.id}?source_branch_id=${sourceBranchId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();

          // Proactively update the items with the new source_current_stock
          setReceiptItems(data.items);
        } catch (err) {
          console.error('Error updating stock levels:', err);
        }
      };

      refreshStockLevels();
    }
  }, [sourceBranchId, viewingReceipt?.id, token]); // Listen for changes here

  useEffect(() => {
    if (viewingReceipt && sourceBranchId && receiptItems.length > 0) {
      const fetchInitialStock = async () => {
        const updated = [...receiptItems];
        let needsUpdate = false;

        for (let i = 0; i < updated.length; i++) {
          if (
            updated[i].inventory_item_id &&
            (updated[i].source_current_stock === undefined || updated[i].unit_price === undefined)
          ) {
            const inv = inventoryItems.find(x => x.id == updated[i].inventory_item_id);
            if (!inv) continue;

            try {
              const stockRes = await fetchWithAuth(
                `${API_URL}/inventory/available-stock?item_id=${inv.item_id}&branch_id=${sourceBranchId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const stockData = await stockRes.json();
              updated[i].source_current_stock = stockData.available;

              const priceRes = await fetchWithAuth(
                `${API_URL}/inventory/fifo-price?inventory_id=${inv.id}&branch_id=${sourceBranchId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const priceData = await priceRes.json();
              updated[i].unit_price = priceData.selling_price || 0;

              needsUpdate = true;
            } catch (err) {
              updated[i].source_current_stock = 0;
              updated[i].unit_price = 0;
              needsUpdate = true;
            }
          }
        }

        if (needsUpdate) {
          setReceiptItems(updated);
        }
      };

      fetchInitialStock();
    }
  }, [viewingReceipt?.id, sourceBranchId, inventoryItems]);

  const fetchReceipts = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/delivery_receipts`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReceipts(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventorydropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setInventoryItems(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const generateReceipt = async (branchId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/delivery_receipts/generate/${branchId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Delivery receipt generated successfully!');
        fetchReceipts();
      } else {
        const data = await res.json();
        alert(data.message || 'No low stock items for this branch');
      }
    } catch (err) {
      alert('Error generating receipt: ' + err.message);
    }
  };

  const startManualReceipt = () => {
    setIsDraft(true);
    setEditMode(true);

    setViewingReceipt({
      id: null,
      status: 'pending',
      branch_id: '',
      branch_name: '',
      source_branch_id: '',
      delivery_date: '',
      delivery_fee: '',
      created_at: new Date().toLocaleDateString()
    });

    setSourceBranchId('');
    setDeliveryDate('');
    setDeliveryFee('');
    setReceiptItems([]);
  };

  const viewReceipt = async (receiptId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/delivery_receipts/${receiptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingReceipt(data.receipt);
      setReceiptItems(data.items);
      setSourceBranchId(data.receipt.source_branch_id);
      setDeliveryDate(data.receipt.delivery_date || '');
      setDeliveryFee(data.receipt.delivery_fee);
      setEditMode(false);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading receipt details');
    }
  };

  const closeModal = () => {
    setViewingReceipt(null);
    setReceiptItems([]);
    setEditMode(false);
    setSourceBranchId('');
    setDeliveryDate('');
    setDeliveryFee('');
    setIsDraft(false);
  };

  const addItem = () => {
    setReceiptItems([...receiptItems, {
      inventory_item_id: '',
      item_code: '',
      item_name: '',
      quantity: 0,
      unit_of_measurement: '',
      notes: ''
    }]);
  };

  const removeItem = (idx) => {
    setReceiptItems(receiptItems.filter((_, i) => i !== idx));
  };

  const updateItem = async (idx, field, value) => {
    const updated = [...receiptItems];

    const activeSourceId = viewingReceipt?.source_branch_id || sourceBranchId;

    if (field === 'inventory_item_id') {
      const selectedInv = inventoryItems.find(inv => inv.id == value);
      if (!selectedInv) return;

      // Set basic item details immediately
      updated[idx] = {
        ...updated[idx],
        inventory_item_id: value,
        item_code: selectedInv.item_code,
        item_name: selectedInv.item_name,
        unit_of_measurement: selectedInv.unit_of_measurement,
        source_current_stock: 'Loading...',
        unit_price: ''
      };

      if (activeSourceId) {
        try {
          const stockRes = await fetchWithAuth(
            `${API_URL}/inventory/available-stock?item_id=${selectedInv.item_id}&branch_id=${activeSourceId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const stockData = await stockRes.json();
          updated[idx].source_current_stock = stockData.available;

          const priceRes = await fetchWithAuth(
            `${API_URL}/inventory/fifo-price?inventory_id=${selectedInv.id}&branch_id=${activeSourceId}`
          );
          const priceData = await priceRes.json();
          updated[idx].unit_price = priceData.selling_price || 0;
        } catch (err) {
          console.error('Error fetching stock:', err);
          updated[idx].source_current_stock = 0;
          updated[idx].unit_price = 0;
        }
      }
      setReceiptItems(updated);
    } else {
      updated[idx][field] = value;
      setReceiptItems(updated);
    }
  };

  const saveChanges = async () => {
    if (isDraft) {
      return createDeliveryReceipt();
    }

    if (!window.confirm('Save changes to this delivery receipt?')) return;

    const hasInvalidItems = receiptItems.some(item => !item.inventory_item_id || item.quantity <= 0);
    if (hasInvalidItems) {
      alert('Please ensure all items have valid inventory selection and quantity');
      return;
    }

    if (!viewingReceipt?.source_branch_id) {
      alert('Please select a Source Branch');
      return;
    }

    try {
      const itemsToSave = receiptItems.map(item => ({
        inventory_item_id: item.inventory_item_id,
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0
      }));

      const payload = {
        delivery_date: deliveryDate,
        delivery_fee: deliveryFee,
        source_branch_id: parseInt(sourceBranchId),
        items: itemsToSave
      };

      const res = await fetchWithAuth(`${API_URL}/delivery_receipts/${viewingReceipt.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to update delivery receipt');
      }

      alert('Delivery receipt updated successfully!');
      setEditMode(false);
      fetchReceipts();
      viewReceipt(viewingReceipt.id);
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error saving changes');
    }
  };

  const createDeliveryReceipt = async () => {
    if (!viewingReceipt.branch_id) {
      alert('Please select a destination branch');
      return;
    }

    if (!viewingReceipt?.source_branch_id) {
      alert('Please select a source branch');
      return;
    }

    if (receiptItems.length === 0) {
      alert('Please add at least one item');
      return;
    }

    if (isPeriodRestricted(viewingReceipt.branch_id, deliveryDate, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    try {
      const activeSourceId = viewingReceipt.source_branch_id || sourceBranchId;

      const payload = {
        branch_id: parseInt(viewingReceipt.branch_id),
        source_branch_id: activeSourceId ? parseInt(activeSourceId) : null,
        delivery_date: deliveryDate,
        delivery_fee: deliveryFee,
        items: receiptItems.map(item => ({
          inventory_item_id: item.inventory_item_id,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price || 0)
        }))
      };

      const res = await fetchWithAuth(
        `${API_URL}/delivery_receipts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create delivery receipt');
      }

      const data = await res.json();

      setIsDraft(false);
      fetchReceipts();
      viewReceipt(data.id);

    } catch (err) {
      alert(err.message);
    }
  };


  const dispatchReceipt = async () => {
    if (!sourceBranchId || !viewingReceipt?.source_branch_id) {
      alert('Please select a source branch before dispatching');
      return;
    }

    if (!window.confirm('Dispatch this delivery receipt? This will deduct items from the source branch inventory and change status to "in transit".')) {
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/delivery_receipts/${viewingReceipt.id}/dispatch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to dispatch delivery receipt');
      }

      const data = await res.json();
      alert(data.message);
      setReadyToDispatch(false);
      fetchReceipts();
      fetchInventoryItems();
      viewReceipt(viewingReceipt.id);
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error dispatching delivery receipt');
    }
  };


  const deleteReceipt = async (id) => {
    if (!window.confirm('Delete this delivery receipt?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/delivery_receipts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Delivery receipt deleted successfully');
        fetchReceipts();
        if (viewingReceipt?.id === id) {
          closeModal();
        }
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting receipt');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting receipt');
    }
  };

  const branchInventoryItems =
    viewingReceipt && viewingReceipt.branch_id
      ? inventoryItems
        .filter(item => item.branch_id == viewingReceipt.branch_id)
        .sort((a, b) => (a.item_code || "").localeCompare(b.item_code || ""))
      : [];

  const exportToPDF = async (receiptId) => {
    try {
      // Fetch receipt details with items
      const res = await fetchWithAuth(`${API_URL}/delivery_receipts/${receiptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch delivery receipt');
      }

      const data = await res.json();
      const receipt = data.receipt;
      const items = data.items || [];

      // Create PDF
      const doc = new jsPDF();

      const pesoFormatter = new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        currencyDisplay: 'code',
      });

      const qtyFormatter = new Intl.NumberFormat('en-PH', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Company Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('DELIVERY RECEIPT', 105, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(receipt.source_legal_name || 'N/A', 105, 28, { align: 'center' });
      doc.text(receipt.source_address || 'N/A', 105, 33, { align: 'center' });
      doc.text(receipt.source_phone || 'N/A', 105, 38, { align: 'center' });

      // Line separator
      doc.setLineWidth(0.5);
      doc.line(20, 43, 190, 43);

      // DR Information - Left Side
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DR Number:', 20, 55);
      doc.text('Branch:', 20, 62);
      doc.text('Status:', 20, 69);
      doc.text('Created Date:', 20, 76);

      doc.setFont('helvetica', 'normal');
      doc.text(receipt.id ? receipt.id.toString() : 'N/A', 55, 55);
      doc.text(receipt.branch_name || 'N/A', 55, 62);
      doc.text((receipt.status || 'pending').toUpperCase(), 55, 69);
      doc.text(receipt.created_at ? new Date(receipt.created_at).toLocaleDateString('en-US', dateOptions) : 'N/A', 55, 76);

      if (receipt.source_branch_name) {
        doc.setFont('helvetica', 'bold');
        doc.text('Source Branch:', 120, 55);
        doc.setFont('helvetica', 'normal');
        doc.text(receipt.source_branch_name, 160, 55);
      }

      // Received Date - Right Side (if applicable)
      if (receipt.received_at) {
        doc.setFont('helvetica', 'bold');
        doc.text('Received Date:', 120, 62);
        doc.setFont('helvetica', 'normal');
        doc.text(new Date(receipt.received_at).toLocaleDateString('en-US', dateOptions), 160, 62);
      }

      // Items Table
      const tableData = items.map((item) => [
        item.item_code.split('.').pop(),
        item.item_name || 'N/A',
        qtyFormatter.format(item.quantity || 0),
        item.unit_of_measurement || 'N/A',
        pesoFormatter.format(item.unit_price || 0),
        pesoFormatter.format(item.unit_price * item.quantity || 0)
      ]);

      autoTable(doc, {
        startY: 85,
        head: [['Item Code', 'Item Name', 'Qty', 'Unit', 'Unit Price', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [79, 70, 229],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 4
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 35 },
          2: { cellWidth: 25, halign: 'center' },
          3: { cellWidth: 15, halign: 'center' },
          4: { cellWidth: 40, halign: 'right' },
          5: { cellWidth: 45, halign: 'right' }
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        }
      });

      // Summary section
      const finalY = doc.lastAutoTable.finalY + 10;
      const rightAlignX = 190; // X-coordinate for the values to be right-aligned

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Total Items:', 130, finalY);
      doc.text('Total Quantity:', 130, finalY + 7);
      doc.text('Delivery Fee:', 130, finalY + 14);
      doc.text('TOTAL:', 130, finalY + 21);

      doc.setFont('helvetica', 'normal');
      const totalItems = items.length;
      const totalQuantity = items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
      const deliveryFee = receipt.delivery_fee;
      const totalAmount = items.reduce((sum, item) =>
        sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)), 0) + parseFloat(deliveryFee || 0);

      doc.text(totalItems.toString(), rightAlignX, finalY, { align: 'right' });
      doc.text(qtyFormatter.format(totalQuantity), rightAlignX, finalY + 7, { align: 'right' });
      doc.text(pesoFormatter.format(deliveryFee), rightAlignX, finalY + 14, { align: 'right' });
      doc.text(pesoFormatter.format(totalAmount), rightAlignX, finalY + 21, { align: 'right' });

      // Signature Section
      const sigY = finalY + 37;

      // Prepared By
      const splitPreparedBy = doc.splitTextToSize(receipt.created_by_name, 170);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Prepared By:', 20, sigY);
      // doc.line(20, sigY + 15, 70, sigY + 15); // Signature line
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(splitPreparedBy, 20, sigY + 7);
      doc.text(`Date: ${new Date(receipt.created_at).toLocaleDateString('en-US', dateOptions)}`, 20, sigY + 14);

      // Received By
      if (receipt.received_date) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Received By:', 140, sigY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(receipt.received_by, 140, sigY + 7);
        doc.text(`Date: ${new Date(receipt.received_date).toLocaleDateString('en-US', dateOptions)}`, 140, sigY + 14);
      }

      // Notes Section (if any)
      if (receipt.notes) {
        const notesY = sigY + 35;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 20, notesY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const splitNotes = doc.splitTextToSize(receipt.notes, 170);
        doc.text(splitNotes, 20, notesY + 5);
      }

      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text('This is a computer-generated document.', 105, pageHeight - 15, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, pageHeight - 10, { align: 'center' });

      // Save PDF
      doc.save(`Delivery_Receipt_DR-${receipt.id}.pdf`);

    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          Delivery Receipts
        </h1>
        <button
          onClick={() => startManualReceipt()}
          style={{ ...styles.button }}
        >
          <Plus style={{ width: '20px', height: '20px' }} />
          Add Delivery Receipt
        </button>
      </div>

      {/* Generate Receipts Card */}
      {/* <div style={styles.card}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
          Generate Delivery Receipt for Low Stock Items
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {branches.map(b => (
            <button
              key={b.id}
              onClick={() => generateReceipt(b.id)}
              style={{
                ...styles.button,
                backgroundColor: '#6366f1'
              }}
            >
              <Package style={{ width: '16px', height: '16px' }} />
              {b.name}
            </button>
          ))}
        </div>
      </div> */}

      {/* Receipts Table */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          All Delivery Receipts
        </h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>DR Number</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Created Date</th>
              <th style={styles.th}>Received Date</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ ...styles.td, textAlign: 'center', color: '#6b7280' }}>
                  No delivery receipts found
                </td>
              </tr>
            ) : (
              receipts.map(receipt => (
                <tr key={receipt.id}>
                  <td style={{ ...styles.td, fontWeight: '500' }}>DR-{receipt.id}</td>
                  <td style={styles.td}>{receipt.destination_branch_name}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(receipt.status === 'pending' ? styles.badgeYellow :
                        receipt.status === 'in transit' ? { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' } :
                          styles.badgeGreen)
                    }}>
                      {receipt.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {new Date(receipt.created_at).toLocaleDateString('en-US', dateOptions)}
                  </td>
                  <td style={styles.td}>
                    {receipt.received_at
                      ? new Date(receipt.received_at).toLocaleDateString('en-US', dateOptions)
                      : '-'}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => viewReceipt(receipt.id)}
                        style={{ ...styles.iconButton, color: '#3b82f6' }}
                        title="View Details"
                      >
                        <Eye style={{ width: '16px', height: '16px' }} />
                      </button>
                      {receipt.status !== 'pending' && (
                        <button
                          onClick={() => exportToPDF(receipt.id)}
                          style={{ ...styles.iconButton, color: '#10b981' }}
                          title="Export PDF"
                        >
                          <Download style={{ width: '16px', height: '16px' }} />
                        </button>
                      )}
                      {/* <button
                        onClick={() => deleteReceipt(receipt.id)}
                        style={{ ...styles.iconButton, color: '#ef4444' }}
                        title="Delete"
                        disabled={receipt.status === 'received'}
                      >
                        <Trash2 style={{ width: '16px', height: '16px' }} />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View/Edit Modal */}
      {viewingReceipt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            ...styles.card,
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: 0
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                  {isDraft ? "New Delivery Receipt" : `Delivery Receipt #${viewingReceipt.id}`}
                </h2>
                {isDraft ? (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={styles.label}>Destination Branch</label>
                    <select
                      value={viewingReceipt.branch_id}
                      onChange={(e) =>
                        setViewingReceipt({
                          ...viewingReceipt,
                          branch_id: e.target.value
                        })
                      }
                      style={styles.input}
                    >
                      <option value="">Select Destination Branch</option>
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p style={{ fontSize: '14px', color: '#6b7280' }}>
                    {viewingReceipt.branch_name} • Created: {new Date(viewingReceipt.created_at).toLocaleDateString('en-US', dateOptions)}
                  </p>
                )}
              </div>
              <button onClick={closeModal} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {/* Status Banner */}
            {!isDraft && viewingReceipt && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                // Dynamic Background based on status
                backgroundColor:
                  viewingReceipt.status === 'received' ? '#d1fae5' :
                    viewingReceipt.status === 'in transit' ? '#dbeafe' : '#fef3c7'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Dynamic Icons */}
                  {viewingReceipt.status === 'received' ? (
                    <Check style={{ width: '20px', height: '20px', color: '#065f46' }} />
                  ) : viewingReceipt.status === 'in transit' ? (
                    <Truck style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                  ) : (
                    <AlertCircle style={{ width: '20px', height: '20px', color: '#92400e' }} />
                  )}

                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    color:
                      viewingReceipt.status === 'received' ? '#065f46' :
                        viewingReceipt.status === 'in transit' ? '#1e40af' : '#92400e'
                  }}>
                    {viewingReceipt.status === 'received' && 'This delivery receipt has been received and cannot be edited'}
                    {viewingReceipt.status === 'in transit' && 'This delivery receipt is in transit and cannot be edited'}
                    {viewingReceipt.status === 'pending' && 'This delivery receipt is pending and can be edited'}
                  </p>
                </div>
              </div>
            )}

            {/* Source Branch and Delivery Date Fields */}
            {editMode && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Delivery Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ ...styles.label, marginBottom: '8px', display: 'block' }}>
                      Source Branch (Where items come from)
                    </label>
                    {/* <select
                      value={sourceBranchId}
                      onChange={async (e) => {
                        const newBranchId = e.target.value;
                        setSourceBranchId(newBranchId); // Update the state

                        // Refresh stock for all items currently in the list
                        if (newBranchId && receiptItems.length > 0) {
                          const updatedItems = [...receiptItems];

                          for (let i = 0; i < updatedItems.length; i++) {
                            const item = updatedItems[i];
                            if (item.inventory_item_id) {
                              // Find the base item ID from your inventory list
                              const inv = inventoryItems.find(x => x.id == item.inventory_item_id);
                              if (!inv) continue;

                              try {
                                const res = await fetchWithAuth(
                                  `${API_URL}/inventory/available-stock?item_id=${inv.item_id}&branch_id=${newBranchId}`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                const data = await res.json();
                                updatedItems[i].source_current_stock = data.available;
                              } catch (err) {
                                updatedItems[i].source_current_stock = 0;
                              }
                            }
                          }
                          setReceiptItems(updatedItems);
                        }
                      }}
                      style={styles.input}
                    > */}
                    <select
                      value={viewingReceipt.source_branch_id || ''}
                      onChange={async (e) => {
                        const newBranchId = e.target.value;

                        // Update the main object so the item filter sees the change immediately
                        setViewingReceipt(prev => ({ ...prev, source_branch_id: newBranchId }));

                        // Refresh stock for all items currently in the list
                        if (newBranchId && receiptItems.length > 0) {
                          try {
                            const updatedItems = await Promise.all(receiptItems.map(async (item) => {
                              if (!item.inventory_item_id) return item;

                              const inv = inventoryItems.find(x => x.id == item.inventory_item_id);
                              if (!inv) return item;

                              const res = await fetchWithAuth(
                                `${API_URL}/inventory/available-stock?item_id=${inv.item_id}&branch_id=${newBranchId}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              const data = await res.json();

                              return { ...item, source_current_stock: data.available || 0 };
                            }));

                            setReceiptItems(updatedItems);
                          } catch (err) {
                            console.error("Error updating stocks:", err);
                          }
                        }
                      }}
                      style={styles.input}
                    >
                      <option value="">Select Source Branch</option>
                      {branches.filter(b => b.id !== viewingReceipt.branch_id).map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Required for dispatching items
                    </small>
                  </div>
                  <div>
                    <label style={{ ...styles.label, marginBottom: '8px', display: 'block' }}>
                      Delivery Date
                    </label>
                    <input
                      type="date"
                      min={getMinDate(viewingReceipt.branch_id, postedPeriods)}
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      style={{
                        ...styles.input,
                        borderColor: isPeriodRestricted(viewingReceipt.branch_id, deliveryDate, postedPeriods) ? '#ef4444' : '#d1d5db'
                      }}
                    />
                  </div>
                </div>
                {/* <div>
                  <label style={{ ...styles.label, marginBottom: '8px', display: 'block' }}>
                    Notes
                  </label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={styles.input} placeholder="Remarks or special instructions..." />
                </div> */}
              </div>
            )}

            {/* Display Source Branch and Delivery Date in View Mode */}
            {!editMode && (viewingReceipt.source_branch_name || viewingReceipt.delivery_date) && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                  {viewingReceipt.source_branch_name && (
                    <div>
                      <span style={{ color: '#6b7280' }}>Source Branch:</span>
                      <span style={{ fontWeight: '600', marginLeft: '8px' }}>
                        {viewingReceipt.source_branch_name}
                      </span>
                    </div>
                  )}
                  {viewingReceipt.delivery_date && (
                    <div>
                      <span style={{ color: '#6b7280' }}>Delivery Date:</span>
                      <span style={{ fontWeight: '600', marginLeft: '8px' }}>
                        {new Date(viewingReceipt.delivery_date).toLocaleDateString('en-US', dateOptions)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Items Table */}
            <div style={{ marginBottom: '20px', overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: editMode ? '25%' : '35%' }}>Item</th>
                    <th style={{ ...styles.th, width: '15%', textAlign: 'center' }}>Quantity</th>
                    <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Unit</th>
                    <th style={{ ...styles.th, width: '20%', textAlign: 'center' }}>Unit Price</th>
                    <th style={{ ...styles.th, width: '20%', textAlign: 'center' }}>Line Total</th>
                    {editMode && <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {receiptItems.length === 0 ? (
                    <tr>
                      <td colSpan={editMode ? "4" : "3"} style={{ ...styles.td, textAlign: 'center', padding: '40px' }}>
                        <Package style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3, color: '#6b7280' }} />
                        <p style={{ margin: 0, color: '#6b7280' }}>
                          {editMode ? 'No items yet. Click "Add Item" to start.' : 'No items in this delivery receipt'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    receiptItems.map((item, idx) => {
                      const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
                      const insufficientStock = parseFloat(item.quantity) > parseFloat(item.source_current_stock || 0);
                      const isSourceSelected = Boolean(viewingReceipt?.source_branch_id || sourceBranchId);

                      return (
                        <tr
                          key={idx}
                          style={{
                            backgroundColor: insufficientStock ? '#fef2f2' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <td style={styles.td}>
                            {editMode ? (
                              <select
                                value={item.inventory_item_id || ''}
                                onChange={(e) => updateItem(idx, 'inventory_item_id', e.target.value)}
                                style={{ ...styles.input, padding: '6px 8px', fontSize: '13px' }}
                                disabled={!viewingReceipt.branch_id}
                              >
                                <option value="">Select Item...</option>

                                {branchInventoryItems.map(inv => (
                                  <option key={inv.id} value={inv.id}>
                                    {inv.item_code.includes('.') ? inv.item_code.split('.').pop() : inv.item_code}
                                    {' - '}
                                    {inv.item_name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div>
                                <span style={{ fontWeight: '500', display: 'block' }}>
                                  {item.item_code.includes('.') ? item.item_code.split('.').pop() : item.item_code}
                                </span>
                                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                  {item.item_name}
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            {editMode ? (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity || ''}
                                placeholder="0.00"
                                onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                style={{ ...styles.input, textAlign: 'center' }}
                              />
                            ) : (
                              <span style={{ fontWeight: '600' }}>{formatCurrency(item.quantity)}</span>
                            )}
                            <div style={{
                              fontSize: '11px',
                              marginTop: '4px',
                              fontWeight: isSourceSelected && insufficientStock ? 'bold' : 'normal',
                              color: isSourceSelected && insufficientStock ? '#ef4444' : '#6b7280' // Red if over, Gray if sufficient
                            }}>
                              {isSourceSelected ? (
                                item.source_current_stock !== null && item.source_current_stock !== undefined
                                  ? `Available: ${item.source_current_stock}`
                                  : 'Loading...'
                              ) : 'Available: -'}
                            </div>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <span style={{ fontSize: '13px' }}>{item.unit_of_measurement}</span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            {editMode ? (
                              <input type="number" placeholder="Unit Price" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} style={{ ...styles.input, padding: '6px 8px', fontSize: '13px' }} disabled />
                            ) : (
                              <span style={{ fontWeight: '600' }}>{formatCurrency(item.unit_price)}</span>
                            )}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <span style={{ fontWeight: '600', color: '#4f46e5' }}>
                              ₱{formatCurrency(lineTotal.toFixed(2))}
                            </span>
                          </td>
                          {editMode && (
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              <button
                                onClick={() => removeItem(idx)}
                                style={{ ...styles.iconButton, color: '#ef4444' }}
                                title="Remove Item"
                              >
                                <Trash2 style={{ width: '16px', height: '16px' }} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {!editMode && (
                <div style={{ marginTop: '12px' }}>
                  <span style={{ color: '#6b7280' }}>Delivery Fee:</span>
                  <span style={{ fontWeight: '600', marginLeft: '8px' }}>
                    {formatCurrency(viewingReceipt.delivery_fee)}
                  </span>
                </div>
              )}

              {editMode && (
                <div style={{ marginBottom: '12px', marginTop: '12px' }}>
                  <label style={styles.label}>Delivery Fee</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryFee || ''}
                    placeholder="0.00"
                    onChange={(e) => setDeliveryFee(e.target.value)}
                    style={{ ...styles.input, textAlign: 'right' }}
                  />
                </div>
              )}
            </div>

            {/* Summary */}
            {receiptItems.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Items:</span>
                    <span style={{ fontWeight: '600' }}>{receiptItems.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Quantity:</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(receiptItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0).toFixed(2))}
                    </span>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '16px',
                  paddingTop: '12px',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <span style={{ fontWeight: 'bold' }}>Total Amount:</span>
                  <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                    ₱{formatCurrency(
                      (
                        receiptItems.reduce((sum, item) =>
                          sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)),
                          0
                        ) + (parseFloat(deliveryFee) || 0)
                      ).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
            )}


            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
              {!editMode && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setEditMode(viewingReceipt.id)}
                    style={styles.button}
                    disabled={viewingReceipt.status === 'received'}
                  >
                    <Edit style={{ width: '16px', height: '16px' }} /> Edit
                  </button>
                  {viewingReceipt.status === 'pending' && viewingReceipt.source_branch_id && !readyToDispatch && (
                    <button
                      onClick={async () => {
                        await viewReceipt(viewingReceipt.id); // refresh batch + prices
                        setReadyToDispatch(true);
                      }}
                      style={{ ...styles.button, backgroundColor: '#f59e0b' }}
                    >
                      Proceed to Dispatch
                    </button>
                  )}

                  {readyToDispatch && (
                    <button
                      onClick={dispatchReceipt}
                      style={{ ...styles.button, backgroundColor: '#3b82f6' }}
                    >
                      Dispatch (Final)
                    </button>
                  )}
                  {viewingReceipt.status === 'pending' ? (
                    <button
                      onClick={() => deleteReceipt(viewingReceipt.id)}
                      style={{
                        ...styles.button,
                        backgroundColor: '#ef4444'
                      }}
                      disabled={viewingReceipt.status === 'received'}
                    >
                      <Trash2 style={{ width: '16px', height: '16px' }} /> Delete
                    </button>
                  ) : (
                    <button
                      onClick={() => exportToPDF(viewingReceipt.id)}
                      style={{
                        ...styles.button,
                        backgroundColor: '#10b981'
                      }}
                    >
                      <Download style={{ width: '16px', height: '16px' }} /> Export PDF
                    </button>
                  )}
                </div>
              )}

              {editMode && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                  <button
                    onClick={saveChanges}
                    style={{ ...styles.button, ...styles.buttonSuccess }}
                  >
                    <Check style={{ width: '16px', height: '16px' }} /> Save Changes
                  </button>
                  <button
                    onClick={() => {
                      if (viewingReceipt?.id) {
                        setEditMode(false);
                        viewReceipt(viewingReceipt.id);
                      } else {
                        closeModal();
                      }
                    }}
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addItem}
                    style={{
                      ...styles.button,
                      backgroundColor: '#6366f1',
                      marginLeft: 'auto'
                    }}
                  >
                    <Plus style={{ width: '16px', height: '16px' }} /> Add Item
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const ReceivingEntries = ({ token, postedPeriods }) => {
  const [entries, setEntries] = useState([]);
  const [pendingSources, setPendingSources] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sourceType, setSourceType] = useState('purchase_order');
  const [selectedSource, setSelectedSource] = useState('');
  const [receivingItems, setReceivingItems] = useState([]);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [entryItems, setEntryItems] = useState([]);
  const [receivedDate, setReceivedDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [deliveryFee, setDeliveryFee] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchEntries();
    fetchPendingSources();
    fetchInventoryItems();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/receiving_entries`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setEntries(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchPendingSources = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/receiving_entries/receivables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPendingSources(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      // const res = await fetchWithAuth(`${API_URL}/inventory`, { headers: { Authorization: `Bearer ${token}` } });
      const res = await fetchWithAuth(`${API_URL}/inventorydropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setInventoryItems(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const loadSourceItems = async (sourceId) => {
    if (!sourceId) return;

    try {
      const source = pendingSources.find(s => s.id == sourceId && s.source_type === sourceType);
      if (!source) {
        alert('Source not found');
        return;
      }

      setSelectedBranchId(source.branch_id);
      setDeliveryFee(source.delivery_fee);

      const res = await fetchWithAuth(`${API_URL}/receiving_entries/source-items/${sourceType}/${sourceId}`, { headers: { Authorization: `Bearer ${token}` } });
      const items = await res.json();

      const mappedItems = items.map(item => ({
        inventory_item_id: item.inventory_item_id,
        item_code: item.item_code,
        item_name: item.item_name,
        quantity: parseFloat(item.quantity) || 0,
        received_quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        unit_of_measurement: item.unit_of_measurement
      }));

      setReceivingItems(mappedItems);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading items');
    }
  };

  const updateReceivedQuantity = (idx, value) => {
    const updated = [...receivingItems];
    updated[idx].received_quantity = parseFloat(value) || 0;
    setReceivingItems(updated);
  };

  const updateUnitCost = (idx, value) => {
    const updated = [...receivingItems];
    updated[idx].unit_price = parseFloat(value) || 0;
    setReceivingItems(updated);
  };

  const updateInventorySelection = (idx, inventoryItemId) => {
    const updated = [...receivingItems];
    const selectedItem = inventoryItems.find(item => item.id == inventoryItemId);

    if (selectedItem) {
      updated[idx].inventory_item_id = inventoryItemId;
      updated[idx].item_code = selectedItem.item_code;
      updated[idx].item_name = selectedItem.item_name;
      updated[idx].unit_of_measurement = selectedItem.unit_of_measurement;
    }

    setReceivingItems(updated);
  };

  const addItem = () => {
    setReceivingItems([...receivingItems, {
      inventory_item_id: null,
      item_code: '',
      item_name: '',
      quantity: 0,
      received_quantity: 0,
      unit_price: 0,
      unit_of_measurement: ''
    }]);
  };

  const removeItem = (idx) => {
    setReceivingItems(receivingItems.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (isPeriodRestricted(selectedBranchId, receivedDate, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    if (!selectedSource) {
      alert('Please select a source');
      return;
    }

    if (receivingItems.length === 0) {
      alert('Please add at least one item');
      return;
    }

    const hasUnselectedItems = receivingItems.some(item => !item.inventory_item_id);
    if (hasUnselectedItems) {
      alert('Please select inventory items for all entries');
      return;
    }

    if (sourceType === 'purchase_order') {
      const hasInvalidCosts = receivingItems.some(item => !item.unit_price || item.unit_price <= 0);
      if (hasInvalidCosts) {
        alert('Please ensure all items have valid unit prices for purchase orders');
        return;
      }
    }

    const hasInvalidQuantities = receivingItems.some(item => !item.received_quantity || item.received_quantity <= 0);
    if (hasInvalidQuantities) {
      alert('Please ensure all items have valid received quantities');
      return;
    }

    const itemsToReceive = receivingItems.map(item => ({
      inventory_item_id: item.inventory_item_id,
      item_code: item.item_code,
      item_name: item.item_name,
      quantity: item.received_quantity,
      unit_price: item.unit_price || 0,
      unit_of_measurement: item.unit_of_measurement
    }));

    try {
      const res = await fetchWithAuth(`${API_URL}/receiving_entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_type: sourceType,
          source_id: selectedSource,
          branch_id: selectedBranchId,
          items: itemsToReceive,
          received_date: receivedDate,
          delivery_fee: deliveryFee,
          notes: notes
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to create receiving entry');
      }

      const data = await res.json();
      alert(`Receiving entry created successfully! ${data.items_received} items received and inventory updated.`);
      fetchEntries();
      fetchPendingSources();
      fetchInventoryItems();
      setShowCreateForm(false);
      resetForm();
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error creating receiving entry');
    }
  };

  const resetForm = () => {
    setSourceType('purchase_order');
    setSelectedSource('');
    setReceivingItems([]);
    setReceivedDate(new Date().toLocaleDateString('en-CA'));
    setDeliveryFee('');
    setNotes('');
    setSelectedBranchId(null);
  };

  const viewEntry = async (entryId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/receiving_entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingEntry(data.entry);
      setEntryItems(data.items);
      setEditNotes(data.entry.notes || '');
      setEditMode(false);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading entry details');
    }
  };

  const closeModal = () => {
    setViewingEntry(null);
    setEntryItems([]);
    setEditMode(false);
    setEditNotes('');
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this receiving entry? This will reverse all inventory changes.')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/receiving_entries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to delete entry');
      }

      alert('Receiving entry deleted successfully and inventory reversed');
      fetchEntries();
      fetchPendingSources();
      fetchInventoryItems();

      if (viewingEntry?.id === id) {
        closeModal();
      }
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error deleting entry');
    }
  };

  const handleUpdateEntry = async () => {
    if (!viewingEntry) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/receiving_entries/${viewingEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          notes: editNotes,
          status: viewingEntry.status
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to update entry');
      }

      alert('Receiving entry updated successfully');
      setEditMode(false);
      viewEntry(viewingEntry.id);
      fetchEntries();
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Error updating entry');
    }
  };

  const exportToPDF = async (entryId) => {
    try {
      // Fetch entry details
      const res = await fetchWithAuth(`${API_URL}/receiving_entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch receiving entry');
      }

      const data = await res.json();
      const entry = data.entry;
      const items = data.items || [];

      const doc = new jsPDF();

      const pesoFormatter = new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        currencyDisplay: 'code',
      });

      const qtyFormatter = new Intl.NumberFormat('en-PH', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Company Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('RECEIVING ENTRY', 105, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(entry.legal_company_name || '', 105, 28, { align: 'center' });
      doc.text(entry.branch_address || '', 105, 33, { align: 'center' });
      doc.text(entry.branch_phone || '', 105, 38, { align: 'center' });

      doc.setLineWidth(0.5);
      doc.line(20, 43, 190, 43);

      // RE Information - Left Side
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('RE Number:', 20, 55);
      doc.text('Source Branch:', 20, 62);
      doc.text('Source:', 20, 69);
      doc.text('Received By:', 20, 76);

      doc.setFont('helvetica', 'normal');
      doc.text(`RE-${entry.id}`, 55, 55);
      doc.text(entry.branch_name || 'Main Office', 55, 62);
      doc.text(entry.source_reference || `${entry.source_type}-${entry.source_id}`, 55, 69);
      doc.text(entry.received_by_name || 'N/A', 55, 76);

      // Date Information - Right Side
      doc.setFont('helvetica', 'bold');
      doc.text('Received Date:', 120, 55);
      doc.text('Status:', 120, 62);

      doc.setFont('helvetica', 'normal');
      const receivedDateStr = entry.received_date
        ? new Date(entry.received_date).toLocaleDateString('en-US', dateOptions)
        : new Date(entry.created_at).toLocaleDateString('en-US', dateOptions);
      doc.text(receivedDateStr, 160, 55);
      doc.text((entry.status || 'confirmed').toUpperCase(), 160, 62);

      // Items Table
      const isPO = entry.source_type === 'purchase_order';
      // const tableColumns = isPO
      //   ? ['Item Code', 'Item Name', 'Quantity', 'Unit', 'Unit Price', 'Total']
      //   : ['Item Code', 'Item Name', 'Quantity', 'Unit'];
      const tableColumns = ['Item Code', 'Item Name', 'Quantity', 'Unit', 'Unit Price', 'Total']

      const tableData = items.map((item, index) => {
        const row = [
          item.item_code.split('.').pop(),
          item.item_name || 'N/A',
          qtyFormatter.format(item.quantity || 0),
          item.unit_of_measurement || 'pc',
          pesoFormatter.format(item.unit_price),
          pesoFormatter.format((item.quantity || 0) * parseFloat(item.unit_price || 0))
        ];

        return row;
      });

      const columnStyles = {
        0: { cellWidth: 30, halign: 'left' },
        1: { cellWidth: 60, halign: 'left' },
        2: { cellWidth: 20, halign: 'right' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 35, halign: 'right' }
      };

      autoTable(doc, {
        startY: 85,
        head: [tableColumns],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [79, 70, 229],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        columnStyles: columnStyles,
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        }
      });

      // Summary
      const finalY = doc.lastAutoTable.finalY + 10;
      const rightAlignX = 190; // X-coordinate for the values to be right-aligned

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Total Items:', 130, finalY);
      doc.text('Total Quantity:', 130, finalY + 7);
      doc.text('Delivery Fee:', 130, finalY + 14);
      doc.text('TOTAL:', 130, finalY + 21);

      doc.setFont('helvetica', 'normal');
      const totalItems = items.length;
      const totalQuantity = items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
      const deliveryFee = parseFloat(entry.delivery_fee);
      const totalItemsAmount = items.reduce((sum, item) =>
        sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)), 0);
      const totalAmount = totalItemsAmount + parseFloat(entry.delivery_fee || 0);

      doc.text(totalItems.toString(), rightAlignX, finalY, { align: 'right' });
      doc.text(qtyFormatter.format(totalQuantity), rightAlignX, finalY + 7, { align: 'right' });
      doc.text(pesoFormatter.format(deliveryFee), rightAlignX, finalY + 14, { align: 'right' });
      doc.text(pesoFormatter.format(totalAmount), rightAlignX, finalY + 21, { align: 'right' });

      // Notes Section
      if (entry.notes) {
        const notesY = finalY + (isPO ? 25 : 18);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 20, notesY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const splitNotes = doc.splitTextToSize(entry.notes, 170);
        doc.text(splitNotes, 20, notesY + 5);
      }

      // Signature Section
      const sigY = finalY + (entry.notes ? 50 : 37);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Received By:', 140, sigY);
      // doc.line(140, sigY + 15, 190, sigY + 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(entry.received_by_name || 'Received by name', 140, sigY + 7);
      doc.text(`Date: ${receivedDateStr}`, 140, sigY + 14);

      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text('This is a computer-generated document.', 105, pageHeight - 15, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleString()}`, 105, pageHeight - 10, { align: 'center' });

      doc.save(`Receiving_Entry_RE-${entry.id}.pdf`);

    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Get inventory items filtered by selected branch
  const filteredInventoryItems = selectedBranchId
    ? inventoryItems.filter(item => item.branch_id === selectedBranchId)
    : inventoryItems;

  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = userData.role?.toLowerCase();
  const allowedBranchIds = (userData.allowed_branches || []).map(b => Number(b.id));
  // const filteredPendingSources = pendingSources.filter(s => s.source_type === sourceType);
  const filteredPendingSources = pendingSources.filter(s => {
    const matchesType = s.source_type === sourceType;
    if (!matchesType) return false;

    if (userRole === 'administrator') return true;

    return allowedBranchIds.includes(Number(s.branch_id));
  });

  const totalItemsCost = receivingItems.reduce((sum, item) =>
    sum + ((parseFloat(item.received_quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0
  );

  // const totalCost = totalItemsCost + parseFloat(pendingSources.find(s => s.id == selectedSource)?.delivery_fee || 0);

  // 1. Determine which fee to use
  const effectiveDeliveryFee = sourceType === 'purchase_order'
    ? (parseFloat(deliveryFee) || 0)
    : (parseFloat(pendingSources.find(s => s.id == selectedSource)?.delivery_fee) || 0);

  // 2. Calculate the total using that effective fee
  const totalCost = totalItemsCost + effectiveDeliveryFee;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          Receiving Entries
        </h1>
        {canCreate('receiving_entries') && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={styles.button}
          >
            <Plus style={{ width: '20px', height: '20px' }} /> Create Receiving Entry
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
            Create Receiving Entry
          </h2>

          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#eff6ff',
            borderRadius: '8px',
            border: '1px solid #bfdbfe'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
              📦 Receiving Process:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#3b82f6' }}>
              <li>Select a pending delivery receipt or purchase order</li>
              <li>Verify and adjust received quantities if needed</li>
              <li>Inventory batch/es will be created automatically</li>
              <li>Inventory quantities will be updated automatically</li>
              <li>Source document status will be marked as "received"</li>
            </ul>
          </div>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Source Type *</label>
              <select
                value={sourceType}
                onChange={(e) => {
                  setSourceType(e.target.value);
                  setSelectedSource('');
                  setReceivingItems([]);
                  setSelectedBranchId(null);
                }}
                style={styles.input}
              >
                <option value="purchase_order">From Purchase Order (Supplier)</option>
                <option value="delivery_receipt">From Delivery Receipt (Internal Transfer)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Select {sourceType === 'delivery_receipt' ? 'Delivery Receipt' : 'Purchase Order'} *
              </label>
              <select
                value={selectedSource}
                onChange={(e) => {
                  setSelectedSource(e.target.value);
                  loadSourceItems(e.target.value);
                }}
                style={styles.input}
              >
                <option value="">Choose...</option>
                {filteredPendingSources.map(source => (
                  <option key={source.id} value={source.id}>
                    {source.reference} - {source.branch_name}
                    {source.supplier_name && ` - ${source.supplier_name}`}
                    {source.total_amount && ` (₱${formatCurrency(source.total_amount)})`}
                    {` - ${source.item_count} items`}
                  </option>
                ))}
              </select>
              {filteredPendingSources.length === 0 && (
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  No pending {sourceType === 'delivery_receipt' ? 'delivery receipts' : 'purchase orders'} available
                </small>
              )}
            </div>
          </div>

          {/* {selectedSource && ( */}
          {selectedSource && selectedBranchId && (
            <>
              <div style={{
                marginBottom: '16px',
                padding: '10px 12px',
                backgroundColor: '#f0fdf4',
                borderRadius: '6px',
                border: '1px solid #86efac'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  <strong>Receiving to Branch:</strong> {
                    pendingSources.find(s => s.id == selectedSource)?.branch_name || 'Unknown'
                  }
                </p>
              </div>

              {/* {sourceType === 'purchase_order' && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #f59e0b'
                }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                    ⚠️ Important: Unit Price Entry
                  </p>
                  <p style={{ margin: '0', fontSize: '13px', color: '#92400e' }}>
                    Enter the <strong>actual final cost</strong> per unit (including all adjustments, discounts, and shipping costs).
                    This will be used for inventory batching and FIFO costing calculations.
                  </p>
                </div>
              )} */}

              <div style={{
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                  Items to Receive ({receivingItems.length})
                </h3>
                <button
                  onClick={addItem}
                  style={{
                    ...styles.button,
                    ...styles.buttonSuccess,
                    fontSize: '13px',
                    padding: '6px 12px'
                  }}
                >
                  <Plus style={{ width: '16px', height: '16px' }} /> Add Item
                </button>
              </div>

              <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: '28%' }}>Inventory Item *</th>
                      <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Expected</th>
                      <th style={{ ...styles.th, width: '12%', textAlign: 'center' }}>Received *</th>
                      <th style={{ ...styles.th, width: '8%', textAlign: 'center' }}>Unit</th>
                      <th style={{ ...styles.th, width: '12%', textAlign: 'center' }}>Unit Price *</th>
                      <th style={{ ...styles.th, width: '12%', textAlign: 'right' }}>Line Total</th>
                      {/* <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Current Stock</th> */}
                      <th style={{ ...styles.th, width: '8%', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivingItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={sourceType === 'purchase_order' ? "8" : "6"}
                          style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#6b7280' }}
                        >
                          No items loaded. Select a source above or click "Add Item".
                        </td>
                      </tr>
                    ) : (
                      receivingItems.map((item, idx) => {
                        const selectedInventoryItem = inventoryItems.find(inv => inv.id == item.inventory_item_id);
                        const lineTotal = (item.received_quantity || 0) * (item.unit_price || 0);

                        return (
                          <tr key={idx}>
                            <td style={styles.td}>
                              <select
                                value={item.inventory_item_id || ''}
                                onChange={(e) => updateInventorySelection(idx, e.target.value)}
                                style={{ ...styles.input, padding: '6px 8px', fontSize: '13px' }}
                              >
                                <option value="">Select Item...</option>
                                {filteredInventoryItems.map(invItem => {
                                  const ShortItemCode = invItem.item_code.includes('.') ? invItem.item_code.split('.').pop() : invItem.item_code;
                                  return (
                                    <option key={invItem.id} value={invItem.id}>
                                      {ShortItemCode} - {invItem.item_name}
                                    </option>
                                  )
                                })}
                              </select>
                              {item.inventory_item_id && selectedInventoryItem && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {selectedInventoryItem.item_description}
                                </div>
                              )}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                {item.quantity || 0}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.received_quantity || ''}
                                placeholder="0.00"
                                onChange={(e) => updateReceivedQuantity(idx, e.target.value)}
                                style={{ ...styles.input, textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              <span style={{ fontSize: '13px' }}>
                                {selectedInventoryItem?.unit_of_measurement || item.unit_of_measurement}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price || ''}
                                onChange={(e) => updateUnitCost(idx, e.target.value)}
                                style={{ ...styles.input, textAlign: 'right' }}
                                placeholder="0.00"
                                disabled />
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                              <span style={{ fontWeight: '600', color: '#4f46e5' }}>
                                ₱{formatCurrency(lineTotal)}
                              </span>
                            </td>
                            {/* <td style={{ ...styles.td, textAlign: 'center' }}>
                              {selectedInventoryItem ? (
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: selectedInventoryItem.quantity_on_hand < selectedInventoryItem.min_quantity
                                    ? '#ef4444'
                                    : '#059669'
                                }}>
                                  {selectedInventoryItem.quantity_on_hand}
                                </span>
                              ) : (
                                <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                              )}
                            </td> */}
                            <td style={{ ...styles.td, textAlign: 'center' }}>
                              <button
                                onClick={() => removeItem(idx)}
                                style={{ ...styles.iconButton, color: '#ef4444' }}
                                title="Remove Item"
                              >
                                <Trash2 style={{ width: '16px', height: '16px' }} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {sourceType === 'purchase_order' ? (
                <div style={{ marginBottom: '12px', marginTop: '12px' }}>
                  <label style={styles.label}>Delivery Fee</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={deliveryFee || ''}
                    placeholder="0.00"
                    style={{ ...styles.input, textAlign: 'right' }}
                    onChange={(e) => setDeliveryFee(e.target.value)}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '12px', marginTop: '12px' }}>
                  <label style={styles.label}>Delivery Fee</label>
                  <input
                    type="number"
                    value={effectiveDeliveryFee.toFixed(2)}
                    style={{ ...styles.input, textAlign: 'right', backgroundColor: '#f3f4f6' }}
                    disabled
                  />
                </div>
              )}

              {receivingItems.length > 0 && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                    <span>Total Items:</span>
                    <span style={{ fontWeight: '600' }}>{receivingItems.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                    <span>Total Quantity:</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(receivingItems.reduce((sum, item) => sum + (parseFloat(item.received_quantity) || 0), 0).toFixed(2))}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '16px',
                    paddingTop: '8px',
                    borderTop: '2px solid #e5e7eb'
                  }}>
                    <span style={{ fontWeight: 'bold' }}>Total Amount:</span>
                    <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                      ₱{formatCurrency(totalCost)}
                    </span>
                  </div>
                </div>
              )}

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Received Date *</label>
                  <input
                    type="date"
                    min={getMinDate(selectedBranchId, postedPeriods)}
                    value={receivedDate}
                    onChange={(e) => setReceivedDate(e.target.value)}
                    style={{
                      ...styles.input,
                      borderColor: isPeriodRestricted(selectedBranchId, receivedDate, postedPeriods) ? '#ef4444' : '#d1d5db'
                    }}
                    max={new Date().toLocaleDateString('en-CA')}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }}
                    placeholder="Remarks, discrepancies, or special instructions..."
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleSubmit} style={styles.button}>
                  <Check style={{ width: '16px', height: '16px' }} />
                  Confirm Receipt & Update Inventory
                </button>
                <button
                  onClick={() => { setShowCreateForm(false); resetForm(); }}
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Entries Table */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          All Receiving Entries
        </h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>RE Number</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Source</th>
              <th style={styles.th}>Received By</th>
              <th style={styles.th}>Received Date</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ ...styles.td, textAlign: 'center', color: '#6b7280' }}>
                  No receiving entries found
                </td>
              </tr>
            ) : (
              entries.map(entry => (
                <tr key={entry.id}>
                  <td style={{ ...styles.td, fontWeight: '500' }}>RE-{entry.id}</td>
                  <td style={styles.td}>{entry.branch_name || 'Main Office'}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...styles.badgeBlue }}>
                      {entry.source_reference}
                    </span>
                  </td>
                  <td style={styles.td}>{entry.received_by_name}</td>
                  <td style={styles.td}>
                    {entry.received_date
                      ? new Date(entry.received_date).toLocaleDateString('en-US', dateOptions)
                      : new Date(entry.created_at).toLocaleDateString('en-US', dateOptions)}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(entry.status === 'confirmed' ? styles.badgeGreen : styles.badgeYellow)
                    }}>
                      {entry.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => viewEntry(entry.id)}
                        style={{ ...styles.iconButton, color: '#3b82f6' }}
                        title="View Details"
                      >
                        <Eye style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => exportToPDF(entry.id)}
                        style={{ ...styles.iconButton, color: '#10b981' }}
                        title="Export PDF"
                      >
                        <Download style={{ width: '16px', height: '16px' }} />
                      </button>
                      {canDelete('receiving_entries') && (
                        <button
                          onClick={() => deleteEntry(entry.id)}
                          style={{ ...styles.iconButton, color: '#ef4444' }}
                          title="Delete"
                        >
                          <Trash2 style={{ width: '16px', height: '16px' }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      {viewingEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            ...styles.card,
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: 0
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                  Receiving Entry #{viewingEntry.id}
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  {viewingEntry.branch_name} • {viewingEntry.source_reference} •
                  {new Date(viewingEntry.received_date || viewingEntry.created_at).toLocaleDateString('en-US', dateOptions)}
                </p>
              </div>
              <button onClick={closeModal} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {/* Entry Info */}
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#d1fae5',
              borderRadius: '8px',
              border: '1px solid #6ee7b7'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                fontSize: '14px'
              }}>
                <div>
                  <span style={{ fontWeight: '600', color: '#065f46' }}>Source Type:</span>
                  <span style={{ color: '#065f46', marginLeft: '8px' }}>
                    {viewingEntry.source_type === 'delivery_receipt' ? 'Delivery Receipt' : 'Purchase Order'}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#065f46' }}>Source ID:</span>
                  <span style={{ color: '#065f46', marginLeft: '8px' }}>
                    {viewingEntry.source_reference}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#065f46' }}>Received By:</span>
                  <span style={{ color: '#065f46', marginLeft: '8px' }}>
                    {viewingEntry.received_by_name}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#065f46' }}>Status:</span>
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#065f46'
                  }}>
                    {viewingEntry.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {!editMode && viewingEntry.notes && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fcd34d'
              }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: '600', fontSize: '14px', color: '#92400e' }}>
                  Notes:
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                  {viewingEntry.notes}
                </p>
              </div>
            )}

            {/* Edit Notes Form */}
            {editMode && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #7dd3fc'
              }}>
                <label style={{ ...styles.label, marginBottom: '8px', display: 'block' }}>
                  Edit Notes:
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  style={{
                    ...styles.input,
                    minHeight: '80px',
                    resize: 'vertical',
                    width: '100%'
                  }}
                  placeholder="Add notes or remarks about this receiving entry..."
                />
              </div>
            )}

            {/* Items Table */}
            <div style={{ marginBottom: '20px', overflowX: 'auto' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                Received Items
              </h3>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item Code</th>
                    <th style={styles.th}>Item Name</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Quantity</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Unit</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Unit Price</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {entryItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan={viewingEntry.source_type === 'purchase_order' ? "6" : "4"}
                        style={{ ...styles.td, textAlign: 'center', color: '#6b7280', padding: '40px' }}
                      >
                        No items found
                      </td>
                    </tr>
                  ) : (
                    entryItems.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ ...styles.td, fontWeight: '500' }}>{item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code}</td>
                        <td style={styles.td}>
                          <div>
                            <span style={{ display: 'block' }}>{item.item_name || item.item_description}</span>
                            {item.item_type && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                Category: {item.category_name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ ...styles.td, fontWeight: '600', textAlign: 'center' }}>
                          {formatCurrency(item.quantity)}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {item.unit_of_measurement}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          ₱{formatCurrency(item.unit_price || 0)}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#4f46e5' }}>
                          ₱{formatCurrency((item.quantity * (item.unit_price || 0)).toFixed(2))}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {!editMode && (
                <div style={{ marginTop: '12px' }}>
                  <span style={{ color: '#6b7280' }}>Delivery Fee:</span>
                  <span style={{ fontWeight: '600', marginLeft: '8px' }}>
                    {formatCurrency(viewingEntry.delivery_fee)}
                  </span>
                </div>
              )}
            </div>

            {/* Summary */}
            {entryItems.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  fontSize: '14px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Items:</span>
                    <span style={{ fontWeight: '600' }}>{entryItems.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Quantity:</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(entryItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0).toFixed(2))}
                    </span>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '16px',
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <span style={{ fontWeight: 'bold' }}>Total Amount:</span>
                  <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                    ₱{formatCurrency(
                      (
                        entryItems.reduce((sum, item) =>
                          sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0)),
                          0
                        ) + parseFloat(viewingEntry.delivery_fee || 0)
                      ).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
              {!editMode ? (
                <>
                  <button
                    onClick={() => setEditMode(true)}
                    style={styles.button}
                  >
                    <Edit style={{ width: '16px', height: '16px' }} />
                    Edit Notes
                  </button>
                  <button
                    onClick={() => exportToPDF(viewingEntry.id)}
                    style={{
                      ...styles.button,
                      backgroundColor: '#10b981'
                    }}
                  >
                    <Download style={{ width: '16px', height: '16px' }} />
                    Export PDF
                  </button>
                  {canDelete('receiving_entries') && (
                    <button
                      onClick={() => deleteEntry(viewingEntry.id)}
                      style={{
                        ...styles.button,
                        backgroundColor: '#ef4444'
                      }}
                    >
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                      Delete Entry
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    style={{ ...styles.button, ...styles.buttonSecondary, marginLeft: 'auto' }}
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleUpdateEntry}
                    style={{ ...styles.button, ...styles.buttonSuccess }}
                  >
                    <Check style={{ width: '16px', height: '16px' }} />
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setEditNotes(viewingEntry.notes || '');
                    }}
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const ItemSalesSummary = ({ token }) => {
  const [sales, setSales] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({
    branch_id: '',
    sales_date: getPHTDateString()
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [viewingSale, setViewingSale] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchSales();
    fetchBranches();
  }, []);

  const fetchSales = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/item_sales_summary`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSales(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;
        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
      if (authorizedBranches.length === 1) {
        setImportForm(prev => ({ ...prev, branch_id: authorizedBranches[0].id }));
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split('\n');
        const data = [];

        // Skip header (row 0)
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;

          // Using simple split based on sample provided
          // Handle potential carriage return
          const line = lines[i].replace('\r', '');
          const values = line.split(',').map(v => v.trim());

          if (values.length >= 13) {
            data.push({
              item_name: values[0],
              SKU: values[1],
              category: values[2],
              items_sold: parseFloat(values[3]) || 0,
              gross_sales: parseFloat(values[4]) || 0,
              items_refunded: parseFloat(values[5]) || 0,
              refunds: parseFloat(values[6]) || 0,
              discounts: parseFloat(values[7]) || 0,
              net_sales: parseFloat(values[8]) || 0,
              cost_of_goods: parseFloat(values[9]) || 0,
              gross_profit: parseFloat(values[10]) || 0,
              margin: values[11].replace('%', ''),
              taxes: parseFloat(values[12]) || 0
            });
          }
        }
        setCsvData(data);
      };
      reader.readAsText(file);
    }
  };

  const handleSubmit = async () => {
    if (!importForm.branch_id || !importForm.sales_date || !csvData.length) {
      alert('Please fill all fields and upload a valid CSV');
      return;
    }

    setImporting(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/item_sales_summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...importForm,
          items: csvData
        })
      });

      const data = await res.json();
      if (res.ok) {
        alert('Daily sales imported successfully');
        fetchSales();
        setShowImport(false);
        setImportForm(prev => ({ ...prev, branch_id: branches.length === 1 ? prev.branch_id : '' }));
        setSelectedFile(null);
        setCsvData([]);
      } else {
        alert(data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Import error: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleFetchDetails = async (id) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/item_sales_summary/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingSale(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading details');
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/item_sales_summary/${id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Approved successfully');
        setShowApprovalModal(null);
        fetchSales();
      } else {
        const data = await res.json();
        alert(data.error || 'Approval failed');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error approving');
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/item_sales_summary/${id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Rejected successfully');
        setShowApprovalModal(null);
        fetchSales();
      } else {
        const data = await res.json();
        alert(data.error || 'Rejection failed');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error rejecting');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/item_sales_summary/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Deleted successfully');
        fetchSales();
      } else {
        const data = await res.json();
        alert(data.error || 'Deletion failed');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#fef3c7', color: '#92400e', border: '#f59e0b', text: 'Pending' },
      approved: { bg: '#d1fae5', color: '#065f46', border: '#10b981', text: 'Approved' },
      rejected: { bg: '#fee2e2', color: '#991b1b', border: '#ef4444', text: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span style={{
        ...styles.badge,
        backgroundColor: badge.bg,
        color: badge.color,
        border: `1px solid ${badge.border}`
      }}>
        {badge.text}
      </span>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Item Sales Summary</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canCreate('item_sales_summary') && (
            <button onClick={() => setShowImport(!showImport)} style={styles.button}>
              <Upload style={{ width: '20px', height: '20px' }} /> Import CSV
            </button>
          )}
        </div>
      </div>

      {showImport && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>Import Daily Sales</h2>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Select Branch *</label>
              <select
                value={importForm.branch_id}
                onChange={(e) => setImportForm({ ...importForm, branch_id: e.target.value })}
                style={styles.input}
              >
                <option value="">Choose Branch</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Sales Date *</label>
              <input
                type="date"
                value={importForm.sales_date}
                onChange={(e) => setImportForm({ ...importForm, sales_date: e.target.value })}
                style={styles.input}
              />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Select CSV File *</label>
            <input type="file" accept=".csv" onChange={handleFileChange} style={styles.input} />
          </div>

          {csvData.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#166534' }}>✓ {csvData.length} items parsed ready for import</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#166534' }}>Total Net Sales: {formatCurrency(csvData.reduce((sum, item) => sum + item.net_sales, 0))}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSubmit}
              disabled={importing || !csvData.length}
              style={{ ...styles.button, opacity: (importing || !csvData.length) ? 0.6 : 1 }}
            >
              {importing ? 'Importing...' : 'Submit Sales Entry'}
            </button>
            <button onClick={() => setShowImport(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Sales Date</th>
            <th style={styles.th}>Branch</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Submitted By</th>
            <th style={styles.th}>Approved By</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sales.map(sale => (
            <tr key={sale.id}>
              <td style={{ ...styles.td, fontFamily: 'monospace' }}>DS-{sale.id}</td>
              <td style={styles.td}>{new Date(sale.sales_date).toLocaleDateString('en-US', dateOptions)}</td>
              <td style={styles.td}>{sale.branch_name}</td>
              <td style={styles.td}>{getStatusBadge(sale.status)}</td>
              <td style={styles.td}>{sale.submitted_by}</td>
              <td style={styles.td}>{sale.approved_by || '-'}</td>
              <td style={styles.td}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button onClick={() => handleFetchDetails(sale.id)} style={{ ...styles.iconButton, color: '#4f46e5' }} title="View">
                    <Eye style={{ width: '16px', height: '16px' }} />
                  </button>
                  {sale.status === 'pending' && canApprove('item_sales_summary') && (
                    <button
                      onClick={() => setShowApprovalModal(sale)}
                      style={{
                        ...styles.iconButton,
                        color: sale.unmapped_items ? '#ef4444' : '#10b981',
                        opacity: sale.unmapped_items ? 0.7 : 1
                      }}
                      title={sale.unmapped_items ? `Unmapped Items: ${sale.unmapped_items}` : "Approve/Reject"}
                    >
                      {sale.unmapped_items ? <ShieldAlert style={{ width: '16px', height: '16px' }} /> : <Check style={{ width: '16px', height: '16px' }} />}
                    </button>
                  )}
                  {sale.status === 'pending' && canDelete('item_sales_summary') && (
                    <button onClick={() => handleDelete(sale.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Delete">
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>


      <Modal
        isOpen={!!viewingSale}
        onClose={() => setViewingSale(null)}
        title={`Sales Entry DS-${viewingSale?.sales?.id || ''}`}
        maxWidth="1000px"
      >
        {viewingSale && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div><strong>Branch:</strong> {viewingSale.sales.branch_name}</div>
              <div><strong>Date:</strong> {new Date(viewingSale.sales.sales_date).toLocaleDateString('en-US', dateOptions)}</div>
              <div><strong>Status:</strong> {getStatusBadge(viewingSale.sales.status)}</div>
              <div><strong>Submitted By:</strong> {viewingSale.sales.submitted_by}</div>
            </div>

            {/* Financial Summary */}
            {(() => {
              const totals = viewingSale.items.reduce((acc, item) => ({
                grossSales: acc.grossSales + (parseFloat(item.gross_sales) || 0),
                refunds: acc.refunds + (parseFloat(item.refunds) || 0),
                discounts: acc.discounts + (parseFloat(item.discounts) || 0),
                netSales: acc.netSales + (parseFloat(item.net_sales) || 0),
                grossProfit: acc.grossProfit + (parseFloat(item.gross_profit) || 0),
              }), { grossSales: 0, refunds: 0, discounts: 0, netSales: 0, grossProfit: 0 });

              const summaryCards = [
                { label: 'Gross Sales', value: totals.grossSales, color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
                { label: 'Refunds', value: totals.refunds, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
                { label: 'Discounts', value: totals.discounts, color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
                { label: 'Net Sales', value: totals.netSales, color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
                { label: 'Gross Profit', value: totals.grossProfit, color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
              ];

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '24px' }}>
                  {summaryCards.map(card => (
                    <div key={card.label} style={{
                      padding: '16px',
                      backgroundColor: card.bg,
                      border: `1px solid ${card.border}`,
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {card.label}
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: card.color }}>
                        {formatCurrency(card.value)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
              <table style={{ ...styles.table, fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item</th>
                    <th style={styles.th}>SKU</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Sold</th>
                    <th style={styles.th}>Gross Sales</th>
                    <th style={styles.th}>Refunded</th>
                    <th style={styles.th}>Refunded Amount</th>
                    <th style={styles.th}>Discount</th>
                    <th style={styles.th}>Net Sales</th>
                    <th style={styles.th}>C.O.G.S.</th>
                    <th style={styles.th}>Gross Profit</th>
                    <th style={styles.th}>Margin</th>
                    <th style={styles.th}>Taxes</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingSale.items.map(item => (
                    <tr key={item.id}>
                      <td style={styles.td}>{item.item_name}</td>
                      <td style={styles.td}>{item.SKU}</td>
                      <td style={styles.td}>{item.category}</td>
                      <td style={styles.td}>{item.items_sold}</td>
                      <td style={styles.td}>{formatCurrency(item.gross_sales)}</td>
                      <td style={styles.td}>{formatCurrency(item.items_refunded)}</td>
                      <td style={styles.td}>{formatCurrency(item.refunds)}</td>
                      <td style={styles.td}>{formatCurrency(item.discounts)}</td>
                      <td style={styles.td}>{formatCurrency(item.net_sales)}</td>
                      <td style={styles.td}>{formatCurrency(item.cost_of_goods)}</td>
                      <td style={styles.td}>{formatCurrency(item.gross_profit)}</td>
                      <td style={styles.td}>{formatCurrency(item.margin)}%</td>
                      <td style={styles.td}>{formatCurrency(item.taxes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={!!showApprovalModal}
        onClose={() => setShowApprovalModal(null)}
        title="Approve / Reject Sales Entry"
        maxWidth="500px"
      >
        {showApprovalModal && (
          <>
            <p>Are you sure you want to approve or reject this sales entry for {showApprovalModal.branch_name}?</p>

            {showApprovalModal.unmapped_items && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '14px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: 'bold' }}>
                  <ShieldAlert style={{ width: '18px', height: '18px' }} />
                  Cannot Approve: Unmapped Items Found
                </div>
                <div>Please map the following SKUs before approving:</div>
                <div style={{ marginTop: '4px', fontFamily: 'monospace' }}>
                  {showApprovalModal.unmapped_items}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
              <button
                onClick={() => handleApprove(showApprovalModal.id)}
                disabled={!!showApprovalModal.unmapped_items}
                style={{
                  ...styles.button,
                  ...styles.buttonSuccess,
                  flex: 1,
                  opacity: showApprovalModal.unmapped_items ? 0.5 : 1,
                  cursor: showApprovalModal.unmapped_items ? 'not-allowed' : 'pointer'
                }}
              >
                Approve
              </button>
              <button onClick={() => handleReject(showApprovalModal.id)} style={{ ...styles.button, ...styles.buttonDanger, flex: 1 }}>
                Reject
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};


const ProductionFormulas = ({ token }) => {
  const [formulas, setFormulas] = useState([]);
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingFormula, setViewingFormula] = useState(null);
  const [form, setForm] = useState({
    finished_good_id: '',
    formula_name: '',
    yield_quantity: '',
    items: [{
      raw_material_id: '',
      item_code: '',
      item_name: '',
      quantity_needed: 0,
      unit_of_measurement: '',
      is_main_ingredient: false
    }]
  });

  useEffect(() => {
    fetchFormulas();
    fetchFinishedGoods();
    fetchRawMaterials();
  }, []);

  const fetchFormulas = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-formulas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFormulas(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchFinishedGoods = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventory?branch_id=2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFinishedGoods(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/inventory?branch_id=2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRawMaterials(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const validateMainIngredient = () => {
    const mainCount = form.items.filter(item => item.is_main_ingredient).length;
    if (mainCount === 0) {
      alert('You must designate one item as the main ingredient');
      return false;
    }
    if (mainCount > 1) {
      alert('Only one item can be designated as the main ingredient');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!form.finished_good_id || !form.yield_quantity) {
      alert('Please select finished good and specify yield quantity');
      return;
    }

    const hasInvalidItems = form.items.some(item =>
      !item.raw_material_id || item.quantity_needed <= 0
    );

    if (hasInvalidItems) {
      alert('Please ensure all items have valid selection and quantity greater than 0');
      return;
    }

    if (!validateMainIngredient()) {
      return;
    }

    try {
      const url = editingId
        ? `${API_URL}/production-formulas/${editingId}`
        : `${API_URL}/production-formulas`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchFormulas();
        resetForm();
        alert(editingId ? 'Formula updated successfully' : 'Formula created successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error saving formula');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error saving formula');
    }
  };

  const handleEdit = async (formula) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-formulas/${formula.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      setForm({
        finished_good_id: data.formula.finished_good_id,
        formula_name: data.formula.formula_name || '',
        yield_quantity: data.formula.yield_quantity || 1,
        items: data.items.map(item => ({
          raw_material_id: item.raw_material_id,
          item_code: item.item_code,
          item_name: item.item_name,
          quantity_needed: item.quantity_needed,
          unit_of_measurement: item.unit_of_measurement,
          is_main_ingredient: item.is_main_ingredient || false
        }))
      });
      setEditingId(formula.id);
      setShowForm(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading formula');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this formula?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/production-formulas/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchFormulas();
        alert('Formula deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting formula');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting formula');
    }
  };

  const viewFormula = async (formulaId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-formulas/${formulaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingFormula(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading formula details');
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [
        ...form.items,
        {
          raw_material_id: '',
          item_code: '',
          item_name: '',
          quantity_needed: 0,
          unit_of_measurement: '',
          is_main_ingredient: false
        }
      ]
    });
  };

  const removeItem = (index) => {
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...form.items];

    if (field === 'raw_material_id') {
      const selectedItem = rawMaterials.find(item => item.id == value);
      if (selectedItem) {
        updatedItems[index].raw_material_id = value;
        updatedItems[index].item_code = selectedItem.item_code;
        updatedItems[index].item_name = selectedItem.item_name;
        updatedItems[index].unit_of_measurement = selectedItem.unit_of_measurement;
      }
    } else if (field === 'is_main_ingredient') {
      // Uncheck all other items when checking this one
      updatedItems.forEach((item, idx) => {
        item.is_main_ingredient = idx === index ? value : false;
      });
    } else {
      updatedItems[index][field] = value;
    }

    setForm({ ...form, items: updatedItems });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({
      finished_good_id: '',
      formula_name: '',
      yield_quantity: 1,
      items: [{
        raw_material_id: '',
        item_code: '',
        item_name: '',
        quantity_needed: 0,
        unit_of_measurement: '',
        is_main_ingredient: false
      }]
    });
  };

  const styles = {
    container: { padding: '24px', backgroundColor: '#f3f4f6', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
    button: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
    buttonSecondary: { backgroundColor: '#6b7280' },
    buttonSuccess: { backgroundColor: '#10b981' },
    buttonDanger: { backgroundColor: '#ef4444' },
    card: { backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    table: { width: '100%', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgeGreen: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #10b981' },
    badgeBlue: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' },
    badgeYellow: { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' },
    alert: { padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }
  };

  const mainIngredientCount = form.items.filter(item => item.is_main_ingredient).length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Production Formulas</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} />
          Add Formula
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ ...styles.card, maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
                {editingId ? 'Edit Formula' : 'New Formula'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={styles.iconButton}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {mainIngredientCount === 0 && (
              <div style={styles.alert}>
                ⚠️ You must designate one item as the main ingredient. Only the main ingredient will be used for cost calculations.
              </div>
            )}

            {mainIngredientCount > 1 && (
              <div style={{ ...styles.alert, backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#ef4444' }}>
                ❌ Only ONE item can be designated as the main ingredient. Please uncheck the others.
              </div>
            )}

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Finished Good *</label>
                <select
                  value={form.finished_good_id}
                  onChange={(e) => setForm({ ...form, finished_good_id: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select Finished Good</option>
                  {finishedGoods.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code} - {item.item_name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Formula Name</label>
                <input
                  type="text"
                  value={form.formula_name}
                  onChange={(e) => setForm({ ...form, formula_name: e.target.value })}
                  style={styles.input}
                  placeholder="Optional formula name"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Yield Quantity *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.yield_quantity || ''}
                  placeholder="0.00"
                  onChange={(e) => setForm({ ...form, yield_quantity: parseFloat(e.target.value) || 1 })}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold' }}>Ingredients</h3>
                <button
                  onClick={addItem}
                  style={styles.button}
                >
                  <Plus style={{ width: '16px', height: '16px' }} /> Add Ingredient
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Raw Material *</th>
                      <th style={styles.th}>Quantity *</th>
                      <th style={styles.th}>Unit</th>
                      <th style={styles.th}>Main?</th>
                      <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>
                          <select
                            value={item.raw_material_id}
                            onChange={(e) => updateItem(idx, 'raw_material_id', e.target.value)}
                            style={styles.input}
                          >
                            <option value="">Select Raw Material...</option>
                            {rawMaterials.map(rm => (
                              <option key={rm.id} value={rm.id}>
                                {rm.item_code?.includes('.') ? rm.item_code.split('.').pop() : rm.item_code} - {rm.item_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={styles.td}>
                          <input
                            type="number"
                            min="0"
                            step="0.0001"
                            value={item.quantity_needed || ''}
                            placeholder="0.0000"
                            onChange={(e) => updateItem(idx, 'quantity_needed', parseFloat(e.target.value) || 0)}
                            style={styles.input}
                          />
                        </td>
                        <td style={styles.td}>
                          {item.unit_of_measurement}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={item.is_main_ingredient}
                            onChange={(e) => updateItem(idx, 'is_main_ingredient', e.target.checked)}
                            style={{ transform: 'scale(1.2)' }}
                          />
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            onClick={() => removeItem(idx)}
                            style={{ ...styles.iconButton, color: '#ef4444' }}
                            disabled={form.items.length === 1}
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={handleSubmit} style={styles.button}>
                {editingId ? 'Update' : 'Create'} Formula
              </button>
              <button onClick={resetForm} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Finished Good</th>
                <th style={styles.th}>Formula Name</th>
                <th style={styles.th}>Yield Qty</th>
                <th style={styles.th}>Items</th>
                <th style={styles.th}>Main Ingredient</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {formulas.map(formula => (
                <tr key={formula.id}>
                  <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>
                    PF-{formula.id}
                  </td>
                  <td style={styles.td}>
                    {formula.finished_good_code?.includes('.') ? formula.finished_good_code.split('.').pop() : formula.finished_good_code} - {formula.finished_good_name}
                  </td>
                  <td style={styles.td}>{formula.formula_name || '—'}</td>
                  <td style={styles.td}>{formula.yield_quantity}</td>
                  <td style={styles.td}>
                    <span style={styles.badgeBlue}>{formula.raw_material_count || 0} items</span>
                  </td>
                  <td style={styles.td}>
                    {formula.main_ingredient_name ? (
                      <span style={styles.badgeGreen}>
                        {formula.main_ingredient_code?.includes('.') ? formula.main_ingredient_code.split('.').pop() : formula.main_ingredient_code} - {formula.finished_good_name}
                      </span>
                    ) : (
                      <span style={{ ...styles.badge, ...styles.badgeYellow }}>
                        Not set
                      </span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => viewFormula(formula.id)}
                      style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}
                      title="View Details"
                    >
                      <Eye style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button
                      onClick={() => handleEdit(formula)}
                      style={{ ...styles.iconButton, color: '#f59e0b', marginRight: '8px' }}
                      title="Edit"
                    >
                      <Edit style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(formula.id)}
                      style={{ ...styles.iconButton, color: '#ef4444' }}
                      title="Delete"
                    >
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {formulas.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <Search style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No production formulas found</p>
          </div>
        )}
      </div>

      {/* View Formula Modal */}
      {viewingFormula && (
        <div style={styles.modal} onClick={() => setViewingFormula(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Formula PF-{viewingFormula.formula.id}
              </h2>
              <button onClick={() => setViewingFormula(null)} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px', marginBottom: '24px' }}>
              <strong>Finished Good:</strong>
              <span>{viewingFormula.formula.finished_good_code} - {viewingFormula.formula.finished_good_name}</span>

              <strong>Formula Name:</strong>
              <span>{viewingFormula.formula.formula_name || 'N/A'}</span>

              <strong>Yield Quantity:</strong>
              <span>{viewingFormula.formula.yield_quantity}</span>

              <strong>Created:</strong>
              <span>{formatDateTime(viewingFormula.formula.created_at)}</span>
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>Raw Materials</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item Code</th>
                    <th style={styles.th}>Item Name</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Unit</th>
                    <th style={styles.th}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingFormula.items.map((item, idx) => (
                    <tr key={idx} style={{ backgroundColor: item.is_main_ingredient ? '#f0fdf4' : 'transparent' }}>
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{item.item_code}</td>
                      <td style={styles.td}>{item.item_name}</td>
                      <td style={{ ...styles.td, fontWeight: '600' }}>{item.quantity_needed}</td>
                      <td style={styles.td}>{item.unit_of_measurement}</td>
                      <td style={styles.td}>
                        {item.is_main_ingredient ? (
                          <span style={styles.badgeGreen}>
                            <CheckCircle style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                            Main Ingredient
                          </span>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            Supporting
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '6px', fontSize: '13px', color: '#065f46' }}>
              💡 <strong>Note:</strong> Production cost calculations will be based only on the main ingredient using FIFO method.
            </div>

            <button
              onClick={() => setViewingFormula(null)}
              style={{ ...styles.button, marginTop: '16px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


const ProductionEntry = ({ token, postedPeriods }) => {
  const [entries, setEntries] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [costCalculation, setCostCalculation] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [calculatingCost, setCalculatingCost] = useState(false);
  const [form, setForm] = useState({
    formula_id: '',
    production_date: new Date().toLocaleDateString('en-CA'),
    quantity_produced: 1,
    batch_number: '',
    notes: '',
    finished_good_unit: ''
  });

  useEffect(() => {
    fetchEntries();
    fetchFormulas();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEntries(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchFormulas = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-formulas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFormulas(data.filter(f => f.is_active));
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const calculateCost = async (entryId, multiplier = 1) => {
    setCalculatingCost(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/production-entries/${entryId}/calculate-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ multiplier })
      });

      if (res.ok) {
        const data = await res.json();
        setCostCalculation(data);
        setShowCostModal(true);
      } else {
        const data = await res.json();
        alert(data.error || 'Error calculating cost');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error calculating cost');
    } finally {
      setCalculatingCost(false);
    }
  };

  const handleSubmit = async () => {
    if (isPeriodRestricted(2, form.production_date, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    if (!form.formula_id || !form.production_date || !form.quantity_produced) {
      alert('Please fill in all required fields');
      return;
    }

    if (form.quantity_produced <= 0) {
      alert('Quantity produced must be greater than 0');
      return;
    }

    try {
      const url = editingId
        ? `${API_URL}/production-entries/${editingId}`
        : `${API_URL}/production-entries`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchEntries();
        resetForm();
        alert(editingId ? 'Production entry updated successfully' : 'Production entry created successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error saving production entry');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error saving production entry');
    }
  };

  const handleEdit = async (entry) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-entries/${entry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      setForm({
        formula_id: data.entry.formula_id,
        production_date: data.entry.production_date,
        quantity_produced: data.entry.quantity_produced,
        batch_number: data.entry.batch_number || '',
        notes: data.entry.notes || '',
        finished_good_unit: data.entry.finished_good_unit || ''
      });
      setEditingId(entry.id);
      setShowForm(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading production entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this production entry?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/production-entries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchEntries();
        alert('Production entry deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting production entry');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting production entry');
    }
  };

  const viewEntry = async (entryId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/production-entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingEntry(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading entry details');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({
      formula_id: '',
      production_date: new Date().toLocaleDateString('en-CA'),
      quantity_produced: 1,
      batch_number: '',
      notes: '',
      finished_good_unit: ''
    });
  };

  const styles = {
    container: { padding: '24px', backgroundColor: '#f3f4f6', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
    button: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
    buttonSecondary: { backgroundColor: '#6b7280' },
    buttonSuccess: { backgroundColor: '#10b981' },
    buttonDanger: { backgroundColor: '#ef4444' },
    buttonWarning: { backgroundColor: '#f59e0b' },
    card: { backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    table: { width: '100%', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgeGreen: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #10b981' },
    badgeBlue: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' },
    badgeYellow: { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' },
    alert: { padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
    infoBox: { padding: '16px', borderRadius: '6px', marginTop: '16px', backgroundColor: '#f0fdf4', border: '1px solid #10b981' }
  };

  const selectedFormula = formulas.find(f => f.id == form.formula_id);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Production Entries</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} />
          Add Production Entry
        </button>
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
            {editingId ? 'Edit Production Entry' : 'New Production Entry'}
          </h2>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Production Formula *</label>
              <select
                value={form.formula_id}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  // 1. Find the selected formula object from your list
                  const selectedFormula = formulas.find(f => f.id.toString() === selectedId);

                  // 2. Update the form state with BOTH the ID and the yield_quantity
                  setForm({
                    ...form,
                    formula_id: selectedId,
                    // If found, update quantity_produced; otherwise reset to 0 or leave as is
                    quantity_produced: selectedFormula ? parseFloat(selectedFormula.yield_quantity) : 0
                  });
                }}
                style={styles.input}
              >
                <option value="">Select Formula</option>
                {formulas.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.finished_good_code?.includes('.') ? f.finished_good_code.split('.').pop() : f.finished_good_code} - {f.finished_good_name}
                    {f.formula_name && ` (${f.formula_name})`}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Production Date *</label>
              <input
                type="date"
                min={getMinDate(form.branch_id, postedPeriods)}
                value={form.production_date}
                onChange={(e) => setForm({ ...form, production_date: e.target.value })}
                style={{
                  ...styles.input,
                  borderColor: isPeriodRestricted(form.branch_id, form.order_date, postedPeriods) ? '#ef4444' : '#d1d5db'
                }}
              />
            </div>

            {selectedFormula && (
              <div style={styles.formGroup}>
                {/* <label style={styles.label}>Quantity Produced (in {selectedFormula.finished_good_unit})*</label> */}
                <label style={styles.label}>Quantity Produced (in {selectedFormula.finished_good_unit})*</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.quantity_produced || ''}
                  placeholder="0.00"
                  onChange={(e) => setForm({ ...form, quantity_produced: parseFloat(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              style={{ ...styles.input, minHeight: '60px' }}
              placeholder="Optional production notes"
            />
          </div>

          {selectedFormula && (
            <div style={styles.infoBox}>
              <div style={{ fontSize: '13px', color: '#065f46' }}>
                <strong>📋 Formula Details:</strong>
                <div style={{ marginTop: '8px' }}>
                  • Yield: {selectedFormula.yield_quantity} units<br />
                  • Raw Materials: {selectedFormula.raw_material_count || 0} items<br />
                  • Main Ingredient: {selectedFormula.main_ingredient_name ? (
                    <span style={styles.badgeGreen}>{selectedFormula.main_ingredient_name}</span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>Not set - Cost calculation unavailable</span>
                  )}
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                  💡 Cost will be calculated based on the main ingredient only using FIFO method
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={handleSubmit} style={styles.button}>
              {editingId ? 'Update' : 'Create'} Production Entry
            </button>
            <button onClick={resetForm} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Finished Good</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Qty Produced</th>
                <th style={styles.th}>Batch #</th>
                <th style={styles.th}>Created</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>
                    PE-{entry.id}
                  </td>
                  <td style={styles.td}>
                    {entry.finished_good_code?.includes('.') ? entry.finished_good_code.split('.').pop() : entry.finished_good_code} - {entry.finished_good_name}
                  </td>
                  <td style={styles.td}>
                    {new Date(entry.production_date).toLocaleDateString('en-US', dateOptions)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: '600' }}>
                    {entry.quantity_produced}
                  </td>
                  <td style={styles.td}>
                    {entry.batch_number ? (
                      <span style={{ ...styles.badge, ...styles.badgeBlue }}>
                        {entry.batch_number}
                      </span>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {new Date(entry.created_at).toLocaleDateString('en-US', dateOptions)}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => viewEntry(entry.id)}
                      style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}
                      title="View Details"
                    >
                      <Eye style={{ width: '16px', height: '16px' }} />
                    </button>
                    {/* <button
                      onClick={() => handleEdit(entry)}
                      style={{ ...styles.iconButton, color: '#f59e0b', marginRight: '8px' }}
                      title="Edit"
                    >
                      <Edit style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      style={{ ...styles.iconButton, color: '#ef4444' }}
                      title="Delete"
                    >
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entries.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <Package style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No production entries found</p>
          </div>
        )}
      </div>

      {/* View Entry Modal */}
      {viewingEntry && (
        <div style={styles.modal} onClick={() => setViewingEntry(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Production Entry PE-{viewingEntry.entry.id}
              </h2>
              <button onClick={() => setViewingEntry(null)} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px', marginBottom: '24px' }}>
              <strong>Finished Good:</strong>
              <span>{viewingEntry.entry.end_product_code} - {viewingEntry.entry.end_product_name}</span>

              <strong>Production Date:</strong>
              <span>{new Date(viewingEntry.entry.production_date).toLocaleDateString('en-US', dateOptions)}</span>

              <strong>Quantity Produced:</strong>
              <span style={{ fontWeight: '600' }}>{viewingEntry.entry.quantity_produced} {viewingEntry.entry.end_product_unit}</span>

              <strong>Batch Number:</strong>
              <span>{viewingEntry.entry.batch_number || 'N/A'}</span>

              <strong>Formula:</strong>
              <span>{viewingEntry.entry.formula_name || 'N/A'}</span>

              <strong>Notes:</strong>
              <span>{viewingEntry.entry.notes || 'N/A'}</span>

              <strong>Created:</strong>
              <span>{formatDateTime(viewingEntry.entry.created_at)}</span>
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>Raw Materials Used</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Item Code</th>
                    <th style={styles.th}>Item Name</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Unit</th>
                    <th style={styles.th}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingEntry.items && viewingEntry.items.map((item, idx) => (
                    <tr key={idx} style={{ backgroundColor: item.is_main_ingredient ? '#f0fdf4' : 'transparent' }}>
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{item.item_code?.includes('.') ? item.item_code.split('.').pop() : item.item_code}</td>
                      <td style={styles.td}>{item.item_name}</td>
                      <td style={{ ...styles.td, fontWeight: '600' }}>{item.quantity_consumed}</td>
                      <td style={styles.td}>{item.unit_of_measurement}</td>
                      <td style={styles.td}>
                        {/* {item.is_main_ingredient ? (
                          <span style={styles.badgeGreen}>Main Ingredient</span>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>Supporting</span>
                        )} */}
                        {item.is_main_ingredient ? (
                          <span style={styles.badgeGreen}>
                            <CheckCircle style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                            Main Ingredient
                          </span>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            Supporting
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => setViewingEntry(null)}
              style={{ ...styles.button, marginTop: '16px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Cost Calculation Modal */}
      {showCostModal && costCalculation && (
        <div style={styles.modal} onClick={() => setShowCostModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                <DollarSign style={{ width: '24px', height: '24px', display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />
                Production Cost Calculation
              </h2>
              <button onClick={() => setShowCostModal(false)} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ ...styles.alert, backgroundColor: '#dbeafe', color: '#1e3a8a', border: '1px solid #3b82f6', marginBottom: '16px' }}>
              ℹ️ <strong>Note:</strong> This cost is calculated using the main ingredient only via FIFO method from Production branch.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px', marginBottom: '24px' }}>
              <strong>Multiplier:</strong>
              <span>{costCalculation.multiplier}x</span>

              <strong>Branch:</strong>
              <span>Production (ID: {costCalculation.branch_id})</span>

              <strong>Main Ingredient Only:</strong>
              <span style={styles.badgeGreen}>Yes</span>
            </div>

            <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                Total Cost: ₱{costCalculation.total_cost.toFixed(2)}
              </div>
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>Cost Breakdown</h3>
            {costCalculation.cost_breakdown.map((item, idx) => (
              <div key={idx} style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #10b981' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div>
                    <strong style={{ fontSize: '15px' }}>{item.item_code} - {item.item_name}</strong>
                    <div style={{ fontSize: '13px', color: '#065f46', marginTop: '4px' }}>
                      Quantity: {item.quantity_needed} units
                    </div>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#065f46' }}>
                    ₱{item.item_total_cost.toFixed(2)}
                  </div>
                </div>

                <div style={{ fontSize: '13px' }}>
                  <strong>FIFO Batches Used:</strong>
                  <table style={{ ...styles.table, marginTop: '8px', fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, padding: '8px' }}>Batch ID</th>
                        <th style={{ ...styles.th, padding: '8px' }}>Quantity</th>
                        <th style={{ ...styles.th, padding: '8px' }}>Unit Price</th>
                        <th style={{ ...styles.th, padding: '8px' }}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.batches.map((batch, bIdx) => (
                        <tr key={bIdx}>
                          <td style={{ ...styles.td, padding: '8px', fontFamily: 'monospace' }}>#{batch.batch_id}</td>
                          <td style={{ ...styles.td, padding: '8px' }}>{batch.quantity.toFixed(2)}</td>
                          <td style={{ ...styles.td, padding: '8px' }}>₱{batch.unit_price.toFixed(2)}</td>
                          <td style={{ ...styles.td, padding: '8px', fontWeight: '600' }}>₱{batch.total_cost.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <button
              onClick={() => setShowCostModal(false)}
              style={{ ...styles.button, marginTop: '16px', width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


const UserManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role_id: '', branch_ids: [], is_active: 1 });
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchBranches();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/roles`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRoles(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branches?is_active=1&branch_type=branch`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data.filter(b => b.is_active));
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/users/${editingId}` : `${API_URL}/users`;
      const method = editingId ? 'PUT' : 'POST';

      // For updates, don't send password if it's empty
      const payload = { ...form };
      if (editingId && !form.password) {
        delete payload.password;
      }

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        fetchUsers();
        setShowForm(false);
        setEditingId(null);
        setForm({
          username: '',
          email: '',
          fullname: '',
          password: '',
          role_id: '',
          branch_ids: [],
          is_active: 1
        });
        alert(editingId ? 'User updated successfully' : 'User created successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error saving user');
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error saving user');
      }
    }
  };

  const handleEdit = (user) => {
    setForm({
      username: user.username,
      email: user.email,
      fullname: user.fullname,
      password: '', // Don't populate password for security
      role_id: user.role_id,
      branch_ids: user.branches ? user.branches.map(b => b.id) : [],
      is_active: user.is_active
    });
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete user "${username}"? This action cannot be undone.`)) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchUsers();
        alert('User deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting user');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus ? 0 : 1;
    const action = newStatus ? 'activate' : 'deactivate';

    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (res.ok) {
        fetchUsers();
        alert(`User ${action}d successfully`);
      } else {
        const data = await res.json();
        alert(data.error || `Error ${action}ing user`);
      }
    } catch (err) {
      console.error('Error:', err);
      alert(`Error ${action}ing user`);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      username: '',
      email: '',
      fullname: '',
      password: '',
      role_id: '',
      branch_ids: [],
      is_active: 1
    });
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          User Management
        </h1>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          style={styles.button}
        >
          <Plus style={{ width: '20px', height: '20px' }} /> Add User
        </button>
      </div>

      {showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          title={editingId ? 'Edit User' : 'Add User'}
        >
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Username *</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                style={styles.input}
                disabled={editingId !== null}
              />
              {editingId && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  Username cannot be changed
                </span>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                value={form.fullname}
                onChange={(e) => setForm({ ...form, fullname: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Password {editingId ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Role *</label>
              <select
                value={form.role_id}
                onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                style={styles.input}
              >
                <option value="">Select Role</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <select
                value={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: parseInt(e.target.value) })
                }
                style={styles.input}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Allowed Branches</label>
              <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {branches.map(b => (
                  <label key={b.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={form.branch_ids.includes(b.id)}
                      onChange={(e) => {
                        const newIds = e.target.checked
                          ? [...form.branch_ids, b.id]
                          : form.branch_ids.filter(id => id !== b.id);
                        setForm({ ...form, branch_ids: newIds });
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {b.name}
                  </label>
                ))}
                {branches.length === 0 && <span style={{ color: '#6b7280' }}>No active branches available</span>}
              </div>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                Select one or more branches to grant access. Admin role ignores this.
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            <button onClick={handleSubmit} style={styles.button}>
              {editingId ? 'Update' : 'Create'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              style={{ ...styles.button, ...styles.buttonSecondary }}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ ...styles.td, fontWeight: '500' }}>
                  {u.username}
                  {u.id === 1 && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '11px',
                      color: '#8b5cf6',
                      fontWeight: '600'
                    }}>
                      ADMIN
                    </span>
                  )}
                </td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    backgroundColor: '#eff6ff',
                    color: '#1e40af',
                    border: '1px solid #3b82f6'
                  }}>
                    {u.role_name}
                  </span>
                </td>
                <td style={styles.td}>
                  {u.branch_name || (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>All Branches</span>
                  )}
                </td>
                <td style={styles.td}>
                  <button
                    onClick={() => toggleUserStatus(u.id, u.is_active)}
                    disabled={u.id === 1}
                    style={{
                      ...styles.badge,
                      ...(u.is_active ? styles.badgeGreen : styles.badgeRed),
                      cursor: u.id === 1 ? 'not-allowed' : 'pointer',
                      border: 'none',
                      opacity: u.id === 1 ? 0.6 : 1
                    }}
                    title={u.id === 1 ? 'Cannot deactivate admin user' : 'Click to toggle status'}
                  >
                    {u.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button
                    onClick={() => handleEdit(u)}
                    style={{
                      ...styles.iconButton,
                      color: '#4f46e5',
                      marginRight: '8px'
                    }}
                    title="Edit User"
                  >
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button
                    onClick={() => handleDelete(u.id, u.username)}
                    style={{ ...styles.iconButton, color: '#ef4444' }}
                    disabled={u.id === 1}
                    title={u.id === 1 ? 'Cannot delete admin user' : 'Delete User'}
                  >
                    <Trash2 style={{
                      width: '16px',
                      height: '16px',
                      opacity: u.id === 1 ? 0.3 : 1
                    }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px',
            color: '#6b7280'
          }}>
            <User style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              opacity: 0.3
            }} />
            <p style={{ margin: 0 }}>No users found. Click "Add User" to create one.</p>
          </div>
        )}
      </div>
    </div>
  );
};


const InventoryBatchesReport = ({ token }) => {
  const [reportType, setReportType] = useState('inventory-batches');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/inventory-batches?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderInventoryBatchesReport = () => {
    if (!reportData || !reportData.data) return null;

    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const category = item.item_category || "Uncategorized";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][category]) acc[branch][category] = [];

      acc[branch][category].push(item);
      return acc;
    }, {});

    return (
      <div>
        <div style={styles.grid4}>
          <div style={styles.statCard}>
            <div style={styles.statCardContent}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0' }}>Total Items</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>{"TBA"}</p>
              </div>
              <div style={{ ...styles.statIcon, backgroundColor: '#3b82f6' }}>
                <Package style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statCardContent}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0' }}>Low Stock</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444', margin: 0 }}>{"TBA"}</p>
              </div>
              <div style={{ ...styles.statIcon, backgroundColor: '#ef4444' }}>
                <Package style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statCardContent}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0' }}>Normal Stock</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981', margin: 0 }}>{"TBA" || 0}</p>
              </div>
              <div style={{ ...styles.statIcon, backgroundColor: '#10b981' }}>
                <Package style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statCardContent}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px 0' }}>Overstock</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b', margin: 0 }}>{"TBA"}</p>
              </div>
              <div style={{ ...styles.statIcon, backgroundColor: '#f59e0b' }}>
                <Package style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
            </div>
          </div>
        </div>

        {Object.keys(nestedData).map((branch) => (
          <div key={branch} style={{ ...styles.card, marginTop: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}></h3>
            <hr />
            <div style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                <p style={{ fontWeight: 'bold' }}>{branch} | </p>
                <CalendarDays style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                <p>From <span style={{ fontWeight: 'bold' }}>{new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)}</span> to <span style={{ fontWeight: 'bold' }}> {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}</span></p>
              </div>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Batch </th>
                  <th style={styles.th}>Beginning Balance</th>
                  <th style={styles.th}>Received Qty</th>
                  <th style={styles.th}>Produced Qty</th>
                  <th style={styles.th}>Consumption Qty</th>
                  <th style={styles.th}>Sales Qty</th>
                  <th style={styles.th}>Adjustment Qty</th>
                  <th style={styles.th}>Ending Balance</th>
                  <th style={styles.th}>Unit Price</th>
                  <th style={styles.th}>Selling Price</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(nestedData[branch]).map((category) => (
                  <React.Fragment key={category}>
                    {/* CATEGORY ROW (Spans the whole table) */}
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <td colSpan="11" style={{
                        padding: '12px 16px',
                        fontWeight: '700',
                        fontSize: '14px',
                        color: '#4b5563',
                        borderBottom: '1px solid #e5e7eb',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Category: {category}
                      </td>
                    </tr>

                    {nestedData[branch][category].map((row) => (
                      <tr key={row.batch_id}>
                        <td style={styles.td}>
                          <span style={{ display: 'block' }}>{row.item_code?.includes('.') ? row.item_code.split('.').pop() : row.item_code} - {row.item_name}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>Category: {row.item_category}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ display: 'block' }}>{row.batch_number}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>Batch Date: {new Date(row.batch_date).toLocaleDateString('en-US', dateOptions)}</span>
                        </td>
                        <td style={styles.td}>{row.beg_bal}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.recv}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.prod}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.consumption}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.sales}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.adj}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.ending_bal}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.unit_price}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.selling_price}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Inventory Batches Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'inventory-batches' && renderInventoryBatchesReport()}
        </div>
      )}
    </div>
  );
};


const InventorySummaryReport = ({ token }) => {
  const [reportType, setReportType] = useState('inventory-summary');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/inventory-summary?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderInventorySummaryReport = () => {
    if (!reportData || !reportData.data) return null;

    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const category = item.item_category || "Uncategorized";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][category]) acc[branch][category] = [];

      acc[branch][category].push(item);
      return acc;
    }, {});

    return (
      <div>
        {Object.keys(nestedData).map((branchName) => {
          const categoriesInBranch = Object.keys(nestedData[branchName]);

          return (
            <div key={branchName} style={styles.grid4}>
              {/* Header Area */}
              <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderBottom: '2px solid #3b82f6' }}>
                <h2 style={{ margin: 0, color: '#1e293b' }}>🏢 {branchName}</h2>
                <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '0.9rem' }}>
                  Period: {new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)} to {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}
                </p>
              </div>

              <div style={{ padding: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap', backgroundColor: '#fff' }}>
                {categoriesInBranch.map(cat => {
                  const items = nestedData[branchName][cat];
                  const totalEndBal = items.reduce((sum, i) => sum + parseFloat(i.ending_bal), 0);
                  const totalRecv = items.reduce((sum, i) => sum + parseFloat(i.recv), 0);

                  const healthWidth = Math.min(Math.max((totalEndBal / (totalRecv || 1)) * 100, 0), 100);

                  return (
                    <div key={cat} style={{ flex: '1', minWidth: '200px', padding: '15px', border: '1px solid #f1f5f9', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px' }}>{cat.toUpperCase()}</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a' }}>{totalEndBal.toFixed(2)}</div>
                      <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', marginTop: '10px' }}>
                        <div style={{ width: `${healthWidth}%`, height: '100%', backgroundColor: healthWidth > 20 ? '#22c55e' : '#ef4444', borderRadius: '4px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {Object.keys(nestedData).map((branch) => (
          <div key={branch} style={{ ...styles.card, marginTop: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>Inventory Summary Report</h3>
            <hr />
            <div style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                <p style={{ fontWeight: 'bold' }}>{branch} | </p>
                <CalendarDays style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                <p>From <span style={{ fontWeight: 'bold' }}>{new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)}</span> to <span style={{ fontWeight: 'bold' }}> {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}</span></p>
              </div>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Beginning Balance</th>
                  <th style={styles.th}>Received Qty</th>
                  <th style={styles.th}>Produced Qty</th>
                  <th style={styles.th}>Consumption Qty</th>
                  <th style={styles.th}>Sales Qty</th>
                  <th style={styles.th}>Adjustment Qty</th>
                  <th style={styles.th}>Ending Balance</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(nestedData[branch]).map((category) => (
                  <React.Fragment key={category}>
                    {/* CATEGORY ROW (Spans the whole table) */}
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <td colSpan="11" style={{
                        padding: '12px 16px',
                        fontWeight: '700',
                        fontSize: '14px',
                        color: '#4b5563',
                        borderBottom: '1px solid #e5e7eb',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Category: {category}
                      </td>
                    </tr>

                    {nestedData[branch][category].map((row) => (
                      <tr key={row.batch_id}>
                        <td style={styles.td}>
                          <span style={{ display: 'block' }}>{row.item_code?.includes('.') ? row.item_code.split('.').pop() : row.item_code} - {row.item_name}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>Category: {row.item_category}</span>
                        </td>
                        <td style={styles.td}>{row.beg_bal}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.recv}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.prod}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.consumption}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.sales}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.adj}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.ending_bal}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Inventory Summary Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'inventory-summary' && renderInventorySummaryReport()}
        </div>
      )}
    </div>
  );
};


const InventorySummaryPrepEndingReport = ({ token }) => {
  const [reportType, setReportType] = useState('inventory-summary-with-prep-ending');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/inventory-summary-with-prep-ending?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };


  const renderInventorySummaryWithPrepEndingReport = () => {
    if (!reportData || !reportData.data) return null;

    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const category = item.item_category || "Uncategorized";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][category]) acc[branch][category] = [];

      acc[branch][category].push(item);
      return acc;
    }, {});

    return (
      <div>
        {Object.keys(nestedData).map((branchName) => {
          const categoriesInBranch = Object.keys(nestedData[branchName]);

          return (
            <div key={branchName} style={styles.grid4}>
              {/* Header Area */}
              <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderBottom: '2px solid #3b82f6' }}>
                <h2 style={{ margin: 0, color: '#1e293b' }}>🏢 {branchName}</h2>
                <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '0.9rem' }}>
                  Period: {new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)} to {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}
                </p>
              </div>

              <div style={{ padding: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap', backgroundColor: '#fff' }}>
                {categoriesInBranch.map(cat => {
                  const items = nestedData[branchName][cat];
                  const totalEndBal = items.reduce((sum, i) => sum + parseFloat(i.ending_bal), 0);
                  const totalRecv = items.reduce((sum, i) => sum + parseFloat(i.recv), 0);

                  const healthWidth = Math.min(Math.max((totalEndBal / (totalRecv || 1)) * 100, 0), 100);

                  return (
                    <div key={cat} style={{ flex: '1', minWidth: '200px', padding: '15px', border: '1px solid #f1f5f9', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px' }}>{cat.toUpperCase()}</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a' }}>{totalEndBal.toFixed(2)}</div>
                      <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', marginTop: '10px' }}>
                        <div style={{ width: `${healthWidth}%`, height: '100%', backgroundColor: healthWidth > 20 ? '#22c55e' : '#ef4444', borderRadius: '4px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {Object.keys(nestedData).map((branch) => (
          <div key={branch} style={{ ...styles.card, marginTop: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>Inventory Summary with Prep Ending Report</h3>
            <hr />
            <div style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                <p style={{ fontWeight: 'bold' }}>{branch} | </p>
                <CalendarDays style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                <p>From <span style={{ fontWeight: 'bold' }}>{new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)}</span> to <span style={{ fontWeight: 'bold' }}> {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}</span></p>
              </div>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Beginning Balance</th>
                  <th style={styles.th}>Received Qty</th>
                  <th style={styles.th}>Produced Qty</th>
                  <th style={styles.th}>Consumption Qty</th>
                  <th style={styles.th}>Sales Qty</th>
                  <th style={styles.th}>Adjustment Qty</th>
                  <th style={styles.th}>Ending Balance</th>
                  <th style={styles.th}>Prep Ending</th> {/* New column */}
                </tr>
              </thead>
              <tbody>
                {Object.keys(nestedData[branch]).map((category) => (
                  <React.Fragment key={category}>
                    {/* CATEGORY ROW */}
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <td colSpan="11" style={{
                        padding: '12px 16px',
                        fontWeight: '700',
                        fontSize: '14px',
                        color: '#4b5563',
                        borderBottom: '1px solid #e5e7eb',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Category: {category}
                      </td>
                    </tr>

                    {nestedData[branch][category].map((row) => (
                      <tr key={row.batch_id}>
                        <td style={styles.td}>
                          <span style={{ display: 'block' }}>{row.item_code?.includes('.') ? row.item_code.split('.').pop() : row.item_code} - {row.item_name}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>Category: {row.item_category}</span>
                        </td>
                        <td style={styles.td}>{row.beg_bal}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.recv}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.prod}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.consumption}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.sales}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.adj}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.ending_bal}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td>
                        <td style={styles.td}>{row.prep_ending || '-'}<span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>{row.unit_of_measurement}</span></td> {/* Prep Ending */}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Inventory Summary with Prep Ending Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'inventory-summary-with-prep-ending' && renderInventorySummaryWithPrepEndingReport()}
        </div>
      )}
    </div>
  );
};


const ReceivingDetailByDate = ({ token }) => {
  const [reportType, setReportType] = useState('receiving-detail-by-date');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/receiving-detail?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const sumReceivingTotals = (items) => {
    return items.reduce(
      (acc, row) => {
        acc.quantity += Number(row.quantity || 0);
        acc.amount += Number(row.quantity || 0) * Number(row.unit_price || 0);
        return acc;
      },
      { quantity: 0, amount: 0 }
    );
  };

  const renderReceivingDetailbyDate = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const sumReceivingTotals = (items) =>
      items.reduce(
        (acc, row) => {
          const qty = Number(row.quantity || 0);
          const price = Number(row.unit_price || 0);
          acc.quantity += qty;
          acc.amount += qty * price;
          return acc;
        },
        { quantity: 0, amount: 0 }
      );

    // ===============================
    // GROUP DATA
    // ===============================
    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const receivedDate = item.received_date || "Unknown Date";
      const receivingId = item.id || "N/A";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][receivedDate]) acc[branch][receivedDate] = {};
      if (!acc[branch][receivedDate][receivingId])
        acc[branch][receivedDate][receivingId] = [];

      acc[branch][receivedDate][receivingId].push(item);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>

              <CalendarDays style={{ width: 20, height: 20, color: '#1e40af' }} />
              <span>
                {new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)}
                {" – "}
                {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}
              </span>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Item</th>
                  <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty</th>
                  <th style={{ ...styles.th, ...alignRight }}>Unit Price</th>
                  <th style={{ ...styles.th, ...alignRight }}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch])
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((receivedDate) => {
                    let dateQty = 0;
                    let dateAmount = 0;

                    return (
                      <React.Fragment key={receivedDate}>
                        {/* ========== DATE HEADER ========== */}
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <td
                            colSpan="5"
                            style={{
                              padding: '12px 16px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                          >
                            Received Date:{" "}
                            {new Date(receivedDate).toLocaleDateString('en-US', dateOptions)}
                          </td>
                        </tr>

                        {Object.keys(nestedData[branch][receivedDate]).map((receivingId) => {
                          const items = nestedData[branch][receivedDate][receivingId];
                          const supplier = items[0]?.supplier_name || "Unknown Supplier";
                          const notes = items[0]?.notes || "-";
                          const { quantity, amount } = sumReceivingTotals(items);

                          dateQty += quantity;
                          dateAmount += amount;

                          return (
                            <React.Fragment key={receivingId}>
                              {/* ========== RE HEADER ========== */}
                              <tr>
                                <td
                                  colSpan="5"
                                  style={{
                                    backgroundColor: '#f0f9ff',
                                    padding: '10px 16px',
                                    fontWeight: 400,
                                    borderBottom: '1px solid #f0f9ff',
                                    color: '#000000'
                                  }}
                                >
                                  RE No: <strong>{receivingId}</strong> | Supplier:{" "}
                                  <strong>{supplier}</strong> | Notes: {notes}
                                </td>
                              </tr>

                              {/* ========== ITEM ROWS ========== */}
                              {items.map((row) => (
                                <tr key={row.batch_id} style={printSafeRow}>
                                  <td
                                    style={{
                                      ...styles.td,
                                      ...alignLeft,
                                      lineHeight: 1.45
                                    }}
                                  >
                                    <div style={{ fontWeight: 600 }}>
                                      {row.inventory_item_id} – {row.item_name}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                      Category: {row.item_category || "Uncategorized"}
                                    </div>
                                  </td>

                                  <td style={{ ...styles.td, ...alignCenter }}>
                                    {row.unit_of_measurement}
                                  </td>

                                  <td style={{ ...styles.td, ...alignRight }}>
                                    {formatCurrency(row.quantity)}
                                  </td>

                                  <td style={{ ...styles.td, ...alignRight }}>
                                    ₱{formatCurrency(row.unit_price)}
                                  </td>

                                  <td style={{ ...styles.td, ...alignRight }}>
                                    ₱{formatCurrency(row.quantity * row.unit_price)}
                                  </td>
                                </tr>
                              ))}

                              {/* ========== RE TOTAL ========== */}
                              <tr style={{ backgroundColor: '#5077c2' }}>
                                <td
                                  colSpan="2"
                                  style={{
                                    padding: '10px 16px',
                                    fontWeight: 600,
                                    textAlign: 'right'
                                  }}
                                >
                                  RE TOTAL
                                </td>
                                <td style={{ ...alignRight, fontWeight: 700 }}>
                                  {formatCurrency(quantity)}
                                </td>
                                <td />
                                <td style={{ ...alignRight, fontWeight: 700 }}>
                                  ₱{formatCurrency(amount)}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}

                        {/* ========== DATE TOTAL ========== */}
                        <tr style={{ backgroundColor: '#ecfeff' }}>
                          <td
                            colSpan="2"
                            style={{
                              padding: '12px 16px',
                              fontWeight: 700,
                              textAlign: 'right'
                            }}
                          >
                            DATE TOTAL
                          </td>
                          <td style={{ ...alignRight, fontWeight: 800 }}>
                            {formatCurrency(dateQty)}
                          </td>
                          <td />
                          <td style={{ ...alignRight, fontWeight: 800 }}>
                            ₱{formatCurrency(dateAmount)}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Receiving Detail by Date Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'receiving-detail-by-date' && renderReceivingDetailbyDate()}
        </div>
      )}
    </div>
  );
};


const ReceivingDetailBySupplier = ({ token }) => {
  const [reportType, setReportType] = useState('receiving-detail-by-supplier');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/receiving-detail?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const sumReceivingTotals = (items) => {
    return items.reduce(
      (acc, row) => {
        acc.quantity += Number(row.quantity || 0);
        acc.amount += Number(row.quantity || 0) * Number(row.unit_price || 0);
        return acc;
      },
      { quantity: 0, amount: 0 }
    );
  };


  const renderReceivingDetailBySupplier = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const sumReceivingTotals = (items) =>
      items.reduce(
        (acc, row) => {
          const qty = Number(row.quantity || 0);
          const price = Number(row.unit_price || 0);
          acc.quantity += qty;
          acc.amount += qty * price;
          return acc;
        },
        { quantity: 0, amount: 0 }
      );

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return !isNaN(d) ? d.toLocaleDateString('en-US', dateOptions) : dateStr || "-";
    };

    // ===============================
    // GROUP DATA
    // ===============================
    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const supplier = item.supplier_name || "Unknown Supplier";
      const receivingId = item.id || "N/A";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][supplier]) acc[branch][supplier] = {};
      if (!acc[branch][supplier][receivingId]) acc[branch][supplier][receivingId] = [];

      acc[branch][supplier][receivingId].push(item);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>

              <CalendarDays style={{ width: 20, height: 20, color: '#1e40af' }} />
              <span>
                {new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)}
                {" – "}
                {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}
              </span>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Supplier / Item</th>
                  <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty</th>
                  <th style={{ ...styles.th, ...alignRight }}>Unit Price</th>
                  <th style={{ ...styles.th, ...alignRight }}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch]).map((supplier) => {
                  let supplierQty = 0;
                  let supplierAmount = 0;

                  return (
                    <React.Fragment key={supplier}>
                      {/* ========== SUPPLIER HEADER ========== */}
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <td
                          colSpan="5"
                          style={{
                            padding: '12px 16px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                          }}
                        >
                          Supplier: <strong>{supplier}</strong>
                        </td>
                      </tr>

                      {Object.keys(nestedData[branch][supplier]).map((receivingId) => {
                        const items = nestedData[branch][supplier][receivingId];
                        const { quantity, amount } = sumReceivingTotals(items);
                        const receivedDate = formatDate(items[0]?.received_date);

                        supplierQty += quantity;
                        supplierAmount += amount;

                        return (
                          <React.Fragment key={receivingId}>
                            {/* ========== RE HEADER WITH DATE ========== */}
                            <tr style={{ backgroundColor: '#f0f9ff' }}>
                              <td
                                colSpan="5"
                                style={{
                                  padding: '10px 16px',
                                  fontWeight: 400,
                                  borderBottom: '1px solid #f0f9ff',
                                  color: '#000000'
                                }}
                              >
                                RE No: <strong>{receivingId}</strong> | Date: {receivedDate}
                              </td>
                            </tr>

                            {/* ========== ITEM ROWS ========== */}
                            {items.map((row) => (
                              <tr key={row.batch_id} style={printSafeRow}>
                                <td
                                  style={{
                                    ...styles.td,
                                    ...alignLeft,
                                    lineHeight: 1.45
                                  }}
                                >
                                  <div style={{ fontWeight: 600 }}>
                                    {row.inventory_item_id} – {row.item_name}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    Category: {row.item_category || "Uncategorized"}
                                  </div>
                                </td>

                                <td style={{ ...styles.td, ...alignCenter }}>
                                  {row.unit_of_measurement}
                                </td>

                                <td style={{ ...styles.td, ...alignRight }}>
                                  {formatCurrency(row.quantity)}
                                </td>

                                <td style={{ ...styles.td, ...alignRight }}>
                                  ₱{formatCurrency(row.unit_price)}
                                </td>

                                <td style={{ ...styles.td, ...alignRight }}>
                                  ₱{formatCurrency(row.quantity * row.unit_price)}
                                </td>
                              </tr>
                            ))}

                            {/* ========== RE TOTAL ========== */}
                            <tr style={{ backgroundColor: '#5077c2' }}>
                              <td
                                colSpan="2"
                                style={{
                                  padding: '10px 16px',
                                  fontWeight: 600,
                                  textAlign: 'right'
                                }}
                              >
                                RE TOTAL
                              </td>
                              <td style={{ ...alignRight, fontWeight: 700 }}>
                                {formatCurrency(quantity)}
                              </td>
                              <td />
                              <td style={{ ...alignRight, fontWeight: 700 }}>
                                ₱{formatCurrency(amount)}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}

                      {/* ========== SUPPLIER TOTAL ========== */}
                      <tr style={{ backgroundColor: '#ecfeff' }}>
                        <td
                          colSpan="2"
                          style={{
                            padding: '12px 16px',
                            fontWeight: 700,
                            textAlign: 'right'
                          }}
                        >
                          SUPPLIER TOTAL
                        </td>
                        <td style={{ ...alignRight, fontWeight: 800 }}>
                          {formatCurrency(supplierQty)}
                        </td>
                        <td />
                        <td style={{ ...alignRight, fontWeight: 800 }}>
                          ₱{formatCurrency(supplierAmount)}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Receiving Detail by Supplier Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'receiving-detail-by-supplier' && renderReceivingDetailBySupplier()}
        </div>
      )}
    </div>
  );
};


const ReceivingDetailByItem = ({ token }) => {
  const [reportType, setReportType] = useState('receiving-detail-by-item');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/receiving-detail?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const sumReceivingTotals = (items) => {
    return items.reduce(
      (acc, row) => {
        acc.quantity += Number(row.quantity || 0);
        acc.amount += Number(row.quantity || 0) * Number(row.unit_price || 0);
        return acc;
      },
      { quantity: 0, amount: 0 }
    );
  };

  const renderReceivingDetailByItem = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const sumReceivingTotals = (items) =>
      items.reduce(
        (acc, row) => {
          const qty = Number(row.quantity || 0);
          const price = Number(row.unit_price || 0);
          acc.quantity += qty;
          acc.amount += qty * price;
          return acc;
        },
        { quantity: 0, amount: 0 }
      );

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return !isNaN(d) ? d.toLocaleDateString('en-US', dateOptions) : dateStr || "-";
    };

    // ===============================
    // GROUP DATA: Branch → Category → Item
    // ===============================
    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const category = item.item_category || "Uncategorized";
      const itemName = item.item_name || "Unknown Item";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][category]) acc[branch][category] = {};
      if (!acc[branch][category][itemName]) acc[branch][category][itemName] = [];

      acc[branch][category][itemName].push(item);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>

              <CalendarDays style={{ width: 20, height: 20, color: '#1e40af' }} />
              <span>
                {new Date(reportData.dateFrom).toLocaleDateString('en-US', dateOptions)}
                {" – "}
                {new Date(reportData.dateTo).toLocaleDateString('en-US', dateOptions)}
              </span>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Category / Item</th>
                  <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty</th>
                  <th style={{ ...styles.th, ...alignRight }}>Unit Price</th>
                  <th style={{ ...styles.th, ...alignRight }}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch]).map((category) => {
                  let categoryQty = 0;
                  let categoryAmount = 0;

                  return (
                    <React.Fragment key={category}>
                      {/* ========== CATEGORY HEADER ========== */}
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <td
                          colSpan="5"
                          style={{
                            padding: '12px 16px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e5e7eb'
                          }}
                        >
                          Category: <strong>{category}</strong>
                        </td>
                      </tr>

                      {Object.keys(nestedData[branch][category]).map((itemName) => {
                        const items = nestedData[branch][category][itemName];
                        const { quantity, amount } = sumReceivingTotals(items);
                        categoryQty += quantity;
                        categoryAmount += amount;

                        return (
                          <React.Fragment key={itemName}>
                            {/* ========== ITEM ROWS ========== */}
                            {items.map((row) => (
                              <tr key={row.batch_id} style={printSafeRow}>
                                <td style={{ ...styles.td, ...alignLeft, lineHeight: 1.45 }}>
                                  <div style={{ fontWeight: 600 }}>
                                    {row.inventory_item_id} – {row.item_name}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    RE No: {row.id || "-"} | Date: {formatDate(row.received_date)}
                                  </div>
                                </td>

                                <td style={{ ...styles.td, ...alignCenter }}>
                                  {row.unit_of_measurement}
                                </td>
                                <td style={{ ...styles.td, ...alignRight }}>
                                  {formatCurrency(row.quantity)}
                                </td>
                                <td style={{ ...styles.td, ...alignRight }}>
                                  ₱{formatCurrency(row.unit_price)}
                                </td>
                                <td style={{ ...styles.td, ...alignRight }}>
                                  ₱{formatCurrency(row.quantity * row.unit_price)}
                                </td>
                              </tr>
                            ))}

                            {/* ========== ITEM TOTAL ========== */}
                            <tr style={{ backgroundColor: '#5077c2' }}>
                              <td colSpan="2" style={{ padding: '10px 16px', fontWeight: 600, textAlign: 'right', color: '#ffffff' }}>
                                ITEM TOTAL
                              </td>
                              <td style={{ ...alignRight, fontWeight: 700, color: '#ffffff' }}>{formatCurrency(quantity)}</td>
                              <td />
                              <td style={{ ...alignRight, fontWeight: 700, color: '#ffffff' }}>₱{formatCurrency(amount)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      })}

                      {/* ========== CATEGORY TOTAL ========== */}
                      <tr style={{ backgroundColor: '#ecfeff' }}>
                        <td colSpan="2" style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>
                          CATEGORY TOTAL
                        </td>
                        <td style={{ ...alignRight, fontWeight: 800 }}>{formatCurrency(categoryQty)}</td>
                        <td />
                        <td style={{ ...alignRight, fontWeight: 800 }}>₱{formatCurrency(categoryAmount)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Receiving Detail by Item Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'receiving-detail-by-item' && renderReceivingDetailByItem()}
        </div>
      )}
    </div>
  );
};


const ProductionSummaryReport = ({ token }) => {
  const [reportType, setReportType] = useState('production-summary-report');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/production-summary?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const sumReceivingTotals = (items) => {
    return items.reduce(
      (acc, row) => {
        acc.quantity += Number(row.quantity || 0);
        acc.amount += Number(row.quantity || 0) * Number(row.unit_price || 0);
        return acc;
      },
      { quantity: 0, amount: 0 }
    );
  };

  const renderProductionSummary = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const sumProductionTotals = (items) =>
      items.reduce(
        (acc, row) => {
          const qty = Number(row.quantity_produced || 0);
          const price = Number(row.unit_price || 0);
          acc.quantity += qty;
          acc.amount += qty * price;
          return acc;
        },
        { quantity: 0, amount: 0 }
      );

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return !isNaN(d) ? d.toLocaleDateString('en-US', dateOptions) : dateStr || "-";
    };

    // ===============================
    // GROUP DATA: Branch → Production Date → Batch
    // ===============================
    const nestedData = reportData.data.reduce((acc, item) => {
      const branch = item.branch_name || "Unknown Branch";
      const prodDate = item.production_date
        ? new Date(item.production_date).toDateString()
        : "Unknown Date";
      const batch = item.batch_number || "N/A";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][prodDate]) acc[branch][prodDate] = {};
      if (!acc[branch][prodDate][batch]) acc[branch][prodDate][batch] = [];

      acc[branch][prodDate][batch].push(item);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Batch Number</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Item Code</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Item Name</th>
                  <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty Produced</th>
                  <th style={{ ...styles.th, ...alignRight }}>Unit Price</th>
                  <th style={{ ...styles.th, ...alignRight }}>Amount</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Created By</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch])
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((prodDate) => {
                    let dateQty = 0;
                    let dateAmount = 0;

                    return (
                      <React.Fragment key={prodDate}>
                        {/* ========== PRODUCTION DATE HEADER ========== */}
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <td
                            colSpan="8"
                            style={{
                              padding: '12px 16px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                          >
                            Production Date: {formatDate(prodDate)}
                          </td>
                        </tr>

                        {Object.keys(nestedData[branch][prodDate]).map((batch) => {
                          const items = nestedData[branch][prodDate][batch];
                          const { quantity, amount } = sumProductionTotals(items);
                          dateQty += quantity;
                          dateAmount += amount;

                          return (
                            <React.Fragment key={batch}>
                              {/* ========== BATCH ROWS ========== */}
                              {items.map((row) => (
                                <tr key={row.id} style={printSafeRow}>
                                  <td style={{ ...styles.td, ...alignLeft }}>{row.batch_number}</td>
                                  <td style={{ ...styles.td, ...alignLeft }}>{row.item_code}</td>
                                  <td style={{ ...styles.td, ...alignLeft }}>{row.item_name}</td>
                                  <td style={{ ...styles.td, ...alignCenter }}>{row.unit_of_measurement}</td>
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    {formatCurrency(row.quantity_produced)}
                                  </td>
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    ₱{formatCurrency(row.unit_price)}
                                  </td>
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    ₱{formatCurrency(row.quantity_produced * row.unit_price)}
                                  </td>
                                  <td style={{ ...styles.td, ...alignLeft }}>{row.created_y}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}

                        {/* ========== DATE TOTAL ========== */}
                        <tr style={{ backgroundColor: '#ecfeff' }}>
                          <td colSpan="4" style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>
                            DATE TOTAL
                          </td>
                          <td style={{ ...alignRight, fontWeight: 800 }}>{formatCurrency(dateQty)}</td>
                          <td />
                          <td style={{ ...alignRight, fontWeight: 800 }}>₱{formatCurrency(dateAmount)}</td>
                          <td />
                        </tr>
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Production Summary Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'production-summary-report' && renderProductionSummary()}
        </div>
      )}
    </div>
  );
};


const ProductionConsumptionReport = ({ token }) => {
  const [reportType, setReportType] = useState('production-consumption');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/production-consumption?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const sumReceivingTotals = (items) => {
    return items.reduce(
      (acc, row) => {
        acc.quantity += Number(row.quantity || 0);
        acc.amount += Number(row.quantity || 0) * Number(row.unit_price || 0);
        return acc;
      },
      { quantity: 0, amount: 0 }
    );
  };

  const renderProductionConsumption = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return !isNaN(d) ? d.toLocaleDateString('en-US', dateOptions) : dateStr || "-";
    };

    // ===============================
    // GROUP DATA
    // Branch → Production Date → Batch
    // ===============================
    const nestedData = reportData.data.reduce((acc, row) => {
      const branch = row.branch_name || "Unknown Branch";
      const prodDate = row.production_date
        ? new Date(row.production_date).toDateString()
        : "Unknown Date";
      const batch = row.prod_batch_number || "N/A";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][prodDate]) acc[branch][prodDate] = {};
      if (!acc[branch][prodDate][batch]) acc[branch][prodDate][batch] = [];

      acc[branch][prodDate][batch].push(row);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Prod Batch</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Finished Good</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty Produced</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Raw Material</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty Consumed</th>
                  <th style={{ ...styles.th, ...alignRight }}>RM Unit Cost</th>
                  <th style={{ ...styles.th, ...alignRight }}>RM Total Cost</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Created By</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch])
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((prodDate) => {
                    let dateQty = 0;
                    let dateAmount = 0;

                    return (
                      <React.Fragment key={prodDate}>
                        {/* ========== DATE HEADER ========== */}
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <td colSpan="8" style={{ padding: '12px 16px', fontWeight: 700 }}>
                            Production Date: {formatDate(prodDate)}
                          </td>
                        </tr>

                        {Object.entries(nestedData[branch][prodDate]).map(
                          ([batchNo, rows]) =>
                            rows.map((row, index) => {
                              dateQty += Number(row.quantity_consumed || 0);
                              dateAmount += Number(row.rm_total_cost || 0);

                              const isFirstRow = index === 0;

                              return (
                                <tr key={`${batchNo}-${index}`} style={printSafeRow}>
                                  {/* Batch */}
                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    {isFirstRow ? batchNo : ""}
                                  </td>

                                  {/* Finished Good */}
                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    {isFirstRow
                                      ? `${row.prod_item_code} – ${row.prod_item_name}`
                                      : ""}
                                  </td>

                                  {/* Qty Produced */}
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    {isFirstRow
                                      ? formatCurrency(row.quantity_produced)
                                      : ""}
                                  </td>

                                  {/* Raw Material */}
                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    {row.rm_item_code} – {row.rm_item_name}
                                  </td>

                                  {/* Qty Consumed */}
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    {formatCurrency(row.quantity_consumed)}
                                  </td>

                                  {/* Unit Cost */}
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    ₱{formatCurrency(row.rm_unit_price)}
                                  </td>

                                  {/* Total Cost */}
                                  <td style={{ ...styles.td, ...alignRight }}>
                                    ₱{formatCurrency(row.rm_total_cost)}
                                  </td>

                                  {/* Created By */}
                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    {row.created_by}
                                  </td>
                                </tr>
                              );
                            })
                        )}

                        {/* ========== DATE TOTAL ========== */}
                        <tr style={{ backgroundColor: '#ecfeff' }}>
                          <td colSpan="4" style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>
                            DATE TOTAL
                          </td>
                          <td style={{ ...alignRight, fontWeight: 800 }}>
                            {formatCurrency(dateQty)}
                          </td>
                          <td />
                          <td style={{ ...alignRight, fontWeight: 800 }}>
                            ₱{formatCurrency(dateAmount)}
                          </td>
                          <td />
                        </tr>
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Production Consumption Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'production-consumption' && renderProductionConsumption()}
        </div>
      )}
    </div>
  );
};


const DeliveryReceiptReport = ({ token }) => {
  const [reportType, setReportType] = useState('delivery-receipt');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/delivery-receipt?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderDeliveryReceiptReport = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return !isNaN(d) ? d.toLocaleDateString('en-US', dateOptions) : dateStr || "-";
    };

    // ===============================
    // GROUP DATA
    // Branch → Delivery Date → DR No
    // ===============================
    const nestedData = reportData.data.reduce((acc, row) => {
      const branch = row.source_branch_name || "Unknown Branch";
      const deliveryDate = row.delivery_date
        ? new Date(row.delivery_date).toDateString()
        : "Unknown Date";
      const drNo = row.id || "N/A"; // fallback DR number

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][deliveryDate]) acc[branch][deliveryDate] = {};
      if (!acc[branch][deliveryDate][drNo])
        acc[branch][deliveryDate][drNo] = [];

      acc[branch][deliveryDate][drNo].push(row);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Item</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty</th>
                  <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                  <th style={{ ...styles.th, ...alignRight }}>Unit Price</th>
                  <th style={{ ...styles.th, ...alignRight }}>Amount</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch])
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((deliveryDate) => {
                    let dateQty = 0;
                    let dateAmount = 0;

                    return (
                      <React.Fragment key={deliveryDate}>
                        {/* ========== DELIVERY DATE HEADER ========== */}
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <td
                            colSpan="5"
                            style={{
                              padding: '12px 16px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                          >
                            Delivery Date: {formatDate(deliveryDate)}
                          </td>
                        </tr>

                        {Object.entries(nestedData[branch][deliveryDate]).map(
                          ([drNo, rows]) => {
                            const destination =
                              rows[0]?.destination_branch_name || "-";
                            const notes = rows[0]?.notes || "-";

                            return (
                              <React.Fragment key={drNo}>
                                {/* ========== DR HEADER ========== */}
                                <tr>
                                  <td
                                    colSpan="5"
                                    style={{
                                      backgroundColor: '#f0f9ff',
                                      padding: '10px 16px',
                                      fontWeight: 400,
                                      borderBottom: '1px solid #f0f9ff'
                                    }}
                                  >
                                    DR No: <strong>{drNo}</strong> | Destination:{" "}
                                    <strong>{destination}</strong> | Notes: {notes}
                                  </td>
                                </tr>

                                {/* ========== ITEM ROWS ========== */}
                                {rows.map((row) => {
                                  const qty = Number(row.quantity || 0);
                                  const amount =
                                    qty * Number(row.unit_price || 0);

                                  dateQty += qty;
                                  dateAmount += amount;

                                  return (
                                    <tr key={row.batch_id} style={printSafeRow}>
                                      <td
                                        style={{
                                          ...styles.td,
                                          ...alignLeft,
                                          lineHeight: 1.45
                                        }}
                                      >
                                        <div style={{ fontWeight: 600 }}>
                                          {row.inventory_item_id} – {row.item_name}
                                        </div>
                                      </td>

                                      <td style={{ ...styles.td, ...alignRight }}>
                                        {formatCurrency(qty)}
                                      </td>

                                      <td style={{ ...styles.td, ...alignCenter }}>
                                        {row.unit_of_measurement}
                                      </td>

                                      <td style={{ ...styles.td, ...alignRight }}>
                                        ₱{formatCurrency(row.unit_price)}
                                      </td>

                                      <td style={{ ...styles.td, ...alignRight }}>
                                        ₱{formatCurrency(amount)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          }
                        )}

                        {/* ========== DATE TOTAL ========== */}
                        <tr style={{ backgroundColor: '#ecfeff' }}>
                          <td
                            colSpan="2"
                            style={{
                              padding: '12px 16px',
                              fontWeight: 700,
                              textAlign: 'right'
                            }}
                          >
                            DATE TOTAL
                          </td>
                          <td style={{ ...alignRight, fontWeight: 800 }}>
                            {formatCurrency(dateQty)}
                          </td>
                          <td />
                          <td style={{ ...alignRight, fontWeight: 800 }}>
                            ₱{formatCurrency(dateAmount)}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Delivery Receipt Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'delivery-receipt' && renderDeliveryReceiptReport()}
        </div>
      )}
    </div>
  );
};


const AdjustmentReport = ({ token }) => {
  const [reportType, setReportType] = useState('adjustment-report');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/adjustment-report?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderAdjustmentReport = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: 'left' };
    const alignCenter = { textAlign: 'center' };
    const alignRight = { textAlign: 'right', whiteSpace: 'nowrap' };
    const printSafeRow = { pageBreakInside: 'avoid', breakInside: 'avoid' };

    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return !isNaN(d) ? d.toLocaleDateString('en-US', dateOptions) : dateStr || "-";
    };

    // ===============================
    // GROUP DATA
    // Branch → Adjustment Category → Adjustment Date
    // ===============================
    const nestedData = reportData.data.reduce((acc, row) => {
      const branch = row.branch_name || "Unknown Branch";
      const category = row.adjustment_category || "Uncategorized";
      const adjDate = row.adjustment_date
        ? new Date(row.adjustment_date).toDateString()
        : "Unknown Date";

      if (!acc[branch]) acc[branch] = {};
      if (!acc[branch][category]) acc[branch][category] = {};
      if (!acc[branch][category][adjDate]) acc[branch][category][adjDate] = [];

      acc[branch][category][adjDate].push(row);
      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(nestedData).map((branch) => (
          <div
            key={branch}
            style={{
              ...styles.card,
              marginTop: '24px',
              pageBreakAfter: 'always'
            }}
          >
            {/* ================= BRANCH HEADER ================= */}
            <div
              style={{
                marginBottom: '20px',
                padding: '14px 18px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Building style={{ width: 20, height: 20, color: '#1e40af' }} />
              <strong>{branch}</strong>
            </div>

            {/* ================= TABLE ================= */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...alignLeft }}>Item</th>
                  <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                  <th style={{ ...styles.th, ...alignRight }}>Qty</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Details</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Status</th>
                  <th style={{ ...styles.th, ...alignLeft }}>Approved By</th>
                </tr>
              </thead>

              <tbody>
                {Object.keys(nestedData[branch]).map((category) => (
                  <React.Fragment key={category}>
                    {/* ========== CATEGORY HEADER ========== */}
                    <tr style={{ backgroundColor: '#f0f9ff' }}>
                      <td
                        colSpan="6"
                        style={{
                          padding: '12px 16px',
                          fontWeight: 700,
                          borderBottom: '1px solid #bae6fd'
                        }}
                      >
                        Adjustment Category: <strong>{category}</strong>
                      </td>
                    </tr>

                    {Object.keys(nestedData[branch][category])
                      .sort((a, b) => new Date(a) - new Date(b))
                      .map((adjDate) => {
                        let dateQty = 0;

                        return (
                          <React.Fragment key={adjDate}>
                            {/* ========== DATE HEADER ========== */}
                            <tr style={{ backgroundColor: '#f9fafb' }}>
                              <td
                                colSpan="6"
                                style={{
                                  padding: '12px 16px',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  borderBottom: '1px solid #e5e7eb'
                                }}
                              >
                                Adjustment Date: {formatDate(adjDate)}
                              </td>
                            </tr>

                            {/* ========== ITEM ROWS ========== */}
                            {nestedData[branch][category][adjDate].map((row) => {
                              const qty = Number(row.quantity || 0);
                              dateQty += qty;

                              return (
                                <tr key={row.id} style={printSafeRow}>
                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    <strong>{row.inventory_item_id}</strong> –{" "}
                                    {row.item_name}
                                  </td>

                                  <td style={{ ...styles.td, ...alignCenter }}>
                                    {row.unit_of_measurement}
                                  </td>

                                  <td style={{ ...styles.td, ...alignRight }}>
                                    {formatCurrency(qty)}
                                  </td>

                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    <div>
                                      Type:{" "}
                                      <strong>
                                        {row.adjustment_type.toUpperCase()}
                                      </strong>
                                    </div>
                                    {row.reason && (
                                      <div>Reason: {row.reason}</div>
                                    )}
                                  </td>

                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    {row.status}
                                  </td>

                                  <td style={{ ...styles.td, ...alignLeft }}>
                                    {row.approved_by || "-"}
                                  </td>
                                </tr>
                              );
                            })}

                            {/* ========== DATE TOTAL ========== */}
                            <tr style={{ backgroundColor: '#ecfeff' }}>
                              <td
                                colSpan="2"
                                style={{
                                  padding: '12px 16px',
                                  fontWeight: 700,
                                  textAlign: 'right'
                                }}
                              >
                                DATE TOTAL
                              </td>
                              <td style={{ ...alignRight, fontWeight: 800 }}>
                                {formatCurrency(dateQty)}
                              </td>
                              <td colSpan="3" />
                            </tr>
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Adjustment Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'adjustment-report' && renderAdjustmentReport()}
        </div>
      )}
    </div>
  );
};


const PullOutDetailedReport = ({ token }) => {
  const [reportType, setReportType] = useState('pull-out-detailed-report');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/pull-out-detailed-report?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPullOutDetailedReport = () => {
    if (!reportData || !reportData.data) return null;

    // ===============================
    // HELPERS
    // ===============================
    const alignLeft = { textAlign: "left" };
    const alignCenter = { textAlign: "center" };
    const alignRight = { textAlign: "right", whiteSpace: "nowrap" };
    const printSafeRow = { pageBreakInside: "avoid", breakInside: "avoid" };

    // Display DATETIME exactly as stored (no timezone conversion)
    const formatDate = (dateStr) => {
      if (!dateStr) return "-";

      // If backend sends ISO format, clean it safely
      return dateStr
        .replace("T", " ")
        .replace("Z", "")
        .split(".")[0];
    };

    // ===============================
    // GROUP DATA
    // ===============================
    const groupedData = reportData.data.reduce((acc, row) => {
      const branch = row.branch_name || "Unknown Branch";

      if (!acc[branch]) acc[branch] = [];
      acc[branch].push(row);

      return acc;
    }, {});

    // ===============================
    // RENDER
    // ===============================
    return (
      <div>
        {Object.keys(groupedData).map((branch) => {
          let overallTotal = 0;

          return (
            <div
              key={branch}
              style={{
                ...styles.card,
                marginTop: "24px",
                pageBreakAfter: "always",
              }}
            >
              {/* ================= BRANCH HEADER ================= */}
              <div
                style={{
                  marginBottom: "20px",
                  padding: "14px 18px",
                  backgroundColor: "#f0f9ff",
                  borderRadius: "8px",
                  border: "1px solid #bae6fd",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Building style={{ width: 20, height: 20, color: "#1e40af" }} />
                <strong>{branch}</strong>
              </div>

              {/* ================= TABLE ================= */}
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...alignLeft }}>Request Date</th>
                    <th style={{ ...styles.th, ...alignLeft }}>ID</th>
                    <th style={{ ...styles.th, ...alignLeft }}>Item Code</th>
                    <th style={{ ...styles.th, ...alignLeft }}>Item Name</th>
                    <th style={{ ...styles.th, ...alignCenter }}>Unit</th>
                    <th style={{ ...styles.th, ...alignRight }}>Quantity</th>
                    <th style={{ ...styles.th, ...alignLeft }}>Reason</th>
                    <th style={{ ...styles.th, ...alignLeft }}>Requested By</th>
                    <th style={{ ...styles.th, ...alignLeft }}>Approved By</th>
                  </tr>
                </thead>

                <tbody>
                  {groupedData[branch]
                    .sort((a, b) => a.request_date.localeCompare(b.request_date))
                    .map((row) => {
                      const qty = Number(row.quantity || 0);
                      overallTotal += qty;

                      return (
                        <tr key={row.id} style={printSafeRow}>
                          {/* Request Date */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {formatDate(row.request_date)}
                          </td>

                          {/* PR ID */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {`PR-${row.id}`}
                          </td>

                          {/* Item Code */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {row.item_code}
                          </td>

                          {/* Item Name */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {row.item_name}
                          </td>

                          {/* Unit */}
                          <td style={{ ...styles.td, ...alignCenter }}>
                            {row.unit_of_measurement}
                          </td>

                          {/* Quantity */}
                          <td style={{ ...styles.td, ...alignRight }}>
                            {formatCurrency(qty)}
                          </td>

                          {/* Reason */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {row.reason || "-"}
                          </td>

                          {/* Requested By */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {row.requested_by || "-"}
                          </td>

                          {/* Approved By */}
                          <td style={{ ...styles.td, ...alignLeft }}>
                            {row.approved_by || "-"}
                          </td>
                        </tr>
                      );
                    })}

                  {/* ========== OVERALL TOTAL ROW ========== */}
                  <tr style={{ backgroundColor: "#dbeafe" }}>
                    <td
                      colSpan="5"
                      style={{
                        padding: "12px 16px",
                        fontWeight: 800,
                        textAlign: "right",
                      }}
                    >
                      OVERALL TOTAL
                    </td>

                    <td style={{ ...alignRight, fontWeight: 900 }}>
                      {formatCurrency(overallTotal)}
                    </td>

                    <td colSpan="3" />
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Pull Out Detailed Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'pull-out-detailed-report' && renderPullOutDetailedReport()}
        </div>
      )}
    </div>
  );
};

const PullOutSummaryReport = ({ token }) => {
  const [reportType, setReportType] = useState('pull-out-summary-report');
  const [dateFrom, setDateFrom] = useState(getPHTFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getPHTLastDayOfMonth());
  const [branchId, setBranchId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/pull-out-summary-report?dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error generating report');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPullOutSummaryReport = () => {
    if (!reportData || !reportData.data) return null;

    const alignLeft = { textAlign: "left" };
    const alignCenter = { textAlign: "center" };
    const alignRight = { textAlign: "right", whiteSpace: "nowrap" };
    const printSafeRow = { pageBreakInside: "avoid", breakInside: "avoid" };

    // ===============================
    // SAFE DATE FORMATTER (MM/DD/YYYY)
    // ===============================
    const formatDateOnly = (dateStr) => {
      if (!dateStr) return "-";

      const date = new Date(dateStr);
      if (isNaN(date)) return "-";

      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    };

    // ===============================
    // GROUP BY BRANCH
    // ===============================
    const groupedByBranch = reportData.data.reduce((acc, row) => {
      const branch = row.branch_name || "Unknown Branch";
      if (!acc[branch]) acc[branch] = [];
      acc[branch].push(row);
      return acc;
    }, {});

    return (
      <div>
        {Object.keys(groupedByBranch).map((branch) => {
          const branchData = groupedByBranch[branch];

          // ===============================
          // GET UNIQUE DATES (FORMATTED + SORTED)
          // ===============================
          const uniqueDates = [
            ...new Set(
              branchData.map((r) => formatDateOnly(r.request_date))
            ),
          ].sort((a, b) => new Date(a) - new Date(b));

          // ===============================
          // SUMMARIZE BY ITEM
          // ===============================
          const summaryMap = {};

          branchData.forEach((row) => {
            const formattedDate = formatDateOnly(row.request_date);
            const itemKey = row.item_code;

            if (!summaryMap[itemKey]) {
              summaryMap[itemKey] = {
                item_code: row.item_code,
                item_name: row.item_name,
                unit: row.unit_of_measurement,
                totals: {},
                rowTotal: 0,
              };

              // Initialize all date columns to 0
              uniqueDates.forEach((d) => {
                summaryMap[itemKey].totals[d] = 0;
              });
            }

            const qty = Number(row.quantity || 0);

            summaryMap[itemKey].totals[formattedDate] += qty;
            summaryMap[itemKey].rowTotal += qty;
          });

          const summarizedItems = Object.values(summaryMap);

          // ===============================
          // CALCULATE COLUMN TOTALS
          // ===============================
          const columnTotals = {};
          uniqueDates.forEach((d) => (columnTotals[d] = 0));

          let grandTotal = 0;

          summarizedItems.forEach((item) => {
            uniqueDates.forEach((d) => {
              columnTotals[d] += item.totals[d];
            });
            grandTotal += item.rowTotal;
          });

          return (
            <div
              key={branch}
              style={{
                ...styles.card,
                marginTop: "24px",
                pageBreakAfter: "always",
              }}
            >
              {/* ================= BRANCH HEADER ================= */}
              <div
                style={{
                  marginBottom: "20px",
                  padding: "14px 18px",
                  backgroundColor: "#f0f9ff",
                  borderRadius: "8px",
                  border: "1px solid #bae6fd",
                }}
              >
                <strong>{branch}</strong>
              </div>

              {/* ================= TABLE ================= */}
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...alignLeft }}>Item Code</th>
                    <th style={{ ...styles.th, ...alignLeft }}>Item Name</th>
                    <th style={{ ...styles.th, ...alignCenter }}>Unit</th>

                    {uniqueDates.map((date) => (
                      <th key={date} style={{ ...styles.th, ...alignRight }}>
                        {date}
                      </th>
                    ))}

                    <th style={{ ...styles.th, ...alignRight }}>Row Total</th>
                  </tr>
                </thead>

                <tbody>
                  {summarizedItems.map((item) => (
                    <tr key={item.item_code} style={printSafeRow}>
                      <td style={{ ...styles.td, ...alignLeft }}>
                        {item.item_code}
                      </td>

                      <td style={{ ...styles.td, ...alignLeft }}>
                        {item.item_name}
                      </td>

                      <td style={{ ...styles.td, ...alignCenter }}>
                        {item.unit}
                      </td>

                      {uniqueDates.map((date) => (
                        <td
                          key={date}
                          style={{ ...styles.td, ...alignRight }}
                        >
                          {formatCurrency(item.totals[date])}
                        </td>
                      ))}

                      <td style={{ ...styles.td, ...alignRight }}>
                        {formatCurrency(item.rowTotal)}
                      </td>
                    </tr>
                  ))}

                  {/* ================= TOTAL ROW ================= */}
                  <tr style={{ backgroundColor: "#dbeafe", fontWeight: 800 }}>
                    <td colSpan="3" style={{ textAlign: "right" }}>
                      COLUMN TOTAL
                    </td>

                    {uniqueDates.map((date) => (
                      <td key={date} style={{ ...alignRight }}>
                        {formatCurrency(columnTotals[date])}
                      </td>
                    ))}

                    <td style={{ ...alignRight }}>
                      {formatCurrency(grandTotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Pull Out Summary Report</h1>
      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} />
          </div>
          <button onClick={generateReport} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download style={{ width: '20px', height: '20px' }} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && (
        <div style={{ marginTop: '24px' }}>
          {reportType === 'pull-out-summary-report' && renderPullOutSummaryReport()}
        </div>
      )}
    </div>
  );
};


const ChequeMonitoring = ({ token }) => {
  const [cheques, setCheques] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    cheque_number: '',
    status: 'unused',
    issued_to: '',
    amount: 0,
    cheque_date: '',
    signatory: '',
    notes: ''
  });

  useEffect(() => { fetchCheques(); }, []);

  const fetchCheques = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/cheques`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCheques(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/cheques/${editingId}` : `${API_URL}/cheques`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchCheques();
        setShowForm(false);
        setEditingId(null);
        setForm({ cheque_number: '', status: 'unused', issued_to: '', amount: 0, cheque_date: '', signatory: '', notes: '' });
        alert('Cheque saved successfully');
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error saving cheque');
      }
    }
  };

  const handleEdit = (cheque) => {
    setForm({
      cheque_number: cheque.cheque_number,
      status: cheque.status,
      issued_to: cheque.issued_to || '',
      amount: cheque.amount || 0,
      cheque_date: cheque.cheque_date || '',
      signatory: cheque.signatory || '',
      notes: cheque.notes || ''
    });
    setEditingId(cheque.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cheque?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/cheques/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCheques();
        alert('Cheque deleted successfully');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Cheque Monitoring</h1>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ cheque_number: '', status: 'unused', issued_to: '', amount: 0, cheque_date: '', signatory: '' }); }} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Cheque
        </button>
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>{editingId ? 'Edit' : 'Add'} Cheque</h2>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Cheque Number *</label>
              <input type="text" value={form.cheque_number} onChange={(e) => setForm({ ...form, cheque_number: e.target.value })} style={styles.input} placeholder="e.g., CHQ-001" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={styles.input}>
                <option value="unused">Unused</option>
                <option value="used">Used</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Issued To</label>
              <input type="text" value={form.issued_to} onChange={(e) => setForm({ ...form, issued_to: e.target.value })} style={styles.input} placeholder="Payee name" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Amount</label>
              <input type="number" min="0" step="0.01" value={form.amount || ''} placeholder="0.00" onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Cheque Date</label>
              <input type="date" value={form.cheque_date} onChange={(e) => setForm({ ...form, cheque_date: e.target.value })} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Signatory</label>
              <input type="text" value={form.signatory} onChange={(e) => setForm({ ...form, signatory: e.target.value })} style={styles.input} placeholder="Authorized signatory" />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Additional notes or instructions"
              style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSubmit} style={styles.button}>{editingId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditingId(null); setForm({ cheque_number: '', status: 'unused', issued_to: '', amount: 0, cheque_date: '', signatory: '' }); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ padding: '12px 20px', backgroundColor: '#d1fae5', borderRadius: '8px' }}>
            <span style={{ fontSize: '14px', color: '#065f46', fontWeight: '600' }}>
              Unused: {cheques.filter(c => c.status === 'unused').length}
            </span>
          </div>
          <div style={{ padding: '12px 20px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
            <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>
              Used: {cheques.filter(c => c.status === 'used').length}
            </span>
          </div>
          <div style={{ padding: '12px 20px', backgroundColor: '#dbeafe', borderRadius: '8px' }}>
            <span style={{ fontSize: '14px', color: '#1e40af', fontWeight: '600' }}>
              Total: {cheques.length}
            </span>
          </div>
        </div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Cheque #</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Issued To</th>
            <th style={styles.th}>Amount</th>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Signatory</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {cheques.map(cheque => (
            <tr key={cheque.id}>
              <td style={{ ...styles.td, fontWeight: '500' }}>{cheque.cheque_number}</td>
              <td style={styles.td}>
                <span style={{ ...styles.badge, ...(cheque.status === 'unused' ? styles.badgeGreen : styles.badgeYellow) }}>
                  {cheque.status}
                </span>
              </td>
              <td style={styles.td}>{cheque.issued_to || '-'}</td>
              <td style={{ ...styles.td, fontWeight: '600' }}>₱{
                (parseFloat(cheque.amount) || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })
              }</td>
              <td style={styles.td}>{cheque.cheque_date ? new Date(cheque.cheque_date).toLocaleDateString('en-US', dateOptions) : '-'}</td>
              <td style={styles.td}>{cheque.signatory || '-'}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                <button onClick={() => handleEdit(cheque)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                  <Edit style={{ width: '16px', height: '16px' }} />
                </button>
                <button onClick={() => handleDelete(cheque.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                  <Trash2 style={{ width: '16px', height: '16px' }} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  );
};


const ChartOfAccounts = ({ token }) => {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    parent_account_id: '',
    is_active: 1,
    is_bank: 0,
    description: ''
  });
  const [filterType, setFilterType] = useState('');

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/chart_of_accounts`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAccounts(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingId ? `${API_URL}/chart_of_accounts/${editingId}` : `${API_URL}/chart_of_accounts`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchAccounts();
        setShowForm(false);
        setEditingId(null);
        setForm({ account_code: '', account_name: '', account_type: 'asset', parent_account_id: '', is_active: 1, is_bank: 0, description: '' });
        alert('Account saved successfully');
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error saving account');
      }
    }
  };

  const handleEdit = (account) => {
    setForm({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      parent_account_id: account.parent_account_id || '',
      is_active: account.is_active,
      is_bank: account.is_bank || 0,
      description: account.description || ''
    });
    setEditingId(account.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account? This cannot be undone.')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/chart_of_accounts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchAccounts();
        alert('Account deleted successfully');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting account. It may be in use.');
    }
  };

  const accountTypes = [
    { value: 'asset', label: 'Asset', color: '#3b82f6' },
    { value: 'liability', label: 'Liability', color: '#f59e0b' },
    { value: 'equity', label: 'Equity', color: '#10b981' },
    { value: 'revenue', label: 'Revenue', color: '#8b5cf6' },
    { value: 'expense', label: 'Expense', color: '#ef4444' }
  ];

  const filteredAccounts = filterType ? accounts.filter(a => a.account_type === filterType) : accounts;

  const accountTypeStats = accountTypes.map(type => ({
    ...type,
    count: accounts.filter(a => a.account_type === type.value).length
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Chart of Accounts</h1>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ account_code: '', account_name: '', account_type: 'asset', parent_account_id: '', is_active: 1, description: '' }); }} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Account
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {accountTypeStats.map(type => (
          <div
            key={type.value}
            onClick={() => setFilterType(filterType === type.value ? '' : type.value)}
            style={{
              padding: '16px',
              backgroundColor: filterType === type.value ? type.color : 'white',
              color: filterType === type.value ? 'white' : '#1f2937',
              borderRadius: '8px',
              border: `2px solid ${type.color}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{type.count}</div>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{type.label}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>{editingId ? 'Edit' : 'Add'} Account</h2>
          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Account Code *</label>
              <input
                type="text"
                value={form.account_code}
                onChange={(e) => setForm({ ...form, account_code: e.target.value })}
                style={styles.input}
                placeholder="e.g., 1010, 2020"
                disabled={editingId !== null}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Account Name *</label>
              <input type="text" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} style={styles.input} placeholder="e.g., Cash in Bank" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Account Type *</label>
              <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} style={styles.input}>
                {accountTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Parent Account (Optional)</label>
              <select value={form.parent_account_id} onChange={(e) => setForm({ ...form, parent_account_id: e.target.value })} style={styles.input}>
                <option value="">None (Top Level)</option>
                {accounts.filter(a => a.id !== editingId).map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <div style={toggleStyles.toggleContainer}>
                <label style={toggleStyles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={form.is_active === 1}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                    style={toggleStyles.toggleInput}
                  />
                  <span style={toggleStyles.toggleSlider(form.is_active === 1)}>
                    <span style={toggleStyles.toggleSliderBefore(form.is_active === 1)}></span>
                  </span>
                </label>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  {form.is_active === 1 ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Bank Account</label>
              <div style={toggleStyles.toggleContainer}>
                <label style={toggleStyles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={form.is_bank === 1}
                    onChange={(e) => setForm({ ...form, is_bank: e.target.checked ? 1 : 0 })}
                    style={toggleStyles.toggleInput}
                  />
                  <span style={toggleStyles.toggleSlider(form.is_bank === 1)}>
                    <span style={toggleStyles.toggleSliderBefore(form.is_bank === 1)}></span>
                  </span>
                </label>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  {form.is_bank === 1 ? 'Yes' : 'No'}
                </span>
              </div>
              <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                Mark this account as a bank account for reconciliation
              </span>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...styles.input, minHeight: '60px' }} placeholder="Optional notes about this account..." />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSubmit} style={styles.button}>{editingId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditingId(null); setForm({ account_code: '', account_name: '', account_type: 'asset', parent_account_id: '', is_active: 1, description: '' }); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          </div>
        </div>
      )}

      {filterType && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
            Showing: {accountTypes.find(t => t.value === filterType)?.label} accounts ({filteredAccounts.length})
          </span>
          <button onClick={() => setFilterType('')} style={{ ...styles.button, padding: '6px 12px', fontSize: '13px' }}>
            Show All
          </button>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Code</th>
            <th style={styles.th}>Account Name</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Parent Account</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Bank</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAccounts.map(account => {
            const typeInfo = accountTypes.find(t => t.value === account.account_type);
            const parentAccount = accounts.find(a => a.id === account.parent_account_id);
            return (
              <tr key={account.id}>
                <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>{account.account_code}</td>
                <td style={{ ...styles.td, fontWeight: '500' }}>
                  {account.parent_account_id && <span style={{ color: '#6b7280', marginRight: '8px' }}>└─</span>}
                  {account.account_name}
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: typeInfo?.color + '20', color: typeInfo?.color, border: `1px solid ${typeInfo?.color}` }}>
                    {typeInfo?.label}
                  </span>
                </td>
                <td style={styles.td}>
                  {parentAccount ? (
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>
                      {parentAccount.account_code} - {parentAccount.account_name}
                    </span>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, ...(account.is_active ? styles.badgeGreen : styles.badgeRed) }}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  {account.is_bank === 1 ? (
                    <span style={{ ...styles.badge, ...styles.badgeBlue }}>
                      Bank
                    </span>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button onClick={() => handleEdit(account)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button onClick={() => handleDelete(account.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                    <Trash2 style={{ width: '16px', height: '16px' }} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>


      {filteredAccounts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
          <p style={{ margin: 0 }}>No accounts found. Click "Add Account" to create one.</p>
        </div>
      )}
    </div>
  );
};


const RoleManagement = ({ token }) => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  useEffect(() => { fetchRoles(); }, []);

  const fetchRoles = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/roles`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRoles(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchPermissions = async (roleId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/roles/${roleId}/permissions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setPermissions(data);
      setSelectedRole(roleId);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleAddRole = async () => {
    try {
      await fetchWithAuth(`${API_URL}/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newRoleName, description: '' })
      });
      fetchRoles();
      setShowAddRole(false);
      setNewRoleName('');
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await fetchWithAuth(`${API_URL}/roles/${selectedRole}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ permissions })
      });
      alert('Permissions updated successfully');
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const togglePermission = (index, field) => {
    const updated = [...permissions];
    updated[index][field] = updated[index][field] ? 0 : 1;
    setPermissions(updated);
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>Role Management</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600' }}>Roles</h3>
            <button onClick={() => setShowAddRole(!showAddRole)} style={{ ...styles.button, padding: '6px 12px', fontSize: '13px' }}>
              <Plus style={{ width: '16px', height: '16px' }} />
            </button>
          </div>

          {showAddRole && (
            <div style={{ ...styles.card, marginBottom: '16px', padding: '12px' }}>
              <input
                type="text"
                placeholder="Role name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                style={{ ...styles.input, marginBottom: '8px' }}
              />
              <button onClick={handleAddRole} style={{ ...styles.button, width: '100%', justifyContent: 'center', padding: '8px' }}>
                Add Role
              </button>
            </div>
          )}

          <div style={styles.card}>
            {roles.map(role => (
              <div
                key={role.id}
                onClick={() => fetchPermissions(role.id)}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  backgroundColor: selectedRole === role.id ? '#eef2ff' : 'transparent',
                  borderRadius: '6px',
                  marginBottom: '4px',
                  fontWeight: selectedRole === role.id ? '600' : '400'
                }}
              >
                {role.name}
              </div>
            ))}
          </div>
        </div>

        <div>
          {selectedRole && permissions.length > 0 && (
            <div style={styles.card}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Permissions</h3>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Module</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Create</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Read</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Update</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Approve</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((perm, idx) => (
                    <tr key={idx}>
                      <td style={{ ...styles.td, fontWeight: '500' }}>{perm.module}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input type="checkbox" checked={perm.can_create} onChange={() => togglePermission(idx, 'can_create')} />
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input type="checkbox" checked={perm.can_read} onChange={() => togglePermission(idx, 'can_read')} />
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input type="checkbox" checked={perm.can_update} onChange={() => togglePermission(idx, 'can_update')} />
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input type="checkbox" checked={perm.can_approve} onChange={() => togglePermission(idx, 'can_approve')} />
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input type="checkbox" checked={perm.can_delete} onChange={() => togglePermission(idx, 'can_delete')} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={handleUpdatePermissions} style={{ ...styles.button, marginTop: '16px' }}>
                Save Permissions
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const JournalVouchers = ({ token }) => {
  const [vouchers, setVouchers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewingVoucher, setViewingVoucher] = useState(null);
  const [voucherItems, setVoucherItems] = useState([]);
  const [form, setForm] = useState({
    voucher_date: new Date().toLocaleDateString('en-CA'),
    items: [
      { account_id: '', debit: 0, credit: 0 },
      { account_id: '', debit: 0, credit: 0 }
    ]
  });

  useEffect(() => {
    fetchVouchers();
    fetchAccounts();
  }, []);

  const fetchVouchers = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/journal_vouchers`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setVouchers(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/chart_of_accounts`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAccounts(data.filter(a => a.is_active));
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { account_id: '', debit: 0, credit: 0 }]
    });
  };

  const removeItem = (idx) => {
    if (form.items.length <= 2) {
      alert('Journal entry must have at least 2 lines (one debit and one credit)');
      return;
    }
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== idx)
    });
  };

  const updateItem = (idx, field, value) => {
    const updated = [...form.items];
    updated[idx][field] = value;

    // If changing debit, clear credit and vice versa
    if (field === 'debit' && value > 0) {
      updated[idx].credit = 0;
    } else if (field === 'credit' && value > 0) {
      updated[idx].debit = 0;
    }

    setForm({ ...form, items: updated });
  };

  const getTotals = () => {
    const totalDebit = form.items.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0);
    const totalCredit = form.items.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0);
    const balanced = Math.abs(totalDebit - totalCredit) < 0.01;
    return { totalDebit, totalCredit, balanced };
  };

  const handleSubmit = async () => {
    const { totalDebit, totalCredit, balanced } = getTotals();

    if (!balanced) {
      alert(`Entry is not balanced!\nTotal Debit: ₱{formatCurrency(totalDebit)}\nTotal Credit: ₱{formatCurrency(totalCredit)}\nDifference: ₱{formatCurrency(Math.abs(totalDebit - totalCredit))}`);
      return;
    }

    if (totalDebit === 0 || totalCredit === 0) {
      alert('Both debit and credit must have amounts');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/journal_vouchers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        alert('Journal voucher created successfully!');
        fetchVouchers();
        setShowForm(false);
        setForm({
          voucher_date: new Date().toLocaleDateString('en-CA'),
          description: '',
          items: [
            { account_id: '', debit: 0, credit: 0 },
            { account_id: '', debit: 0, credit: 0 }
          ]
        });
      } else {
        const data = await res.json();
        alert('Error: ' + (data.error || 'Failed to create voucher'));
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.message !== 'Session expired. Please login again.') {
        alert('Error creating voucher');
      }
    }
  };

  const viewVoucher = async (voucherId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/journal_vouchers/${voucherId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingVoucher(data.voucher);
      setVoucherItems(data.items);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading voucher details');
    }
  };

  const deleteVoucher = async (id) => {
    if (!window.confirm('Delete this journal voucher? This cannot be undone.')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/journal_vouchers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Journal voucher deleted successfully');
        fetchVouchers();
        if (viewingVoucher?.id === id) {
          setViewingVoucher(null);
          setVoucherItems([]);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting voucher');
    }
  };

  const { totalDebit, totalCredit, balanced } = getTotals();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Journal Vouchers</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Create Journal Entry
        </button>
      </div>

      {showForm && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>Create Journal Voucher</h2>

          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>📒 Double-Entry Accounting:</p>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#3b82f6' }}>
              <li>Every transaction must have equal debits and credits</li>
              <li>Use Debit for increases in Assets or Expenses, decreases in Liabilities/Equity/Revenue</li>
              <li>Use Credit for increases in Liabilities/Equity/Revenue, decreases in Assets or Expenses</li>
            </ul>
          </div>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date *</label>
              <input
                type="date"
                value={form.voucher_date}
                onChange={(e) => setForm({ ...form, voucher_date: e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description *</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                style={styles.input}
                placeholder="e.g., Purchase of office supplies"
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Journal Entries</h3>
              <button onClick={addItem} style={{ ...styles.button, ...styles.buttonSuccess, fontSize: '13px', padding: '6px 12px' }}>
                <Plus style={{ width: '16px', height: '16px' }} /> Add Line
              </button>
            </div>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '30%' }}>Account</th>
                  <th style={{ ...styles.th, width: '30%', textAlign: 'right' }}>Debit</th>
                  <th style={{ ...styles.th, width: '30%', textAlign: 'right' }}>Credit</th>
                  <th style={{ ...styles.th, width: '10%', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>
                      <select
                        value={item.account_id}
                        onChange={(e) => updateItem(idx, 'account_id', e.target.value)}
                        style={{ ...styles.input, padding: '6px 8px', fontSize: '13px' }}
                      >
                        <option value="">Select Account</option>
                        {accounts.map(acc => (
                          <option key={acc.id} value={acc.id}>
                            {acc.account_code} - {acc.account_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.debit || ''}
                        placeholder="0.00"
                        onChange={(e) => updateItem(idx, 'debit', parseFloat(e.target.value) || 0)}
                        style={{ ...styles.input, textAlign: 'right' }}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.credit || ''}
                        placeholder="0.00"
                        onChange={(e) => updateItem(idx, 'credit', parseFloat(e.target.value) || 0)}
                        style={{ ...styles.input, textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <button
                        onClick={() => removeItem(idx)}
                        style={{ ...styles.iconButton, color: '#ef4444' }}
                        disabled={form.items.length <= 2}
                      >
                        <Trash2 style={{ width: '16px', height: '16px' }} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: balanced ? '#d1fae5' : '#fee2e2' }}>
                  <td style={{ ...styles.td, fontWeight: 'bold', textAlign: 'right' }}>
                    {balanced ? '✓ BALANCED' : '⚠️ NOT BALANCED'}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 'bold', textAlign: 'right', color: balanced ? '#065f46' : '#991b1b' }}>
                    ₱{formatCurrency(totalDebit)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 'bold', textAlign: 'right', color: balanced ? '#065f46' : '#991b1b' }}>
                    ₱{formatCurrency(totalCredit)}
                  </td>
                  <td style={styles.td}></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleSubmit} style={styles.button} disabled={!balanced}>
              {balanced ? 'Create Voucher' : 'Balance Entry First'}
            </button>
            <button onClick={() => { setShowForm(false); setForm({ voucher_date: new Date().toLocaleDateString('en-CA'), description: '', items: [{ account_id: '', debit: 0, credit: 0, description: '' }, { account_id: '', debit: 0, credit: 0, description: '' }] }); }} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: viewingVoucher ? '1fr 1.5fr' : '1fr', gap: '24px' }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Journal Vouchers List</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>JV #</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map(voucher => (
                <tr key={voucher.id} style={{ backgroundColor: viewingVoucher?.id === voucher.id ? '#eef2ff' : 'transparent' }}>
                  <td style={{ ...styles.td, fontWeight: '500' }}>JV-{voucher.id}</td>
                  <td style={styles.td}>{new Date(voucher.voucher_date).toLocaleDateString('en-US', dateOptions)}</td>
                  <td style={styles.td}>{voucher.description}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(voucher.status === 'pending' ? styles.badgeYellow : styles.badgeGreen) }}>
                      {voucher.status}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button onClick={() => viewVoucher(voucher.id)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                      <Eye style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button onClick={() => deleteVoucher(voucher.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {viewingVoucher && (
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 4px 0' }}>JV-{viewingVoucher.id}</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                  {new Date(viewingVoucher.voucher_date).toLocaleDateString('en-US', dateOptions)} • {viewingVoucher.description}
                </p>
              </div>
              <button onClick={() => { setViewingVoucher(null); setVoucherItems([]); }} style={{ ...styles.button, ...styles.buttonSecondary }}>
                <X style={{ width: '16px', height: '16px' }} /> Close
              </button>
            </div>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Account</th>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Debit</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {voucherItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ ...styles.td, fontWeight: '500' }}>{item.account_name}</td>
                    <td style={styles.td}>{item.description || '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>
                      {item.debit > 0 ? `₱${formatCurrency(item.debit)}` : '—'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>
                      {item.credit > 0 ? `₱${formatCurrency(item.credit)}` : '—'}
                    </td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <td colSpan={2} style={{ ...styles.td, fontWeight: 'bold', textAlign: 'right' }}>TOTALS</td>
                  <td style={{ ...styles.td, fontWeight: 'bold', textAlign: 'right', color: '#065f46' }}>
                    ₱{formatCurrency(voucherItems.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0))}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 'bold', textAlign: 'right', color: '#065f46' }}>
                    ₱{formatCurrency(voucherItems.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


const DailyEndingInventory = ({ token }) => {
  const [inventories, setInventories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]); // branch sub-categories
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [activeTab, setActiveTab] = useState('');
  const [inventoryDate, setInventoryDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [floorBalances, setFloorBalances] = useState({}); // { inventory_item_id: balance }
  const [remarks, setRemarks] = useState('');
  const [googleDriveLink, setGoogleDriveLink] = useState('');
  const [photo, setPhoto] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingInventoryId, setEditingInventoryId] = useState(null);
  const { hasPermission } = usePermissions();
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchInventories();
    fetchBranches();
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      // Only disable if the user is currently focused on a number input
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };

    window.addEventListener('wheel', handleWheel);

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const fetchInventories = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/daily-ending-inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInventories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching inventories:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
      // setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchInventoryItems = async (branchId) => {
    try {
      setLoading(true);
      const [invRes, itemRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/inventory?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetchWithAuth(`${API_URL}/items`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const invData = await invRes.json();
      const itemData = await itemRes.json();

      // Filter sub-categories (branch categories)
      const subCats = itemData.filter(i => i.item_type === 'category-br');
      setAllSubCategories(subCats);

      // Map inventory items with their branch category info
      const itemsWithCat = invData.map(invItem => {
        const itemInfo = itemData.find(i => i.id === invItem.item_id);
        return {
          ...invItem,
          branch_parent_item_id: itemInfo ? itemInfo.branch_parent_item_id : null
        };
      });

      setInventoryItems(itemsWithCat);

      // Set initial active tab
      const availableSubCatIds = [...new Set(itemsWithCat.map(i => i.branch_parent_item_id).filter(id => id))];
      if (availableSubCatIds.length > 0) {
        setActiveTab(availableSubCatIds[0].toString());
      } else {
        setActiveTab('uncategorized');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching inventory items:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      fetchInventoryItems(selectedBranch);
    } else {
      setInventoryItems([]);
      setAllSubCategories([]);
    }
  }, [selectedBranch]);

  const handleBalanceChange = (id, value) => {
    setFloorBalances(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedBranch) {
      alert('Please select a branch');
      return;
    }

    const itemsToSubmit = Object.entries(floorBalances).map(([id, balance]) => ({
      inventory_item_id: parseInt(id),
      floor_ending_balance: parseFloat(balance) || 0
    }));

    if (itemsToSubmit.length === 0) {
      alert('Please input at least one balance');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('branch_id', selectedBranch);
      formData.append('inventory_date', inventoryDate);
      formData.append('items', JSON.stringify(itemsToSubmit));
      formData.append('remarks', remarks);
      formData.append('google_drive_link', googleDriveLink);
      if (photo) {
        formData.append('photo', photo);
      }

      const res = await fetchWithAuth(`${API_URL}/daily-ending-inventory${editingInventoryId ? `/${editingInventoryId}` : ''}`, {
        method: editingInventoryId ? 'PUT' : 'POST',
        headers: {
          ...(editingInventoryId ? { 'Content-Type': 'application/json' } : {}),
          Authorization: `Bearer ${token}`
        },
        body: editingInventoryId ? JSON.stringify({
          inventory_date: inventoryDate,
          remarks,
          google_drive_link: googleDriveLink,
          items: itemsToSubmit
        }) : formData
      });

      if (res.ok) {
        alert(editingInventoryId ? 'Daily inventory updated successfully' : 'Daily inventory submitted successfully');
        setShowForm(false);
        setEditingInventoryId(null);
        setFloorBalances({});
        setRemarks('');
        setGoogleDriveLink('');
        setPhoto(null);
        fetchInventories();
      } else {
        const error = await res.json();
        alert(error.error || 'Error submitting inventory');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error submitting inventory:', err);
      alert('Submission failed');
      setLoading(false);
    }
  };

  const handleEdit = async (inv) => {
    try {
      setLoading(true);
      // Fetch full details to get all items
      const res = await fetchWithAuth(`${API_URL}/daily-ending-inventory/${inv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      setEditingInventoryId(data.id);
      setSelectedBranch(data.branch_id.toString());
      setInventoryDate(data.inventory_date);
      setRemarks(data.remarks || '');
      setGoogleDriveLink(data.google_drive_link || '');

      // Populate balances
      const balances = {};
      data.items.forEach(item => {
        balances[item.inventory_item_id] = item.floor_ending_balance;
      });
      setFloorBalances(balances);

      setShowForm(true);
      setLoading(false);
    } catch (err) {
      console.error('Error starting edit:', err);
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`${API_URL}/daily-ending-inventory/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedInventory(data);
      setShowDetailsModal(true);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching details:', err);
      setLoading(false);
    }
  };

  // Removed separate handleSaveEdit as we reuse create form

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to confirm this inventory?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/daily-ending-inventory/${id}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Approved successfully');
        fetchInventories();
      } else {
        const err = await res.json();
        alert(err.error || 'Approval failed');
      }
    } catch (err) {
      console.error('Error approving:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record? This cannot be undone.')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/daily-ending-inventory/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Deleted successfully');
        fetchInventories();
      } else {
        const err = await res.json();
        alert(err.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  // Group items by branch category
  const groupedItems = inventoryItems.reduce((acc, item) => {
    const catId = item.branch_parent_item_id ? item.branch_parent_item_id.toString() : 'uncategorized';
    if (!acc[catId]) acc[catId] = [];
    acc[catId].push(item);
    return acc;
  }, {});

  const currentTabItems = groupedItems[activeTab] || [];

  return (
    <div>
      <style>
        {`
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}
      </style>
      <div style={styles.header}>
        <h1 style={styles.title}>Daily Ending Inventory</h1>
        {canCreate('daily_ending_inventory') && (
          <button
            onClick={() => {
              if (showForm) {
                setEditingInventoryId(null);
                setFloorBalances({});
                setRemarks('');
                setGoogleDriveLink('');
                setPhoto(null);
              }
              setShowForm(!showForm);
            }}
            style={styles.button}
          >
            <Plus style={{ width: '20px', height: '20px' }} />
            {showForm ? 'View List' : 'Submit Inventory Balance'}
          </button>
        )}
      </div>

      {showForm ? (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#4f46e5' }}>
              {editingInventoryId ? `Editing Inventory #${editingInventoryId}` : 'New Inventory Submission'}
            </h2>
            {editingInventoryId && (
              <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                EDIT MODE
              </span>
            )}
          </div>
          <div style={{ ...styles.grid2, marginBottom: '24px' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Branch *</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                style={styles.input}
                disabled={!!editingInventoryId}
              >
                <option value="">Select Branch</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Inventory Date *</label>
              <input
                type="date"
                value={inventoryDate}
                onChange={(e) => setInventoryDate(e.target.value)}
                style={styles.input}
              />
            </div>
            {/* <div style={styles.formGroup}>
              <label style={styles.label}>End of Day Photo (Optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhoto(e.target.files[0])}
                style={styles.input}
              />
            </div> */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Google Drive Link (Optional)</label>
              <input
                type="text"
                value={googleDriveLink}
                onChange={(e) => setGoogleDriveLink(e.target.value)}
                placeholder="Paste google drive link here"
                style={styles.input}
              />
            </div>
          </div>

          <div style={{ ...styles.formGroup, marginBottom: '24px' }}>
            <label style={styles.label}>Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add notes here"
              style={{ ...styles.input, minHeight: '80px', paddingTop: '10px' }}
            />
          </div>

          {!selectedBranch && (
            <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <AlertCircle style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
              <p>Please select a branch to load inventory items</p>
            </div>
          )}

          {selectedBranch && (
            <>
              {/* Category Tabs */}
              <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #e5e7eb', marginBottom: '24px', gap: '8px', paddingBottom: '4px' }}>
                {Object.keys(groupedItems).sort((a, b) => a === 'uncategorized' ? 1 : b === 'uncategorized' ? -1 : 0).map(catId => {
                  const cat = allSubCategories.find(c => c.id.toString() === catId);
                  const label = cat ? `${cat.item_code} - ${cat.item_name}` : 'Uncategorized';
                  const isActive = activeTab === catId;
                  return (
                    <button
                      key={catId}
                      onClick={() => setActiveTab(catId)}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderBottom: isActive ? '2px solid #4f46e5' : '2px solid transparent',
                        backgroundColor: isActive ? '#eff6ff' : 'transparent',
                        color: isActive ? '#4f46e5' : '#6b7280',
                        fontWeight: isActive ? '600' : '500',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        borderRadius: '4px 4px 0 0',
                        transition: 'all 0.2s'
                      }}
                    >
                      {label} ({groupedItems[catId].length})
                    </button>
                  );
                })}
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '48px' }}>
                  <RefreshCw style={{ width: '32px', height: '32px', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
                  <p>Loading items...</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Item Code</th>
                        <th style={styles.th}>Item Name</th>
                        <th style={styles.th}>Current System Stock</th>
                        <th style={styles.th}>Unit</th>
                        <th style={{ ...styles.th, width: '200px' }}>Floor Ending Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentTabItems.map(item => (
                        <tr key={item.id}>
                          <td style={{ ...styles.td, fontFamily: 'monospace' }}>{item.item_code}</td>
                          <td style={styles.td}>{item.item_name}</td>
                          <td style={styles.td}>
                            <span style={{ fontWeight: '600', color: item.quantity_on_hand < 0 ? '#ef4444' : '#10b981' }}>
                              {item.quantity_on_hand}
                            </span>
                          </td>
                          <td style={styles.td}>{item.unit_of_measurement}</td>
                          <td style={styles.td}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={floorBalances[item.id] || ''}
                              placeholder="0.00"
                              onChange={(e) => handleBalanceChange(item.id, e.target.value)}
                              style={{ ...styles.input, textAlign: 'right' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {currentTabItems.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                      No items in this category
                    </div>
                  )}

                  <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setEditingInventoryId(null);
                        setFloorBalances({});
                        setRemarks('');
                        setGoogleDriveLink('');
                        setPhoto(null);
                      }}
                      style={{ ...styles.button, ...styles.buttonSecondary }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading || inventoryItems.length === 0}
                      style={{ ...styles.button, opacity: (loading || inventoryItems.length === 0) ? 0.7 : 1 }}
                    >
                      {loading ? 'Processing...' : (editingInventoryId ? 'Update Inventory' : 'Submit Inventory')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Submitted By</th>
                <th style={styles.th}>Approved By</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventories.map(inv => (
                <tr key={inv.id}>
                  <td style={{ ...styles.td, fontWeight: '600' }}>
                    {new Date(inv.inventory_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </td>
                  <td style={styles.td}>{inv.branch_name}</td>
                  <td style={styles.td}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: inv.status === 'approved' ? '#d1fae5' : '#fef3c7',
                      color: inv.status === 'approved' ? '#065f46' : '#92400e',
                      textTransform: 'capitalize'
                    }}>
                      {inv.status === 'approved'
                        ? 'Confirmed'
                        : inv.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '14px' }}>{inv.submitted_by_name}</div>
                    {/* <div style={{ fontSize: '12px', color: '#6b7280' }}>{new Date(inv.created_at).toLocaleString()}</div> */}
                  </td>
                  <td style={styles.td}>{inv.approved_by_name || '-'}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleViewDetails(inv.id)}
                        style={{ ...styles.iconButton, color: '#4f46e5' }}
                        title="View Details"
                      >
                        <Eye style={{ width: '18px', height: '18px' }} />
                      </button>

                      {inv.status === 'pending' && (
                        <>
                          {canCreate('daily_ending_inventory') && (
                            <button
                              onClick={() => handleEdit(inv)}
                              style={{ ...styles.iconButton, color: '#f59e0b' }}
                              title="Edit"
                            >
                              <Edit style={{ width: '18px', height: '18px' }} />
                            </button>
                          )}

                          {canApprove('daily_ending_inventory') && (
                            <button
                              onClick={() => handleApprove(inv.id)}
                              style={{ ...styles.iconButton, color: '#10b981' }}
                              title="Approve"
                            >
                              <Check style={{ width: '18px', height: '18px' }} />
                            </button>
                          )}

                          {canDelete('daily_ending_inventory') && (
                            <button
                              onClick={() => handleDelete(inv.id)}
                              style={{ ...styles.iconButton, color: '#ef4444' }}
                              title="Delete"
                            >
                              <Trash2 style={{ width: '18px', height: '18px' }} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {inventories.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
              <Scale style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
              <p>No daily inventory submissions yet</p>
            </div>
          )}
        </div>
      )}

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); }}
        title={`Inventory Details - ${selectedInventory?.branch_name} (${selectedInventory ? new Date(selectedInventory.inventory_date).toLocaleDateString() : ''})`}
      >
        {selectedInventory && (
          <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ ...styles.grid2, marginBottom: '24px' }}>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>Submitted By</p>
                <p style={{ margin: 0, fontWeight: '600' }}>{selectedInventory.submitted_by_name}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>Status</p>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: selectedInventory.status === 'approved' ? '#d1fae5' : '#fef3c7',
                  color: selectedInventory.status === 'approved' ? '#065f46' : '#92400e'
                }}>
                  {selectedInventory.status === 'approved'
                    ? 'CONFIRMED'
                    : selectedInventory.status.toUpperCase()}
                </span>
              </div>
              {selectedInventory.status === 'approved' && (
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>Approved By</p>
                  <p style={{ margin: 0, fontWeight: '600' }}>{selectedInventory.approved_by_name || 'System'}</p>
                </div>
              )}
              {selectedInventory.google_drive_link && (
                <div style={{ wordBreak: 'break-all' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>Evidence/Link</p>
                  <a href={selectedInventory.google_drive_link} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}>
                    <Download size={14} /> View Link
                  </a>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>Remarks</p>
              <p style={{ margin: 0, fontStyle: selectedInventory.remarks ? 'normal' : 'italic', color: selectedInventory.remarks ? '#1f2937' : '#9ca3af' }}>
                {selectedInventory.remarks || 'No remarks provided'}
              </p>
            </div>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={{ ...styles.th, width: '150px', textAlign: 'right' }}>Floor Balance</th>
                </tr>
              </thead>
              <tbody>
                {selectedInventory.items.map(item => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600' }}>{item.item_name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{item.item_code} - {item.unit_of_measurement}</div>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <span style={{ fontWeight: 'bold' }}>{item.floor_ending_balance}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => { setShowDetailsModal(false); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Close
              </button>
              {selectedInventory.status === 'pending' && (
                <button
                  onClick={() => { setShowDetailsModal(false); handleEdit(selectedInventory); }}
                  style={{ ...styles.button, backgroundColor: '#f59e0b' }}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};


const App = () => {
  const [token, setToken] = useState(localStorage.getItem('token'));

  return (
    <PermissionProvider token={token} API_URL={API_URL} fetchWithAuth={fetchWithAuth}>
      <AppContent token={token} setToken={setToken} />
    </PermissionProvider>
  );
};



const DepositDrops = ({ token }) => {
  const [drops, setDrops] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);         // Branch Users for selected branch
  const [shiftReports, setShiftReports] = useState([]); // Approved, unlinked shift reports
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingDrop, setViewingDrop] = useState(null);
  const [form, setForm] = useState({
    branch_id: '',
    remarks: '',
    shift_reports: []
  });



  const formatDateLocal = (dateString, shiftHours = 0) => {
    if (!dateString) return "-";
    try {
      let normalizedDate = dateString;
      // If it doesn't have an offset or Z/GMT/UTC, treat it as UTC to avoid local shifts
      if (!/[ZGMTUTC]/.test(dateString) && !/[+-]\d{2}:?\d{2}/.test(dateString)) {
        normalizedDate = dateString.includes('T') ? dateString + 'Z' : dateString.replace(' ', 'T') + 'Z';
      }

      // Create date and ensure it's valid
      const d = new Date(normalizedDate);
      if (isNaN(d.getTime())) return "Invalid Date";

      // Shift hours if requested (e.g., +7 for approval timestamps)
      if (shiftHours !== 0) {
        d.setMilliseconds(d.getMilliseconds() + (shiftHours * 60 * 60 * 1000));
      }

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[d.getUTCMonth()];
      const day = d.getUTCDate();
      const year = d.getUTCFullYear();
      const hour = String(d.getUTCHours()).padStart(2, '0');
      const minute = String(d.getUTCMinutes()).padStart(2, '0');

      return `${month} ${day}, ${year} ${hour}:${minute}`;
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    let badgeStyle = styles.badge;
    switch (status) {
      case 'pending':
        badgeStyle = { ...styles.badge, ...styles.badgeYellow };
        break;
      case 'partially_approved':
        badgeStyle = { ...styles.badge, ...styles.badgeBlue };
        break;
      case 'approved':
        badgeStyle = { ...styles.badge, ...styles.badgeGreen };
        break;
      case 'rejected':
        badgeStyle = { ...styles.badge, ...styles.badgeRed };
        break;
      default:
        badgeStyle = styles.badge;
    }
    return (
      <span style={badgeStyle}>
        {status ? status.replace('_', ' ').toUpperCase() : '-'}
      </span>
    );
  };
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchDrops();
    fetchBranches();
  }, [currentPage, perPage]);

  useEffect(() => {
    if (form.branch_id) {
      fetchUsers(form.branch_id);
      // When editing, handleEdit already fetches shift reports including already-linked ones.
      // Only auto-fetch unlinked reports when creating (not editing).
      if (!editingId) {
        fetchApprovedShiftReports(form.branch_id);
      }
    } else {
      setUsers([]);
      if (!editingId) setShiftReports([]);
    }
  }, [form.branch_id]);

  const fetchDrops = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/deposit_drops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDrops(data.drops);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async (branchId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/users/branch/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data.filter(user => user.role !== 'Administrator'));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchApprovedShiftReports = async (branchId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/shift_reports/approved?branch_id=${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setShiftReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setShiftReports([]);
    }
  };

  const resetForm = () => {
    setForm({
      branch_id: '',
      remarks: '',
      shift_reports: []
    });
    setEditingId(null);
  };

  const addShiftReport = () => {
    setForm({
      ...form,
      shift_reports: [...form.shift_reports, {
        shift_report_id: '',
        amount: 0,
        deposit_date: getPHTDateTimeString(),
        deposited_by: ''
      }]
    });
  };

  const removeShiftReport = (idx) => {
    const newShiftReport = form.shift_reports.filter((_, i) => i !== idx);
    const total = newShiftReport.reduce((sum, shift_rpt) => sum + shift_rpt.amount, 0);
    setForm({ ...form, shift_reports: newShiftReport, total_amount: total });
  };

  const updateShiftReport = (idx, field, value) => {
    const newShiftReport = [...form.shift_reports];

    if (field === 'shift_report_id') {
      const selectedShiftRpt = shiftReports.find(sr => String(sr.id) === String(value));
      if (selectedShiftRpt) {
        newShiftReport[idx].shift_report_id = value;
        newShiftReport[idx].amount = selectedShiftRpt.total_cash;
      } else {
        newShiftReport[idx].shift_report_id = '';
        newShiftReport[idx].amount = 0;
      }
    } else {
      newShiftReport[idx][field] = value;
    }

    const total = newShiftReport.reduce((sum, sr) => sum + (parseFloat(sr.amount) || 0), 0);
    setForm({ ...form, shift_reports: newShiftReport, total_amount: total });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.branch_id) {
      alert('Please select a branch');
      return;
    }

    if (form.shift_reports.length === 0) {
      alert('Please add at least one shift report');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/deposit_drops/${editingId}` : `${API_URL}/deposit_drops`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save deposit drop');
      }

      if (res.ok) {
        alert(editingId ? 'Updated successfully' : 'Deposit drop created successfully');
        fetchDrops(currentPage, perPage);
        setShowForm(false);
        resetForm();
        setEditingId(null);
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error submitting form');
    }
  };

  const handleEdit = async (drop) => {
    setEditingId(drop.id);

    try {
      const res_drop = await fetchWithAuth(`${API_URL}/deposit_drops/${drop.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res_drop.json();

      // Fetch approved shift reports for this branch, including already linked ones for this drop
      const branchId = data.drop?.branch_id || drop.branch_id;
      const res_sr = await fetchWithAuth(`${API_URL}/shift_reports/approved?branch_id=${branchId}&include_drop_id=${drop.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const srData = await res_sr.json();
      setShiftReports(Array.isArray(srData) ? srData : []);

      // Map shift reports — format deposit_date for datetime-local input
      const mappedShiftReports = (data.shift_reports || []).map(shift_rpt => ({
        shift_report_id: shift_rpt.shift_report_id,
        amount: shift_rpt.amount,
        deposit_date: getPHTDateTimeString(shift_rpt.deposit_date),
        deposited_by: shift_rpt.deposited_by
      }));

      fetchUsers(branchId);

      const total = mappedShiftReports.reduce((sum, sr) => sum + (parseFloat(sr.amount) || 0), 0);

      setForm({
        branch_id: branchId,
        status: data.drop?.status,
        remarks: data.drop?.remarks || '',
        first_approval_by: data.drop?.first_approval_by,
        first_approval_at: formatDateLocal(data.drop?.first_approval_at, 7),
        final_approval_by: data.drop?.final_approval_by,
        final_approval_at: formatDateLocal(data.drop?.final_approval_at, 7),
        created_by: data.drop?.created_by,
        shift_reports: mappedShiftReports,
        total_amount: total
      });

      setShowForm(true);
      setViewingDrop(null);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading drop details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deposit drop?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/deposit_drops/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchDrops(currentPage, perPage);
        alert('Deposit drop deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this deposit drop?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/deposit_drops/${id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Approved successfully. Status: ${(data.status || '').replace('_', ' ')}`);
        fetchDrops(currentPage, perPage);
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleView = async (drop) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/deposit_drops/${drop.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setViewingDrop(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const filteredShiftReports = form.branch_id
    ? shiftReports.filter(shift_rpt => shift_rpt.branch_id === parseInt(form.branch_id))
    : shiftReports;

  // Helper to open create form
  // const openCreate = () => {
  //   setEditingId(null);
  //   setShiftReports([]);
  //   setUsers([]);
  //   setForm({
  //     branch_id: '',
  //     deposit_date: getPHTDateTimeString(),
  //     shift_report_id: '',
  //     deposited_by: '',
  //     remarks: ''
  //   });
  //   setShowForm(true);
  // };

  // const canApproveDrop = (drop) => {
  //   if (!canApprove('deposit_drops')) return false;

  //   const role = user.role || '';
  //   if (role === 'Administrator') return true; // Admin can approve anything pending appropriate level

  //   if (drop.status === 'pending' && role === 'Branch Supervisor') return true;
  //   if (drop.status === 'supervisor_approved' && role === 'Branch Manager') return true;

  //   return false;
  // };

  const isActionable = (drop, currentUser) => {
    const terminalStatuses = ['approved', 'rejected'];

    if (!canApprove('deposit_drops')) return false;
    if (terminalStatuses.includes(drop.status)) return false;

    // A user cannot approve a drop they created
    if (currentUser.id === drop.created_by) return false;

    // A user cannot approve a drop they partially approved
    if (currentUser.id === drop.first_approval_by) return false;

    return true;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Deposit Drops</h1>
        {canCreate('deposit_drops') && (
          <button onClick={() => { setShowForm(!showForm); resetForm(); }} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> Create Deposit Drop
          </button>
        )}
      </div>

      {viewingDrop && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...styles.card, maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Deposit Drop #{viewingDrop.drop.id}</h2>
              <button onClick={() => setViewingDrop(null)} style={styles.iconButton}>
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Branch</p>
                <p style={{ fontSize: '16px', fontWeight: '600' }}>{viewingDrop.drop.branch_name}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Status</p>
                {getStatusBadge(viewingDrop.drop.status)}
              </div>
              {viewingDrop.drop.created_by_name && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Prepared by</p>
                  <p style={{ fontSize: '16px' }}>{viewingDrop.drop.created_by_name}</p>
                </div>
              )}
              {viewingDrop.drop.first_approval_by_name && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>First Approval</p>
                  <p style={{ fontSize: '16px' }}>{viewingDrop.drop.first_approval_by_name} on {formatDateLocal(viewingDrop.drop.first_approval_at, 7)}</p>
                </div>
              )}
              {viewingDrop.drop.final_approval_by_name && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Final Approval</p>
                  <p style={{ fontSize: '16px' }}>{viewingDrop.drop.final_approval_by_name} on {formatDateLocal(viewingDrop.drop.final_approval_at, 7)}</p>
                </div>
              )}

              {viewingDrop.drop.remarks && (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Remarks</p>
                  <p style={{ fontSize: '16px' }}>{viewingDrop.drop.remarks}</p>
                </div>
              )}
            </div>

            <div style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Shift Reports</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ ...styles.th, textAlign: 'left' }}>Shift Report</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Deposit Date</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Cashier</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Deposited By</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewingDrop.shift_reports || []).map((shift_rpt, idx) => (
                    <tr key={idx}>
                      <td style={{ ...styles.td, textAlign: 'left' }}>SR-{shift_rpt.shift_report_id}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{formatDateLocal(shift_rpt.deposit_date, 0)}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{shift_rpt.cashier_name || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>{shift_rpt.deposited_by_name}</td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>₱{formatCurrency(shift_rpt.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            {viewingDrop.shift_reports?.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginTop: '12px',
                marginBottom: '12px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Shift Reports:</span>
                    <span style={{ fontWeight: '600' }}>{viewingDrop.shift_reports?.length || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280', fontWeight: 'bold' }}>Total Amount:</span>
                    <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                      ₱{formatCurrency(
                        (viewingDrop.shift_reports || []).reduce((sum, shift_rpt) =>
                          sum + (parseFloat(shift_rpt.amount) || 0), 0
                        ).toFixed(2)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={() => handleEdit(viewingDrop.drop)} style={styles.button}>
                <Edit style={{ width: '16px', height: '16px' }} /> Edit
              </button>
              <button onClick={() => setViewingDrop(null)} style={{ ...styles.button, ...styles.buttonSecondary }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ ...styles.card, maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>
                {editingId ? `Edit Deposit Drop #${editingId}` : 'Create Deposit Drop'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={styles.iconButton}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => {
                    const branchId = e.target.value;
                    setForm({
                      ...form,
                      branch_id: branchId,
                      shift_reports: []
                    });
                  }}
                  style={styles.input}
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Remarks</label>
                <textarea
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  placeholder="Optional notes"
                  style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Shift Reports</h3>
                <button
                  onClick={addShiftReport}
                  style={{
                    ...styles.button,
                    ...styles.buttonSuccess,
                    padding: '6px 12px',
                    fontSize: '13px',
                    opacity: form.branch_id ? 1 : 0.5,
                    cursor: form.branch_id ? 'pointer' : 'not-allowed'
                  }}
                  disabled={!form.branch_id}
                  title={form.branch_id ? 'Add Shift Report' : 'Select branch first'}
                >
                  <Plus style={{ width: '16px', height: '16px' }} /> Add Shift Report
                </button>
              </div>

              {!form.branch_id && form.shift_reports.length === 0 && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #f59e0b'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                    ⚠️ Please select a branch first to see shift reports
                  </p>
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: '25%' }}>Shift Report *</th>
                      <th style={{ ...styles.th, width: '15%', textAlign: 'center' }}>Amount</th>
                      <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Deposit Date</th>
                      <th style={{ ...styles.th, width: '20%', textAlign: 'center' }}>Deposited By</th>
                      <th style={{ ...styles.th, width: '10%', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.shift_reports.map((shift_rpt, idx) => {
                      return (
                        <tr key={idx}>
                          <td style={styles.td}>
                            <select
                              value={shift_rpt.shift_report_id || ''}
                              onChange={(e) => updateShiftReport(idx, 'shift_report_id', e.target.value)}
                              style={{ ...styles.input, padding: '6px 8px', fontSize: '13px' }}
                              disabled={!form.branch_id}
                            >
                              <option value="">{form.branch_id ? 'Select Shift Report...' : 'Select branch first...'}</option>
                              {console.log(filteredShiftReports)}
                              {filteredShiftReports.map(sr => {
                                return (
                                  <option key={sr.id} value={sr.id}>
                                    SR-{sr.id} | {formatDateLocal(sr.shift_date, 0)} | ₱{formatCurrency(sr.total_cash)}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <span style={{ fontSize: '13px' }}>₱{formatCurrency(shift_rpt.amount) || ''}</span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <input
                              type="datetime-local"
                              value={shift_rpt.deposit_date || ''}
                              onChange={(e) => updateShiftReport(idx, 'deposit_date', e.target.value)}
                              style={styles.input}
                              required
                            />
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <select
                              value={shift_rpt.deposited_by || ''}
                              onChange={(e) => updateShiftReport(idx, 'deposited_by', e.target.value)}
                              style={styles.input}
                              required
                              disabled={!form.branch_id}
                            >
                              <option value="">
                                {!form.branch_id
                                  ? 'Select Branch First'
                                  : users.length === 0
                                    ? 'No users found for this branch'
                                    : 'Select User'}
                              </option>
                              {users.map(u => (
                                <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            <button onClick={() => removeShiftReport(idx)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                              <Trash2 style={{ width: '16px', height: '16px' }} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {form.shift_reports.length === 0 && form.branch_id && (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderRadius: '6px'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  No shift report added yet. Click "Add Shift Report" to start.
                </p>
              </div>
            )}

            {/* Summary */}
            {form.shift_reports.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Total Shift Reports:</span>
                    <span style={{ fontWeight: '600' }}>{form.shift_reports.length}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280', fontWeight: 'bold' }}>Total Amount:</span>
                    <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>
                      ₱{formatCurrency(
                        form.shift_reports.reduce((sum, shift_rpt) =>
                          sum + (parseFloat(shift_rpt.amount) || 0), 0
                        ).toFixed(2)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleSubmit} style={styles.button}>
                {editingId ? 'Update Order' : 'Create Order'}
              </button>
              <button onClick={() => { setShowForm(false); resetForm(); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Drops Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Branch</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Total Amount</th>
            <th style={styles.th}>First Approval</th>
            <th style={styles.th}>Final Approval</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {drops.map(drop => (
            <tr key={drop.id}>
              <td style={{ ...styles.td, fontWeight: '500' }}>DD-{drop.id}</td>
              <td style={styles.td}>{drop.branch_name}</td>
              <td style={styles.td}>
                {getStatusBadge(drop.status)}
              </td>
              <td style={{ ...styles.td, fontWeight: '600' }}>₱{formatCurrency(drop.total_amount)}</td>
              <td style={styles.td}>{(drop.first_approval_name ? `${drop.first_approval_name} on ${formatDateLocal(drop.first_approval_at, 7)}` : '-')}</td>
              <td style={styles.td}>{(drop.final_approval_name ? `${drop.final_approval_name} on ${formatDateLocal(drop.final_approval_at, 7)}` : '-')}</td>
              <td style={styles.td}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button onClick={() => handleView(drop)} style={{ ...styles.iconButton, color: '#3b82f6' }} title="View">
                    <Eye style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button onClick={() => handleEdit(drop)} style={{ ...styles.iconButton, color: '#10b981' }} title="Edit">
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  {isActionable(drop, user) && (
                    <button onClick={() => handleApprove(drop.id)} style={{ ...styles.iconButton, color: '#10b981' }} title="Approve">
                      <Check style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                  <button onClick={() => handleDelete(drop.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Delete">
                    <Trash2 style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>

        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* <Modal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          title={editingId ? 'Edit Deposit Drop' : 'New Deposit Drop'}
        >
          <form onSubmit={handleSubmit}>
            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Deposit Date & Time *</label>
                <input
                  type="datetime-local"
                  value={form.deposit_date}
                  onChange={(e) => setForm({ ...form, deposit_date: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Shift Report *</label>
                <select
                  value={form.shift_report_id}
                  onChange={(e) => setForm({ ...form, shift_report_id: e.target.value })}
                  style={styles.input}
                  required
                  disabled={!form.branch_id}
                >
                  <option value="">
                    {!form.branch_id
                      ? 'Select Branch First'
                      : shiftReports.length === 0
                        ? 'No approved shift reports available'
                        : 'Select Shift Report'}
                  </option>
                  {shiftReports.map(sr => (
                    <option key={sr.id} value={sr.id}>
                      SR-{sr.id} | {new Date(sr.shift_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} | {formatCurrency(sr.total_cash)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount to Deposit</label>
                <div style={{
                  ...styles.input,
                  backgroundColor: '#f3f4f6',
                  fontWeight: 'bold',
                  color: derivedAmount !== null ? '#059669' : '#9ca3af',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {derivedAmount !== null ? formatCurrency(derivedAmount) : '— select a shift report —'}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Deposited By *</label>
                <select
                  value={form.deposited_by}
                  onChange={(e) => setForm({ ...form, deposited_by: e.target.value })}
                  style={styles.input}
                  required
                  disabled={!form.branch_id}
                >
                  <option value="">
                    {!form.branch_id
                      ? 'Select Branch First'
                      : users.length === 0
                        ? 'No users found for this branch'
                        : 'Select User'}
                  </option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Remarks</label>
              <textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                style={{ ...styles.input, minHeight: '80px' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancel
              </button>
              <button type="submit" style={styles.button}>
                {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )} */}

      {/* <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Shift Report</th>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Branch</th>
            <th style={styles.th}>Amount</th>
            <th style={styles.th}>Deposited By</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Approved By</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {drops.map(drop => (
            <tr key={drop.id}>
              <td style={{ ...styles.td, fontFamily: 'monospace' }}>DD-{drop.id}</td>
              <td style={{ ...styles.td, fontFamily: 'monospace' }}>
                {drop.shift_report_id
                  ? `SR-${drop.shift_report_id}`
                  : <span style={{ color: '#9ca3af' }}>—</span>}
              </td>
              <td style={styles.td}>{formatDateTime(drop.deposit_date)}</td>
              <td style={styles.td}>{drop.branch_name}</td>
              <td style={{ ...styles.td, fontWeight: 'bold' }}>{formatCurrency(drop.amount)}</td>
              <td style={styles.td}>{drop.deposited_by_name}</td>
              <td style={styles.td}>
                <span style={{
                  ...styles.badge,
                  ...(drop.status === 'pending' ? styles.badgeYellow :
                    drop.status === 'partially_approved' ? styles.badgeBlue :
                      drop.status === 'approved' ? styles.badgeGreen : styles.badgeRed)
                }}>
                  {drop.status.replace('_', ' ').toUpperCase()}
                </span>
              </td>
              <td style={{ ...styles.td, fontSize: '12px' }}>
                {drop.supervisor_name && <div>First approver: {drop.supervisor_name}</div>}
                {drop.manager_name && <div>Final approver: {drop.manager_name}</div>}
              </td>
              <td style={styles.td}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {drop.status === 'pending' && canUpdate('deposit_drops') && (
                    <button onClick={() => handleEdit(drop)} style={{ ...styles.iconButton, color: '#4f46e5' }} title="Edit">
                      <Edit style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                  {isActionable(drop, user) && (
                    <button onClick={() => handleApprove(drop.id)} style={{ ...styles.iconButton, color: '#10b981' }} title="Approve">
                      <Check style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                  {drop.status === 'pending' && canDelete('deposit_drops') && (
                    <button onClick={() => handleDelete(drop.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Delete">
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table> */}

      {/* {drops.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          <p>No deposit drops yet</p>
        </div>
      )} */}
    </div>
  );
};


const BranchExpenses = ({ token, postedPeriods }) => {
  const getLocalDateTime = () => {
    return getPHTDateTimeString();
  };

  const formatDateLocal = (dateString) => {
    if (!dateString) return "";
    try {
      // Handle "YYYY-MM-DD HH:mm:ss" or ISO strings by extracting parts directly
      const parts = dateString.split(/T| /);
      const datePart = parts[0];
      const timePart = parts[1] ? parts[1].substring(0, 5) : "";

      if (!datePart) return dateString;

      const [year, month, day] = datePart.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = months[parseInt(month, 10) - 1];

      return `${monthLabel} ${day}, ${year}, ${timePart}`;
    } catch (e) {
      return dateString;
    }
  };

  const [expenses, setExpenses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [form, setForm] = useState({
    branch_id: '',
    transaction_date: getLocalDateTime(),
    description: '',
    expense_category: '',
    amount: 0,
    payee_contact_id: '',
    or_si_dr_no: '',
    cash_voucher_no: '',
    account_affected_id: '',
    status: 'pending',
    notes: ''
  });
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
    fetchExpenses();
    fetchBranches();
    fetchAccounts();
    fetchContacts();
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branch_expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setExpenses(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);

      if (authorizedBranches.length === 1) {
        setForm(prev => ({ ...prev, branch_id: authorizedBranches[0].id }));
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/contactsdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setContacts(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/chart_of_accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAccounts(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async () => {
    if (isPeriodRestricted(form.branch_id, form.transaction_date, postedPeriods)) {
      alert('This period has been posted for the selected branch. No transactions can be created or updated.');
      return;
    }

    if (!form.branch_id || !form.transaction_date) {
      alert('Please select branch and date');
      return;
    }

    try {
      const url = editingId
        ? `${API_URL}/branch_expenses/${editingId}`
        : `${API_URL}/branch_expenses`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        fetchExpenses();
        resetForm();
        alert(editingId ? 'Branch expense request updated successfully' : 'Branch expense request created and pending approval');
      } else {
        const data = await res.json();
        alert(data.error || 'Error saving branch expense');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error saving branch expense');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branch_expenses/${requestId}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        fetchExpenses();
        setShowApprovalModal(null);
        alert('Branch expense request approved successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error approving branch expense');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error approving branch expense');
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/branch_expenses/${requestId}/reject`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rejection_reason: rejectionReason })
      });

      if (res.ok) {
        fetchExpenses();
        setShowApprovalModal(null);
        setRejectionReason('');
        alert('Branch expense rejected');
      } else {
        const data = await res.json();
        alert(data.error || 'Error rejecting branch expense');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error rejecting branch expense');
    }
  };

  const handleEdit = async (expense) => {
    if (expense.status !== 'pending') {
      alert('Only pending requests can be edited');
      return;
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/branch_expenses/${expense.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      // Convert the saved datetime to local datetime-local format
      const localDateTime = getPHTDateTimeString(data.request.request_date);

      setForm({
        branch_id: data.request.branch_id,
        transaction_date: data.request.transaction_date,
        description: data.request.description,
        expense_category: data.request.expense_category,
        amount: data.request.amount,
        payee_contact_id: data.request.payee_contact_id,
        or_si_dr_no: data.request.or_si_dr_no,
        cash_voucher_no: data.request.cash_voucher_no,
        account_affected_id: data.request.account_affected_id,
        status: data.request.status,
        notes: data.request.notes
      });
      setEditingId(expense.id);
      setShowForm(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading branch expense');
    }
  };

  const handleDelete = async (id) => {
    const expense = expenses.find(e => e.id === id);
    if (expense && expense.status !== 'pending') {
      alert('Only pending expenses can be deleted');
      return;
    }

    if (!window.confirm('Delete this branch expense?')) return;

    try {
      const res = await fetchWithAuth(`${API_URL}/branch_expenses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        fetchExpenses();
        alert('Branch expense deleted successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Error deleting branch expense');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error deleting branch expense');
    }
  };

  const viewExpense = async (expenseId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branch_expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      setViewingExpense(data);
    } catch (err) {
      console.error('Error:', err);
      alert('Error loading expense details');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({
      branch_id: '',
      transaction_date: getLocalDateTime(),
      description: '',
      expense_category: '',
      amount: 0,
      payee_contact_id: '',
      or_si_dr_no: '',
      cash_voucher_no: '',
      account_affected_id: '',
      status: 'pending',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#fef3c7', color: '#92400e', border: '#f59e0b', text: 'Pending' },
      approved: { bg: '#d1fae5', color: '#065f46', border: '#10b981', text: 'Approved' },
      rejected: { bg: '#fee2e2', color: '#991b1b', border: '#ef4444', text: 'Rejected' }
    };

    const badge = badges[status] || badges.pending;
    return (
      <span style={{
        ...styles.badge,
        backgroundColor: badge.bg,
        color: badge.color,
        border: `1px solid ${badge.border}`
      }}>
        {badge.text}
      </span>
    );
  };

  const styles = {
    container: { padding: '24px', backgroundColor: '#f3f4f6', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 },
    button: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
    buttonSecondary: { backgroundColor: '#6b7280' },
    buttonSuccess: { backgroundColor: '#10b981' },
    buttonDanger: { backgroundColor: '#ef4444' },
    buttonWarning: { backgroundColor: '#f59e0b' },
    card: { backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    table: { width: '100%', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    td: { padding: '12px 16px', fontSize: '14px', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
    badgeGreen: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #10b981' },
    badgeRed: { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #ef4444' },
    badgeBlue: { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Branch Expenses</h1>
        {canCreate('branch_expenses') && (
          <button onClick={() => setShowForm(!showForm)} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} />
            Add Branch expense
          </button>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ ...styles.card, maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
                {editingId ? 'Edit Branch Expnese' : 'New Branch Expnese'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                style={styles.iconButton}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={styles.grid2}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value, items: [] })}
                  style={styles.input}
                  disabled={editingId}
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Transaction Date & Time *</label>
                <input
                  type="datetime-local"
                  value={form.transaction_date}
                  onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Expense description"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Expense category *</label>
                <select
                  value={form.expense_category}
                  onChange={(e) => setForm({ ...form, expense_category: e.target.value, items: [] })}
                  style={styles.input}
                >
                  <option value="">Select Expense Category</option>
                  {expenses.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount || ''}
                  placeholder="0.00"
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  style={{ ...styles.input, textAlign: 'right' }}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Payee</label>
                <select
                  value={form.payee_contact_id}
                  onChange={(e) => setForm({ ...form, payee_contact_id: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select Payee</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>OR# / SI# / DR#</label>
                <input
                  type="text"
                  value={form.or_si_dr_no}
                  onChange={(e) => setForm({ ...form, or_si_dr_no: e.target.value })}
                  placeholder="Official Receipt / Sales Invoice / Delivery Receipt"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Cash Voucher</label>
                <input
                  type="text"
                  value={form.cash_voucher_no}
                  onChange={(e) => setForm({ ...form, cash_voucher_no: e.target.value })}
                  placeholder="Cash voucher serial number"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Account affected</label>
                <select
                  value={form.account_affected_id}
                  onChange={(e) => setForm({ ...form, account_affected_id: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select Account used to disburse expense</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Reason for this expense"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
              <button onClick={handleSubmit} style={styles.button}>
                {editingId ? 'Update' : 'Create'} Branch Expense
              </button>
              <button onClick={resetForm} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Transaction Date & Time</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Prepared by</th>
                <th style={styles.th}>Approver</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(expense => (
                <tr key={expense.id}>
                  <td style={{ ...styles.td, fontWeight: '600', fontFamily: 'monospace' }}>
                    PR-{expense.id}
                  </td>
                  <td style={styles.td}>{expense.branch_name}</td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '500' }}>{formatDateLocal(expense.transaction_date)}</div>
                    {expense.entry_delay > 0 && (
                      <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: '600', marginTop: '2px' }}>
                        Entry Delay: {expense.entry_delay} {expense.entry_delay === 1 ? 'day' : 'days'}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    {getStatusBadge(expense.amount)}
                  </td>
                  <td style={styles.td}>
                    {getStatusBadge(expense.status || 'pending')}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{expense.submitted_by_name}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{formatPHDatetime(expense.created_at)}</div>
                  </td>
                  <td style={styles.td}>
                    {expense.approved_by_name ? (
                      <>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>{expense.approved_by_name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {formatPHDatetime(expense.approved_at)}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => viewExpense(expense.id)}
                      style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}
                      title="View Details"
                    >
                      <Eye style={{ width: '16px', height: '16px' }} />
                    </button>
                    {expense.status === 'pending' && (
                      <>
                        {canApprove('branch_expenses') && currentUser.id !== expense.submitted_by && (
                          <button
                            onClick={() => setShowApprovalModal(expense)}
                            style={{ ...styles.iconButton, color: '#10b981', marginRight: '8px' }}
                            title="Approve/Reject"
                          >
                            <Check style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canUpdate('branch_expenses') && (
                          <button
                            onClick={() => handleEdit(expense)}
                            style={{ ...styles.iconButton, color: '#f59e0b', marginRight: '8px' }}
                            title="Edit"
                          >
                            <Edit style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                        {canDelete('branch_expenses') && (
                          <button
                            onClick={() => handleDelete(expense.id)}
                            style={{ ...styles.iconButton, color: '#ef4444' }}
                            title="Delete"
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {expenses.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <FileText style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No branch expense found</p>
          </div>
        )}
      </div>

      {/* View Expense Modal */}
      {viewingExpense && (
        <div style={styles.modal} onClick={() => setViewingExpense(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Branch Expense BE-{viewingExpense.expense.id}
              </h2>
              <button onClick={() => setViewingExpense(null)} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', fontSize: '14px', marginBottom: '24px' }}>
              <strong>Branch:</strong>
              <span>{viewingExpense.expense.branch_name}</span>

              <strong>Transaction Date & Time:</strong>
              <span>{formatDateLocal(viewingExpense.expense.transaction_date)}</span>

              <strong>Description:</strong>
              <span>({viewingExpense.expense.expense_category || 'N/A'}) {viewingExpense.expense.description || 'N/A'} </span>

              <strong>Amount:</strong>
              <span>{viewingExpense.expense.amount || 'N/A'}</span>

              <strong>Status:</strong>
              <div>{getStatusBadge(viewingExpense.expense.status || 'pending')}</div>

              <strong>Notes:</strong>
              <span>{viewingExpense.expense.notes || 'N/A'}</span>

              <strong>Prepared By:</strong>
              <span>{viewingExpense.expense.submitted_by_name || 'N/A'}</span>

              {viewingExpense.expense.status !== 'pending' && viewingExpense.expense.submitted_by_name && (
                <>
                  <strong>{viewingExpense.expense.status === 'approved' ? 'Approved By:' : 'Rejected By:'}</strong>
                  <span>{viewingExpense.expense.approved_by_name || '—'}</span>

                  {viewingExpense.expense.approved_at && (
                    <>
                      <strong>{viewingExpense.expense.status === 'approved' ? 'Approved At:' : 'Rejected At:'}</strong>
                      <span>{formatDateTime(viewingExpense.expense.approved_at)}</span>
                    </>
                  )}
                </>
              )}

              {viewingExpense.expense.rejection_reason && (
                <>
                  <strong>Rejection Reason:</strong>
                  <span style={{ color: '#ef4444' }}>{viewingExpense.expense.rejection_reason}</span>
                </>
              )}
            </div>

            <button
              onClick={() => setViewingExpense(null)}
              style={{ ...styles.button, marginTop: '16px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div style={styles.modal} onClick={() => { setShowApprovalModal(null); setRejectionReason(''); }}>
          <div style={{ ...styles.modalContent, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                Review Branch Expense
              </h2>
              <button onClick={() => { setShowApprovalModal(null); setRejectionReason(''); }} style={styles.iconButton}>
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            <div style={{ fontSize: '14px', marginBottom: '24px' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Expense ID:</strong> BE-{showApprovalModal.id}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Branch:</strong> {showApprovalModal.branch_name}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Transaction Date:</strong> {formatDateTime(showApprovalModal.transaction_date)}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Amount:</strong> {showApprovalModal.amount}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...styles.label, display: 'block' }}>Rejection Reason (optional for rejection)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', width: '90%' }}
                placeholder="Provide reason if rejecting..."
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleApprove(showApprovalModal.id)}
                style={{ ...styles.button, ...styles.buttonSuccess, flex: 1 }}
              >
                <Check style={{ width: '18px', height: '18px' }} />
                Approve
              </button>
              <button
                onClick={() => handleReject(showApprovalModal.id)}
                style={{ ...styles.button, ...styles.buttonDanger, flex: 1 }}
              >
                <XCircle style={{ width: '18px', height: '18px' }} />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const ShiftReports = ({ token }) => {
  const [reports, setReports] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);

  // Form State
  const [form, setForm] = useState({
    branch_id: '',
    shift_date: '',
    cash_breakdown: {
      "1000": "", "500": "", "200": "", "100": "", "50": "", "20": "", "10": "", "5": "", "1": "", "centavo": ""
    },
    gcash_amount: '',
    gotyme_amount: '',
    discounts_amount: '',
    refunds_amount: '',
    notes: ''
  });

  const [approvalNotes, setApprovalNotes] = useState('');

  // const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const { canApprove, canCreate, canUpdate, canDelete } = usePermissions();

  const [cashiers, setCashiers] = useState([]);

  const formatShiftDateLocal = (dateString) => {
    if (!dateString) return "";
    try {
      let normalizedDate = dateString;
      // If it doesn't have an offset or Z/GMT/UTC, treat it as UTC to avoid local shifts
      if (!/[ZGMTUTC]/.test(dateString) && !/[+-]\d{2}:?\d{2}/.test(dateString)) {
        normalizedDate = dateString.includes('T') ? dateString + 'Z' : dateString.replace(' ', 'T') + 'Z';
      }

      // Use UTC methods to extract literal components from GMT/UTC strings
      const d = new Date(normalizedDate);
      if (isNaN(d.getTime())) return dateString;

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = months[d.getUTCMonth()];
      const dayNum = d.getUTCDate();
      const year = d.getUTCFullYear();
      const hour = String(d.getUTCHours()).padStart(2, '0');
      const minute = String(d.getUTCMinutes()).padStart(2, '0');

      return `${monthLabel} ${dayNum}, ${year} ${hour}:${minute}`;
    } catch (e) {
      return dateString;
    }
  };

  const getShiftDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      let normalizedDate = dateString;
      // If it doesn't have an offset or Z/GMT/UTC, treat it as UTC to avoid local shifts
      if (!/[ZGMTUTC]/.test(dateString) && !/[+-]\d{2}:?\d{2}/.test(dateString)) {
        normalizedDate = dateString.includes('T') ? dateString + 'Z' : dateString.replace(' ', 'T') + 'Z';
      }

      const d = new Date(normalizedDate);
      if (isNaN(d.getTime())) return "";

      const yyyy = d.getUTCFullYear();
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mins = String(d.getUTCMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${mins}`;
    } catch (e) {
      return "";
    }
  };

  useEffect(() => {
    fetchReports();
    fetchBranches();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/shift_reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setReports(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;

        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
      // setBranches(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCashiers = async (branchId) => {
    if (!branchId) {
      setCashiers([]);
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_URL}/users/cashiers/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCashiers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCashiers(form.branch_id);
  }, [form.branch_id]);

  // Calculations
  const calculateTotalCash = () => {
    let total = 0;
    const bd = form.cash_breakdown;
    for (const [denom, count] of Object.entries(bd)) {
      if (count) {
        if (denom === 'centavo') {
          total += parseFloat(count);
        } else {
          total += parseFloat(denom) * parseFloat(count);
        }
      }
    }
    return total;
  };

  const calculateGross = () => {
    const cash = calculateTotalCash();
    const gcash = parseFloat(form.gcash_amount || 0);
    const gotyme = parseFloat(form.gotyme_amount || 0);
    return cash + gcash + gotyme;
  };

  const calculateDeductions = () => {
    const disc = parseFloat(form.discounts_amount || 0);
    const refund = parseFloat(form.refunds_amount || 0);
    return disc + refund;
  };

  const calculateNet = () => {
    return calculateGross() - calculateDeductions();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingId ? `${API_URL}/shift_reports/${editingId}` : `${API_URL}/shift_reports`;
      const method = editingId ? 'PUT' : 'POST';

      const payload = {
        ...form,
        total_cash: calculateTotalCash()
      };

      const res = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(editingId ? 'Updated successfully' : 'Created successfully');
        setShowForm(false);
        setEditingId(null);
        resetForm();
        fetchReports();
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
      alert('Error submitting form');
    }
  };

  const handleEdit = (report) => {
    setEditingId(report.id);
    fetchCashiers(report.branch_id); // fetch cashiers for the branch when editing
    setForm({
      branch_id: report.branch_id,
      cashier_id: report.cashier_id || '',
      shift_date: getShiftDateForInput(report.shift_date),
      cash_breakdown: {
        "1000": "", "500": "", "200": "", "100": "", "50": "", "20": "", "10": "", "5": "", "1": "", "centavo": "",
        ...report.cash_breakdown // Override with saved values
      },
      gcash_amount: report.gcash_amount,
      gotyme_amount: report.gotyme_amount,
      discounts_amount: report.discounts_amount,
      refunds_amount: report.refunds_amount,
      notes: report.notes || ''
    });
    setShowForm(true);
  };

  const handleView = (report) => {
    setViewingReport(report);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/shift_reports/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchReports();
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleApproveClick = (report) => {
    setSelectedReport(report);
    setApprovalNotes('');
    setShowApprovalModal(true);
  };

  const submitApproval = async () => {
    if (!window.confirm('Approve this shift report?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/shift_reports/${selectedReport.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ notes: approvalNotes })
      });
      if (res.ok) {
        alert('Approved successfully');
        setShowApprovalModal(false);
        setSelectedReport(null);
        fetchReports();
      } else {
        const data = await res.json();
        alert(data.error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    setForm({
      branch_id: '',
      cashier_id: '',
      shift_date: getPHTDateTimeString(),
      cash_breakdown: {
        "1000": "", "500": "", "200": "", "100": "", "50": "", "20": "", "10": "", "5": "", "1": "", "centavo": ""
      },
      gcash_amount: '',
      gotyme_amount: '',
      discounts_amount: '',
      refunds_amount: '',
      notes: ''
    });
  };

  const openCreate = () => {
    setEditingId(null);
    resetForm();
    setShowForm(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span style={{ ...styles.badge, ...styles.badgeBlue }}>APPROVED</span>;
      case 'deposited':
        return <span style={{ ...styles.badge, ...styles.badgeGreen }}>DEPOSITED</span>;
      case 'rejected':
        return <span style={{ ...styles.badge, ...styles.badgeRed }}>REJECTED</span>;
      case 'pending':
        return <span style={{ ...styles.badge, ...styles.badgeYellow }}>PENDING</span>;
      default:
        return <span style={styles.badge}>{status}</span>;
    }
  };

  const denominations = ["1000", "500", "200", "100", "50", "20", "10", "5", "1", "centavo"];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Shift Reports</h1>
        {canCreate('shift_reports') && (
          <button onClick={openCreate} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> New Report
          </button>
        )}
      </div>

      {showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          title={editingId ? `Edit Shift Report #SR-${editingId}` : 'New Shift Report'}
          maxWidth="800px"
        >
          <form onSubmit={handleSubmit}>
            {/* Header Section */}
            <div style={{ ...styles.grid2, marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '20px' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Branch *</label>
                <select
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cashier *</label>
                <select
                  value={form.cashier_id}
                  onChange={(e) => setForm({ ...form, cashier_id: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select Cashier</option>
                  {cashiers.map(c => (
                    <option key={c.id} value={c.id}>{c.fullname}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Shift Date & Time *</label>
                <input
                  type="datetime-local"
                  value={form.shift_date}
                  onChange={(e) => setForm({ ...form, shift_date: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
              {/* Gross Sales Section */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#111827' }}>Gross Sales</h3>

                <div style={{ marginBottom: '16px', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                  <label style={{ ...styles.label, marginBottom: '8px', display: 'block' }}>Cash Breakdown</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {denominations.map(denom => (
                      <div key={denom} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', width: '40px', textAlign: 'right', fontWeight: '600', color: '#4b5563' }}>
                          {denom === 'centavo' ? '¢' : denom}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step={denom === 'centavo' ? "0.01" : "1"}
                          placeholder="0"
                          value={form.cash_breakdown[denom]}
                          onChange={(e) => setForm({
                            ...form,
                            cash_breakdown: { ...form.cash_breakdown, [denom]: e.target.value }
                          })}
                          style={{ ...styles.input, padding: '4px 8px', fontSize: '13px' }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '12px', textAlign: 'right', fontWeight: 'bold', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                    Total Cash: {formatCurrency(calculateTotalCash())}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>GCash Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.gcash_amount || ''}
                    placeholder="0.00"
                    onChange={(e) => setForm({ ...form, gcash_amount: e.target.value })}
                    style={{ ...styles.input, textAlign: 'right' }}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>GoTyme Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.gotyme_amount || ''}
                    placeholder="0.00"
                    onChange={(e) => setForm({ ...form, gotyme_amount: e.target.value })}
                    style={{ ...styles.input, textAlign: 'right' }}
                  />
                </div>

                <div style={{ backgroundColor: '#eff6ff', padding: '12px', borderRadius: '8px', textAlign: 'right' }}>
                  <span style={{ fontSize: '14px', color: '#1e40af' }}>Total Gross Sales</span>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e3a8a' }}>{formatCurrency(calculateGross())}</div>
                </div>
              </div>

              {/* Deductions Section */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#111827' }}>Deductions</h3>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Discounts</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discounts_amount || ''}
                    placeholder="0.00"
                    onChange={(e) => setForm({ ...form, discounts_amount: e.target.value })}
                    style={{ ...styles.input, textAlign: 'right' }}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Refunds</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.refunds_amount}
                    placeholder="0.00"
                    onChange={(e) => setForm({ ...form, refunds_amount: e.target.value })}
                    style={{ ...styles.input, textAlign: 'right' }}
                  />
                </div>

                <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', textAlign: 'right', marginBottom: '24px' }}>
                  <span style={{ fontSize: '14px', color: '#991b1b' }}>Total Deductions</span>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#7f1d1d' }}>{formatCurrency(calculateDeductions())}</div>
                </div>

                <div style={{ backgroundColor: '#ecfdf5', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                  <span style={{ fontSize: '16px', color: '#065f46', fontWeight: '600' }}>NET SALES</span>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#064e3b', marginTop: '4px' }}>{formatCurrency(calculateNet())}</div>
                </div>

                <div style={{ ...styles.formGroup, marginTop: '24px' }}>
                  <label style={styles.label}>Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    style={{ ...styles.input, minHeight: '100px' }}
                    placeholder="General remarks or notes..."
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancel
              </button>
              <button type="submit" style={styles.button}>
                {editingId ? 'Update Report' : 'Create Report'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Approval Modal */}
      <Modal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        title="Approve Shift Report"
        maxWidth="500px"
      >
        <div>
          <p style={{ marginBottom: '16px' }}>
            Are you sure you want to approve the shift report for
            <b> {selectedReport?.branch_name}</b> on
            <b> {formatShiftDateLocal(selectedReport?.shift_date)}</b>?
          </p>
          <div style={styles.formGroup}>
            <label style={styles.label}>Approval Notes (Optional)</label>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              style={{ ...styles.input, minHeight: '80px' }}
              placeholder="Any comments regarding this approval..."
            />
          </div>
          <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
            <button onClick={submitApproval} style={{ ...styles.button, ...styles.buttonSuccess, flex: 1 }}>
              Confirm Approve
            </button>
            <button onClick={() => setShowApprovalModal(false)} style={{ ...styles.button, ...styles.buttonSecondary, flex: 1 }}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      {showViewModal && viewingReport && (
        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={`Shift Report #${viewingReport.id} (${viewingReport.branch_name})`}
          maxWidth="800px"
        >
          <div>
            {/* Header Info */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Shift Date</div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{formatShiftDateLocal(viewingReport.shift_date)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Status</div>
                <div>{getStatusBadge(viewingReport.status)}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
              {/* Left Column: Breakdowns */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#111827' }}>Cash Breakdown</h3>
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                    {denominations.map(denom => {
                      const count = viewingReport.cash_breakdown?.[denom];
                      if (!count) return null;
                      return (
                        <div key={denom} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#4b5563' }}>{denom === 'centavo' ? '¢' : denom} x {count}</span>
                          <span style={{ fontWeight: '500' }}>
                            {denom === 'centavo' ? formatCurrency(count) : formatCurrency(parseFloat(denom) * parseFloat(count))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <span>Total Cash</span>
                    <span>{formatCurrency(viewingReport.total_cash)}</span>
                  </div>
                </div>

                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#111827' }}>Other Payments</h3>
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#4b5563' }}>GCash</span>
                  <span style={{ fontWeight: '500' }}>{formatCurrency(viewingReport.gcash_amount)}</span>
                </div>
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#4b5563' }}>GoTyme</span>
                  <span style={{ fontWeight: '500' }}>{formatCurrency(viewingReport.gotyme_amount)}</span>
                </div>
              </div>

              {/* Right Column: Financials */}
              <div>
                <div style={{ backgroundColor: '#eff6ff', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#1e40af' }}>Total Gross Sales</span>
                    <span style={{ fontWeight: 'bold', color: '#1e3a8a' }}>{formatCurrency(viewingReport.gross_sales)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#b91c1c' }}>
                    <span>Less: Discounts</span>
                    <span>({formatCurrency(viewingReport.discounts_amount)})</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#b91c1c' }}>
                    <span>Less: Refunds</span>
                    <span>({formatCurrency(viewingReport.refunds_amount)})</span>
                  </div>
                  <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: '12px', marginTop: '12px', textAlign: 'center' }}>
                    <span style={{ display: 'block', fontSize: '14px', color: '#1e3a8a', fontWeight: '600' }}>NET SALES</span>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e3a8a' }}>{formatCurrency(viewingReport.net_sales)}</span>
                  </div>
                </div>

                {viewingReport.notes && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px', color: '#374151' }}>Notes</h4>
                    <div style={{ backgroundColor: '#f3f4f6', padding: '10px', borderRadius: '6px', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                      {viewingReport.notes}
                    </div>
                  </div>
                )}

                {viewingReport.approval_notes && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px', color: '#059669' }}>Approval Notes</h4>
                    <div style={{ backgroundColor: '#ecfdf5', padding: '10px', borderRadius: '6px', fontSize: '14px', whiteSpace: 'pre-wrap', color: '#065f46' }}>
                      {viewingReport.approval_notes}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
              <div><b>Cashier:</b> {viewingReport.cashier_name || '-'}</div>
              <div><b>Created by:</b> {viewingReport.created_by_name}</div>
              {viewingReport.approved_by_name && (
                <div><b>Approved by:</b> {viewingReport.approved_by_name}</div>
              )}
            </div>

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowViewModal(false)} style={styles.button}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Shift Date</th>
            <th style={styles.th}>Branch</th>
            <th style={styles.th}>Cashier</th>
            <th style={styles.th}>Net Sales</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Created By</th>
            <th style={styles.th}>Approved By</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.map(report => (
            <tr key={report.id}>
              <td style={{ ...styles.td, fontFamily: 'monospace' }}>SR-{report.id}</td>
              {/* <td style={styles.td}>{formatDateTime(report.shift_date)}</td> */}
              <td style={styles.td}>{formatShiftDateLocal(report.shift_date)}</td>
              <td style={styles.td}>{report.branch_name}</td>
              <td style={styles.td}>{report.cashier_name || '-'}</td>
              <td style={{ ...styles.td, fontWeight: 'bold' }}>{formatCurrency(report.net_sales)}</td>
              <td style={styles.td}>{getStatusBadge(report.status)}</td>
              <td style={styles.td}>{report.created_by_name}</td>
              <td style={styles.td}>{report.approved_by_name || '-'}</td>
              <td style={styles.td}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleView(report)} style={{ ...styles.iconButton, color: '#3b82f6' }} title="View">
                    <Eye style={{ width: '16px', height: '16px' }} />
                  </button>
                  {report.status === 'pending' && canUpdate('shift_reports') && (
                    <button onClick={() => handleEdit(report)} style={{ ...styles.iconButton, color: '#4f46e5' }} title="Edit">
                      <Edit style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                  {report.status === 'pending' && canApprove('shift_reports') && (
                    <button onClick={() => handleApproveClick(report)} style={{ ...styles.iconButton, color: '#10b981' }} title="Approve">
                      <Check style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                  {report.status === 'pending' && canDelete('shift_reports') && (
                    <button onClick={() => handleDelete(report.id)} style={{ ...styles.iconButton, color: '#ef4444' }} title="Delete">
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  );
};


export default App;